
// ========== constants.js ==========
// constants.js — shared constants for the client portal bundle (no DOM, no logic).

// Halo ticket status id -> human label.
const STATUSID_MAP = {
  1: 'New', 5: 'With Supplier', 9: 'Closed', 22: 'Updated', 23: 'Ringing',
  24: 'Missed', 26: 'On Hold', 27: 'Scheduled', 28: 'On Route', 29: 'On Call',
  30: 'On Site', 31: 'Connected', 32: 'Re-Opened', 33: 'Waiting on Customer',
  34: 'Waiting on Vendor', 35: 'Call Completed', 36: 'Connection Ended',
  37: 'Post Call Request', 38: 'Appointment Completed', 39: 'Lead', 40: 'Quote',
  41: 'Negotiation', 42: 'Won', 43: 'Lost', 45: 'Quote Accepted',
  46: 'Quote Rejected', 47: 'Qualified', 48: 'Approved', 49: 'Voicemail',
  50: 'Alert', 51: 'Scheduled (Long Date)', 52: 'Call Back',
  53: 'Waiting on Parts', 54: 'Supplier Updated', 55: 'Follow Up Required',
  56: 'Pending Schedule'
};

// Halo status id -> portal badge variant (maps onto the docs status palette:
// success=green, danger=red, warning=amber, info=blue, neutral=grey).
const STATUS_BADGE = {
  1: 'warning', 5: 'info', 9: 'success', 22: 'neutral', 23: 'warning',
  24: 'danger', 26: 'neutral', 27: 'info', 28: 'info', 29: 'warning',
  30: 'info', 31: 'success', 32: 'warning', 33: 'neutral', 34: 'neutral',
  35: 'success', 36: 'neutral', 37: 'info', 38: 'success', 39: 'info',
  40: 'info', 41: 'warning', 42: 'success', 43: 'danger', 45: 'success',
  46: 'danger', 47: 'success', 48: 'success', 49: 'neutral', 50: 'danger',
  51: 'info', 52: 'warning', 53: 'neutral', 54: 'info', 55: 'warning',
  56: 'neutral'
};

// View ids that exist in the shell markup (in navigation order).
const PORTAL_VIEWS = [
  'view-welcome',
  'view-ticket-form',
  'view-newuser',
  'view-suspend',
  'view-existing',
  'view-ticket-details',
  'view-users',
  'view-billing',
  'view-remote',
  'view-chat'
];

// Navigation lives in the URL hash (the address bar is the breadcrumb) — NOT in
// storage. Hash path <-> view id. The ticket-details view is special-cased in
// the router because it carries an ?id= param (#tickets?id=12345).
const HASH_TO_VIEW = {
  '': 'view-welcome',
  'welcome': 'view-welcome',
  'dashboard': 'view-welcome',
  'new/ticket': 'view-ticket-form',
  'new/user': 'view-newuser',
  'new/suspend': 'view-suspend',
  'tickets': 'view-existing',
  'users': 'view-users',
  'billing': 'view-billing',
  'remote': 'view-remote',
  'chat': 'view-chat'
};

const VIEW_TO_HASH = {
  'view-welcome': '',
  'view-ticket-form': 'new/ticket',
  'view-newuser': 'new/user',
  'view-suspend': 'new/suspend',
  'view-existing': 'tickets',
  // Ticket details rides the tickets path with an ?id= param (#tickets?id=123).
  'view-ticket-details': 'tickets',
  'view-users': 'users',
  'view-billing': 'billing',
  'view-remote': 'remote',
  'view-chat': 'chat'
};

// Top-level nav section per hash path — drives the active nav highlight. The
// new-user / suspend request flows belong to the Users section even though they
// are also reachable from the dashboard's New Issue options.
const NAV_SECTION_BY_PATH = {
  'tickets': 'tickets',
  'users': 'users',
  'new/user': 'users',
  'new/suspend': 'users',
  'billing': 'billing',
  'remote': 'remote',
  'chat': 'chat'
};

// sessionStorage keys — ONLY the unsent ticket draft persists in storage now.
const STORE_KEYS = {
  TICKET_DRAFT: 'portal_ticket_draft'
};
;

// ========== service.js ==========
// service.js — portal API/data layer. No DOM access; pure data in/out.
// Replaces the legacy Socket.IO RPC with plain JSON over HTTP, carrying the
// temporary session id in the X-Session-Id header.

// The API lives at <page-path>/api. Derived from the current location so it
// stays correct behind the nginx events route regardless of any path prefix.
function portalApiBase() {
  return location.pathname.replace(/\/+$/, '') + '/api';
}

// Rewrite externalized action-image srcs to the portal's own ownership-gated route
// (idea #377). The PSA rewrites action body_html images to the canonical
// /ticketing/api/action-images/<id>; that route needs a staff session, so for the
// client portal we point <img> at <portal>/api/action-images/<id> instead, carrying
// the session id in the query string (an <img> can't send the X-Session-Id header).
// No-op for any markup that doesn't contain such a src.
function rewriteActionImageHtml(html) {
  if (!html || html.indexOf('/ticketing/api/action-images/') === -1) return html || '';
  const sid = encodeURIComponent((typeof PORTAL !== 'undefined' && PORTAL.sessionId) || '');
  const base = portalApiBase();
  return String(html).replace(/\/ticketing\/api\/action-images\/(\d+)/g, function (_m, id) {
    return base + '/action-images/' + id + '?session_id=' + sid;
  });
}

// Thrown for any non-OK API response so callers can branch on .code.
function PortalError(message, code, status) {
  const err = new Error(message);
  err.code = code || 'error';
  err.status = status || 0;
  return err;
}

async function apiFetch(method, path, body) {
  let response;
  try {
    response = await fetch(portalApiBase() + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Id': (typeof PORTAL !== 'undefined' && PORTAL.sessionId) || ''
      },
      body: body ? JSON.stringify(body) : undefined
    });
  } catch {
    throw PortalError('Network error — please check your connection.', 'network', 0);
  }

  let data = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (response.status === 401) {
    throw PortalError('Your session has expired. Please reopen the portal.', 'session_expired', 401);
  }
  if (!response.ok) {
    const message = (data && (data.message || data.error)) || 'Request failed.';
    throw PortalError(message, (data && data.error) || 'error', response.status);
  }
  return data;
}

// --- Endpoint wrappers ------------------------------------------------------

// Mint a session from the agent_id/username in the URL. Unguarded — this is the
// bootstrap call, so it runs before window.PORTAL.sessionId exists.
function mintSession(agentId, username, agentUid) {
  return apiFetch('POST', '/session', { agent_id: agentId || '', username: username || '', agent_uid: agentUid || '' });
}

// --- Auth (idea #1078) ------------------------------------------------------
// Login / 2FA / password endpoints report their own status codes (401 invalid,
// 429 locked/rate-limited, 400 bad code). apiFetch collapses 401 into a generic
// "session expired", so these use a raw call that returns { ok, status, data }
// and lets the caller branch on data.error.
async function authFetch(method, path, body) {
  let response;
  try {
    response = await fetch(portalApiBase() + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Id': (typeof PORTAL !== 'undefined' && PORTAL.sessionId) || ''
      },
      body: body ? JSON.stringify(body) : undefined
    });
  } catch {
    return { ok: false, status: 0, data: { error: 'network', message: 'Network error — please check your connection.' } };
  }
  let data = null;
  try { data = await response.json(); } catch { data = null; }
  return { ok: response.ok, status: response.status, data: data || {} };
}

function loginRequest(email, password) {
  return authFetch('POST', '/login', { email: email, password: password });
}
function logoutRequest() {
  return authFetch('POST', '/logout', {});
}
function forgotPasswordRequest(email) {
  return authFetch('POST', '/password/forgot', { email: email });
}
function setPasswordRequest(token, password) {
  return authFetch('POST', '/password/set', { token: token, password: password });
}
function validateResetToken(token) {
  return authFetch('POST', '/password/validate', { token: token });
}
// SSO buttons stay hidden until the server enables them (idea #3089). Opening the portal
// with ?sso_preview=1 reveals them for testing; remembered for the tab so it survives the
// provider round-trip and the URL scrub.
function cpSsoPreviewRequested() {
  try {
    if (new URLSearchParams(location.search).get('sso_preview') === '1') sessionStorage.setItem('cp_sso_preview', '1');
    return sessionStorage.getItem('cp_sso_preview') === '1';
  } catch { return false; }
}
function fetchAuthConfig() {
  return authFetch('GET', '/auth/config' + (cpSsoPreviewRequested() ? '?preview=1' : ''));
}
function request2fa() {
  return authFetch('POST', '/2fa/request', {});
}
function verify2fa(code, rememberDevice) {
  return authFetch('POST', '/2fa/verify', { code: code, remember_device: !!rememberDevice });
}

function fetchContext() {
  return apiFetch('GET', '/context');
}

function fetchUserTickets() {
  return apiFetch('GET', '/tickets');
}

function fetchCompanyUsers() {
  return apiFetch('GET', '/users');
}

// Lighter user list for the Suspend picker (gated on the suspend permission).
function fetchSuspendableUsers() {
  return apiFetch('GET', '/users/suspendable');
}

function fetchTicketDetails(ticketId) {
  return apiFetch('GET', '/tickets/' + encodeURIComponent(ticketId));
}

function submitTicket(payload) {
  return apiFetch('POST', '/tickets', payload);
}

function submitTicketReply(ticketId, message) {
  return apiFetch('POST', '/tickets/' + encodeURIComponent(ticketId) + '/reply', { message: message });
}

function requestScChat(ticketId) {
  return apiFetch('POST', '/chat/start', { ticket_id: ticketId || null });
}

// --- Native websocket chat (idea #1184) — retires ScreenConnect chat ---------
// resolve = find/create this user's live chat session + history; history = backfill
// on reconnect; sendChatHttp = fallback when the socket is down (live send is over
// the websocket, see chat.js).
function resolveChatSession() {
  return apiFetch('POST', '/chat/resolve', {});
}
function fetchChatHistory(sessionId) {
  return apiFetch('POST', '/chat/history', { session_id: sessionId });
}
function sendChatHttp(sessionId, body, extId) {
  return apiFetch('POST', '/chat/send', { session_id: sessionId, body: body, ext_id: extId || null });
}

function submitSuspendRequest(payload) {
  return apiFetch('POST', '/suspend', payload);
}

// --- Remote access (idea #1078) ---------------------------------------------
// NetWatch handoff (idea #3076): a single-use link into a NetWatch session for the user's own company.
function requestNetwatchHandoff() {
  return apiFetch('POST', '/netwatch/handoff', {});
}

function fetchRemoteStations() {
  return apiFetch('GET', '/remote/stations');
}
// Native-RDP self-service ticket (idea #1356) — the browser authenticates with the user's own Windows
// credentials (NLA), so no password is returned. companyId (idea #1619): the station's company — a login
// can span companies, so the client tells the server which company this host is in. Optional; the server
// falls back to the session's home company.
function requestRdpTicket(hostname, target, companyId) {
  return apiFetch('POST', '/remote/rdp-ticket', { hostname: hostname, target: target || 'desktop', company_id: companyId });
}
// Opt-in Shared session ticket (idea #3397): RFB onto the station's live console. The server refuses
// unless this user's per-computer Shared toggle and the fleet kill-switch are both on.
function requestSharedTicket(hostname, companyId) {
  return apiFetch('POST', '/remote/shared-ticket', { hostname: hostname, company_id: companyId });
}

// Report the ironrdp-web connect error to the server so it lands in logs (idea #1356) — the client only
// shows a transient toast otherwise, so connect failures would be invisible server-side without this.
function reportRdpDiag(hostname, phase, error) {
  try { return apiFetch('POST', '/remote/rdp-diag', { hostname: hostname, phase: phase, error: error }); }
  catch (e) { return Promise.resolve(); }
}

// Post-session feedback (idea #1878) — the user flagged an issue/request from the "session complete"
// card OR the list-level "Report a problem" button. The server opens a native-PSA support ticket AND
// logs a beacon. hostname/target are optional (the list-level report has no specific station). Returns
// { ok, number } (number = the ticket ref for the confirmation line).
function submitRdpSessionFeedback(hostname, target, message, context) {
  return apiFetch('POST', '/remote/rdp-feedback', { hostname: hostname || '', target: target || '', message: message || '', context: context || '' });
}

// Sign-in context (idea #1356): domain-join + last-user, to label + prefill the RDP credential prompt.
function requestRdpSigninContext(hostname, companyId) {
  return apiFetch('POST', '/remote/rdp-signin-context', { hostname: hostname, company_id: companyId });
}

// Save the credential the user signed in with (idea #1356) — called AFTER a successful connect only.
function saveRdpCredential(hostname, username, password, companyId) {
  try { return apiFetch('POST', '/remote/rdp-credential', { hostname: hostname, username: username, password: password, company_id: companyId }); }
  catch (e) { return Promise.resolve(); }
}

// --- Billing ----------------------------------------------------------------

function fetchBillingStatus() {
  return apiFetch('GET', '/billing/status');
}

function requestBillingOtp() {
  return apiFetch('POST', '/billing/otp/request', {});
}

function verifyBillingOtp(code) {
  return apiFetch('POST', '/billing/otp/verify', { code: code });
}

function fetchInvoices() {
  return apiFetch('GET', '/billing/invoices');
}

function fetchInvoice(id) {
  return apiFetch('GET', '/billing/invoices/' + encodeURIComponent(id));
}

function fetchBillingPayments() {
  return apiFetch('GET', '/billing/payments');
}

// PDF links are opened via <a target="_blank">, which can't set the X-Session-Id
// header — carry the session id in the query (the API reads it there too).
function invoicePdfUrl(id, qbo) {
  const sid = encodeURIComponent((typeof PORTAL !== 'undefined' && PORTAL.sessionId) || '');
  return portalApiBase() + '/billing/invoices/' + encodeURIComponent(id) +
    (qbo ? '/qbo-pdf' : '/pdf') + '?session_id=' + sid;
}
;

// ========== state.js ==========
// state.js — session tokens, fetched agent context, and the sessionStorage
// ticket draft. Navigation state lives in the URL hash (see route.js), not here.

// Session tokens injected by the EJS shell (sessionId / agentId / username).
function portalContext() {
  return (typeof PORTAL !== 'undefined' && PORTAL) || { sessionId: '', agentId: '', username: '' };
}

// Agent/user context is FETCHED from GET /api/context after the shell loads (it
// is not injected at render), then held here for the rendering + prefill layers.
// `var` (not let/const) + guarded reads: the bundle concatenates files
// alphabetically, so this may be referenced before its initializer runs — var
// hoists to undefined rather than throwing a TDZ ReferenceError.
var portalContextStore = { details: {}, capabilities: {}, recognised: null, authenticated: false, authMethod: 'agent_id', signinRequired: false };

function setPortalContext(ctx) {
  if (ctx && typeof ctx === 'object') {
    portalContextStore = {
      details: ctx.details || {},
      capabilities: ctx.capabilities || {},
      recognised: ctx.recognised !== false,
      // Whether this is a real logged-in session (password/SSO) vs the frictionless
      // agent_id device flow (idea #1078). Remote access is only offered to the former.
      authenticated: ctx.authenticated === true,
      authMethod: ctx.auth_method || 'agent_id',
      // Tray (agent_id) session while the signed-in tier is enforced (idea #3076): the directory,
      // suspend, New User and billing features are offered as "Sign in to use".
      signinRequired: ctx.signin_required === true
    };
  }
}

// True only for a password/SSO session (never the agent_id device flow).
function portalAuthenticated() {
  return !!(portalContextStore && portalContextStore.authenticated);
}

// True when a tray session must sign in before using the signed-in-tier features (idea #3076).
function portalSigninRequired() {
  return !!(portalContextStore && portalContextStore.signinRequired);
}

// Does opening this hash path need a sign-in first? Remote access always needs a password/SSO
// session; users + billing only while the signed-in tier is enforced. Only answers once the
// context is known, so a deep-link before /context loads isn't bounced on a guess.
function portalNeedsSignin(path) {
  if (portalRecognised() === null) return false;
  const c = portalCapabilities();
  if (path === 'remote') return !!c.remote_access && !portalAuthenticated();
  if (path === 'users') return !!(c.directory || c.new_user || c.suspend_user) && portalSigninRequired();
  if (path === 'new/user') return !!c.new_user && portalSigninRequired();
  if (path === 'new/suspend') return !!c.suspend_user && portalSigninRequired();
  if (path === 'billing') return !!c.billing && portalSigninRequired();
  return false;
}

function agentDetails() {
  return (portalContextStore && portalContextStore.details) || {};
}

function portalCapabilities() {
  return (portalContextStore && portalContextStore.capabilities) || {};
}

function portalRecognised() {
  return portalContextStore ? portalContextStore.recognised : null;
}

// --- Lightweight persistence (all guarded; never throws) --------------------

function storeGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storeSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable — non-fatal */
  }
}

function storeRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* non-fatal */
  }
}

function saveTicketDraft(draft) {
  try {
    storeSet(STORE_KEYS.TICKET_DRAFT, JSON.stringify(draft || {}));
  } catch {
    /* non-fatal */
  }
}

function loadTicketDraft() {
  try {
    const raw = storeGet(STORE_KEYS.TICKET_DRAFT);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function clearTicketDraft() {
  storeRemove(STORE_KEYS.TICKET_DRAFT);
}
;

// ========== toast.js ==========
// toast.js — styled toast + modal primitives (replaces native alert/confirm).
// Visual language matches the docs site (cm-* confirm-modal classes + FontAwesome).

const TOAST_ICONS = {
  success: 'fa-circle-check',
  danger: 'fa-circle-exclamation',
  error: 'fa-circle-exclamation',
  warning: 'fa-triangle-exclamation',
  info: 'fa-circle-info'
};

function showToast(message, type, timeoutMs) {
  const stack = document.getElementById('toast-stack');
  if (!stack) return;

  const variant = type || 'info';
  const toast = document.createElement('div');
  toast.className = 'portal-toast portal-toast-' + variant;
  toast.setAttribute('role', 'status');

  const icon = document.createElement('i');
  icon.className = 'fa-solid ' + (TOAST_ICONS[variant] || TOAST_ICONS.info);
  const text = document.createElement('span');
  text.className = 'portal-toast-text';
  text.textContent = message;

  toast.appendChild(icon);
  toast.appendChild(text);
  stack.appendChild(toast);

  // Trigger enter transition on next frame.
  requestAnimationFrame(() => toast.classList.add('show'));

  const dismiss = () => {
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 200);
  };
  toast.addEventListener('click', dismiss);
  setTimeout(dismiss, timeoutMs || 5000);
}

// A blocking message modal (used for fatal states like an expired session).
// `actions` is an array of { label, variant, onClick } — defaults to a single
// dismiss button when omitted.
function showModal(opts) {
  const root = document.getElementById('modal-root');
  if (!root) return;

  const options = opts || {};
  const type = options.type || 'info';

  const backdrop = document.createElement('div');
  backdrop.className = 'cm-backdrop cm-type-' + type;

  const dialog = document.createElement('div');
  dialog.className = 'cm-dialog';

  const iconName = TOAST_ICONS[type] || TOAST_ICONS.info;
  dialog.innerHTML =
    '<div class="cm-header"><div class="cm-title">' + escapeHtml(options.title || 'Notice') + '</div></div>' +
    '<div class="cm-body"><div class="cm-icon"><i class="fa-solid ' + iconName + '"></i></div>' +
    '<div class="cm-message">' + escapeHtml(options.message || '') + '</div></div>' +
    '<div class="cm-footer"></div>';

  const footer = dialog.querySelector('.cm-footer');
  const actions = options.actions || [{ label: 'OK', variant: 'primary' }];
  actions.forEach((action) => {
    const btn = document.createElement('button');
    btn.className = 'cm-btn cm-btn-' + (action.variant === 'secondary' ? 'secondary' : 'primary');
    btn.textContent = action.label;
    btn.addEventListener('click', () => {
      if (typeof action.onClick === 'function') action.onClick();
      if (!options.persist) close();
    });
    footer.appendChild(btn);
  });

  function close() {
    backdrop.classList.remove('show');
    setTimeout(() => backdrop.remove(), 200);
  }

  backdrop.appendChild(dialog);
  root.appendChild(backdrop);
  requestAnimationFrame(() => backdrop.classList.add('show'));
  return close;
}

// Shared HTML escaper (used by ui.js render paths too).
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}
;

// ========== billing.js ==========
// billing.js — the portal billing surface (OTP gate + invoices + payments).
// UI + controllers only; the data layer lives in service.js. Sub-navigation
// rides the URL hash (#billing · #billing?tab=payments · #billing?inv=<id>) so
// every view is deep-linkable and survives a refresh — enterBilling() re-derives
// what to show from the hash params each time the route fires.

// --- formatting -------------------------------------------------------------

function fmtMoney(n) {
  const v = Number(n || 0);
  try { return v.toLocaleString(undefined, { style: 'currency', currency: 'USD' }); }
  catch { return '$' + v.toFixed(2); }
}

// Client-friendly status pill: Overdue (red) / Paid (green) / Open (blue).
function invoiceStatusPill(inv) {
  if (inv.overdue) return '<span class="status-pill status-danger">Overdue</span>';
  if (inv.status === 'paid') return '<span class="status-pill status-success">Paid</span>';
  return '<span class="status-pill status-info">Open</span>';
}

function billingError(msg) {
  return '<div class="empty-state"><i class="fa-solid fa-triangle-exclamation cp-text-danger"></i><p>' +
    escapeHtml(msg) + '</p></div>';
}

// --- panel toggling ---------------------------------------------------------

function setBillingPanel(name) {
  ['loading', 'gate', 'list', 'detail'].forEach((p) => {
    const el = document.getElementById('billing-' + p);
    if (el) el.classList.toggle('is-hidden', p !== name);
  });
}

// --- entry point (called by the router on #billing) -------------------------

async function enterBilling(params) {
  setBillingPanel('loading');
  try {
    const st = await fetchBillingStatus();
    if (!st || !st.verified) { showBillingGate(); return; }
    if (params && params.inv) { setBillingPanel('detail'); loadInvoiceDetail(params.inv); return; }
    setBillingPanel('list');
    setBillingTab(params && params.tab === 'payments' ? 'payments' : 'invoices');
  } catch (error) {
    if (error.code === 'session_expired') return handlePortalError(error);
    showBillingGate();
    showToast((error && error.message) || 'Failed to open billing.', 'danger');
  }
}

// --- OTP gate ---------------------------------------------------------------

function showBillingGate() {
  setBillingPanel('gate');
  const reqForm = document.getElementById('otp-request-form-el');
  const verForm = document.getElementById('otp-verify-form-el');
  if (reqForm) reqForm.classList.remove('is-hidden');
  if (verForm) verForm.classList.add('is-hidden');
}

async function handleOtpRequest(event) {
  event.preventDefault();
  const btn = event.currentTarget.querySelector('[type="submit"]');
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sending…'; }
  try {
    const r = await requestBillingOtp();
    const dest = document.getElementById('otp-dest');
    if (dest && r.email_masked) dest.textContent = r.email_masked;
    document.getElementById('otp-request-form-el').classList.add('is-hidden');
    const vf = document.getElementById('otp-verify-form-el');
    if (vf) vf.classList.remove('is-hidden');
    const code = document.getElementById('otp-code');
    if (code) { code.value = ''; code.focus(); }
    showToast('We’ve emailed you a code.', 'success');
  } catch (error) {
    handlePortalError(error, 'Could not send your code.');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fa-solid fa-paper-plane"></i> Email me a code'; }
  }
}

async function handleOtpResend() {
  try {
    const r = await requestBillingOtp();
    const dest = document.getElementById('otp-dest');
    if (dest && r.email_masked) dest.textContent = r.email_masked;
    showToast('A new code is on its way.', 'success');
  } catch (error) {
    handlePortalError(error, 'Could not resend the code.');
  }
}

async function handleOtpVerify(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const code = form.querySelector('#otp-code');
  const btn = form.querySelector('[type="submit"]');
  const val = code && code.value ? code.value.trim() : '';
  if (!val) { if (code) code.focus(); return; }
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Verifying…'; }
  try {
    await verifyBillingOtp(val);
    showToast('Verified.', 'success');
    // Re-enter with whatever params the URL carries (e.g. a deep-linked invoice).
    enterBilling(parseHash().params);
  } catch (error) {
    handlePortalError(error, 'Verification failed.');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = 'Verify & continue'; }
  }
}

// --- invoices / payments tabs ----------------------------------------------

function setBillingTab(tab) {
  document.querySelectorAll('.billing-tabs .filter-tab').forEach((b) =>
    b.classList.toggle('is-active', b.getAttribute('data-tab') === tab));
  const inv = document.getElementById('billing-invoices');
  const pay = document.getElementById('billing-payments');
  if (inv) inv.classList.toggle('is-hidden', tab !== 'invoices');
  if (pay) pay.classList.toggle('is-hidden', tab !== 'payments');
  if (tab === 'payments') loadBillingPayments();
  else loadBillingInvoices();
}

async function loadBillingInvoices() {
  const c = document.getElementById('billing-invoices');
  if (!c) return;
  c.innerHTML = '<div class="loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading invoices…</div>';
  try {
    const r = await fetchInvoices();
    renderInvoiceList(r.invoices || []);
  } catch (error) {
    if (error.code === 'session_expired') return handlePortalError(error);
    c.innerHTML = billingError(error.message || 'Failed to load invoices.');
  }
}

function renderInvoiceList(invoices) {
  const c = document.getElementById('billing-invoices');
  if (!c) return;
  if (!invoices.length) {
    c.innerHTML = '<div class="empty-state"><i class="fa-solid fa-file-invoice"></i><p>No invoices yet.</p></div>';
    return;
  }
  c.innerHTML = '';
  invoices.forEach((inv) => {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'invoice-row' + (inv.overdue ? ' is-overdue' : '');
    row.setAttribute('data-action', 'open-invoice');
    row.setAttribute('data-invoice-id', inv.id);
    const when = inv.due_at ? 'Due ' + formatLocalDate(inv.due_at, false)
      : (inv.issued_at ? formatLocalDate(inv.issued_at, false) : '');
    const amount = inv.balance > 0 ? inv.balance : inv.total;
    row.innerHTML =
      '<span class="invoice-row-main">' +
        '<span class="invoice-row-title">#' + escapeHtml(inv.number || inv.id) + '</span>' +
        '<span class="invoice-row-meta">' + escapeHtml(when) + '</span>' +
      '</span>' +
      '<span class="invoice-row-right">' +
        '<span class="invoice-amount">' + fmtMoney(amount) + '</span>' +
        invoiceStatusPill(inv) +
      '</span>';
    c.appendChild(row);
  });
}

async function loadInvoiceDetail(id) {
  const c = document.getElementById('billing-detail-body');
  if (!c) return;
  c.innerHTML = '<div class="loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading invoice…</div>';
  try {
    const r = await fetchInvoice(id);
    renderInvoiceDetail(r.invoice);
  } catch (error) {
    if (error.code === 'session_expired') return handlePortalError(error);
    c.innerHTML = billingError(error.message || 'Failed to load invoice.');
  }
}

function _detailItem(label, value) {
  return '<div class="detail-item"><span class="detail-label">' + escapeHtml(label) +
    '</span><span class="detail-value">' + escapeHtml(value) + '</span></div>';
}

function renderInvoiceDetail(inv) {
  const c = document.getElementById('billing-detail-body');
  if (!c || !inv) return;

  const lines = (inv.lines || []).map((l) =>
    '<tr><td>' + escapeHtml(l.description || '') + '</td>' +
    '<td class="num">' + escapeHtml(String(l.qty || 0)) + '</td>' +
    '<td class="num">' + fmtMoney(l.unit_price) + '</td>' +
    '<td class="num">' + fmtMoney(l.amount) + '</td></tr>').join('');

  const payments = (inv.payments || []).map((p) =>
    '<tr><td>' + escapeHtml(p.paid_at ? formatLocalDate(p.paid_at, false) : '—') + '</td>' +
    '<td>' + escapeHtml(p.method || 'Payment') + '</td>' +
    '<td class="num">' + fmtMoney(p.amount) + '</td></tr>').join('');

  const qboBtn = inv.has_qbo_pdf
    ? '<a class="cp-btn cp-btn-outline-secondary cp-btn-sm" href="' + invoicePdfUrl(inv.id, true) +
      '" target="_blank" rel="noopener"><i class="fa-solid fa-file-pdf"></i> QuickBooks PDF</a>'
    : '';

  // External pay link (admin-configured). Server only sets pay_url on payable invoices.
  const payBtn = inv.pay_url
    ? '<a class="cp-btn cp-btn-success cp-btn-sm" href="' + String(inv.pay_url).replace(/"/g, '%22') +
      '" target="_blank" rel="noopener"><i class="fa-solid fa-credit-card"></i> ' +
      escapeHtml(inv.pay_label || 'Pay Now') + '</a>'
    : '';

  c.innerHTML =
    '<div class="detail-toolbar"><div>' +
      '<div class="cp-mb-2"><span class="status-pill status-neutral">#' + escapeHtml(inv.number || inv.id) + '</span> ' +
        invoiceStatusPill(inv) + '</div>' +
      '<div class="portal-heading">' + fmtMoney(inv.total) + '</div>' +
    '</div><div class="detail-toolbar-actions">' + payBtn +
      '<a class="cp-btn cp-btn-outline-secondary cp-btn-sm" href="' + invoicePdfUrl(inv.id, false) +
        '" target="_blank" rel="noopener"><i class="fa-solid fa-file-pdf"></i> PDF</a>' + qboBtn +
    '</div></div>' +
    '<div class="detail-grid">' +
      _detailItem('Issued', inv.issued_at ? formatLocalDate(inv.issued_at, false) : '—') +
      _detailItem('Due', inv.due_at ? formatLocalDate(inv.due_at, false) : '—') +
      _detailItem('Total', fmtMoney(inv.total)) +
      _detailItem('Paid', fmtMoney(inv.paid)) +
      _detailItem('Balance', fmtMoney(inv.balance)) +
    '</div>' +
    '<div class="section-title"><i class="fa-solid fa-list"></i> Line Items</div>' +
    (lines
      ? '<table class="billing-table"><thead><tr><th>Description</th><th class="num">Qty</th>' +
        '<th class="num">Unit</th><th class="num">Amount</th></tr></thead><tbody>' + lines + '</tbody></table>'
      : '<p class="portal-muted">No line items.</p>') +
    (payments
      ? '<div class="section-title"><i class="fa-solid fa-receipt"></i> Payments</div>' +
        '<table class="billing-table"><thead><tr><th>Date</th><th>Method</th>' +
        '<th class="num">Amount</th></tr></thead><tbody>' + payments + '</tbody></table>'
      : '');
}

async function loadBillingPayments() {
  const c = document.getElementById('billing-payments');
  if (!c) return;
  c.innerHTML = '<div class="loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading payments…</div>';
  try {
    const r = await fetchBillingPayments();
    renderPaymentList(r.payments || []);
  } catch (error) {
    if (error.code === 'session_expired') return handlePortalError(error);
    c.innerHTML = billingError(error.message || 'Failed to load payments.');
  }
}

function renderPaymentList(payments) {
  const c = document.getElementById('billing-payments');
  if (!c) return;
  if (!payments.length) {
    c.innerHTML = '<div class="empty-state"><i class="fa-solid fa-receipt"></i><p>No payments recorded yet.</p></div>';
    return;
  }
  c.innerHTML =
    '<table class="billing-table"><thead><tr><th>Date</th><th>Invoice</th><th>Method</th>' +
    '<th class="num">Amount</th></tr></thead><tbody>' +
    payments.map((p) =>
      '<tr><td>' + escapeHtml(p.paid_at ? formatLocalDate(p.paid_at, false) : '—') + '</td>' +
      '<td>' + (p.invoice_number ? '#' + escapeHtml(p.invoice_number) : '—') + '</td>' +
      '<td>' + escapeHtml(p.method || 'Payment') + '</td>' +
      '<td class="num">' + fmtMoney(p.amount) + '</td></tr>').join('') +
    '</tbody></table>';
}
;

// ========== chat.js ==========
// chat.js — native websocket chat for the client portal (idea #1184). Retires ScreenConnect
// chat. This view is ALSO what the NetWatch agent pops on the station in an always-on-top
// WebView2, so it must stand alone with only the portal session (agent_id) for identity.
//
// Live transport: socket.io to the websocket cluster, authenticated with agent_id — the socket
// is auto-joined to the room named by that agent_id, which is exactly where the server emits
// chat_message / chat_typing / chat_ticket_attached. We send with `chat_send` and fall back to
// POST /api/chat/send if the socket is down. No import/require, plain functions, global scope
// (portal house rules) — mirrors the other src/js modules.

var _chatSocket = null;
var _chatSessionId = null;
var _chatWired = false;
var _chatSeen = null;          // Set of rendered message ids (dedupe echoes/backfill)
var _chatTypingSendTimer = null;
var _chatTypingHideTimer = null;
var _chatTicketNumber = null;

// Resolve the websocket origin. Default is same-origin (nginx proxies /socket.io/ at
// portal.cns.tools to the cluster, like the dashboard). A deploy can override with
// PORTAL.wsOrigin (e.g. 'https://ws.cns.tools') when the WS is on a separate host.
function chatWsOrigin() {
  return (typeof PORTAL !== 'undefined' && PORTAL && PORTAL.wsOrigin) || undefined;
}

// Called by the router when #chat is entered. Bootstraps the session + history, opens the
// socket, reveals the nav item, and focuses the input.
async function enterChat() {
  wireChatOnce();
  if (!_chatSeen) _chatSeen = {};
  const messagesEl = document.getElementById('chat-messages');
  const emptyEl = document.getElementById('chat-empty');
  try {
    const res = await resolveChatSession();
    _chatSessionId = res.session && res.session.id;
    _chatTicketNumber = (res.session && res.session.ticket_number) || null;
    // Feature is enabled for us — surface the nav entry.
    const nav = document.getElementById('nav-chat');
    if (nav) nav.classList.remove('is-hidden');
    if (messagesEl) messagesEl.innerHTML = '';
    _chatSeen = {};
    (res.history || []).forEach(function (m) { renderChatMessage(m, false); });
    if (!(res.history || []).length && emptyEl) {
      messagesEl.appendChild(buildEmpty('Say hello — a technician will be with you shortly.'));
    }
    applyTicketBanner(_chatTicketNumber);
    ensureChatSocket();
    scrollChatToBottom();
    const input = document.getElementById('chat-input');
    if (input) input.focus();
  } catch (err) {
    if (messagesEl) {
      messagesEl.innerHTML = '';
      messagesEl.appendChild(buildEmpty(err && err.code === 'chat_disabled'
        ? 'Live chat is not available right now.'
        : 'Could not open chat. Please try again.'));
    }
  }
}

// Navigating away — stop typing timers. We keep the socket connected so messages that arrive
// while the user is on another view still land (and can badge the nav later).
function leaveChat() {
  clearTimeout(_chatTypingSendTimer); _chatTypingSendTimer = null;
  sendTyping(false);
}

function ensureChatSocket() {
  if (_chatSocket || typeof io === 'undefined') return;
  const ctx = (typeof portalContext === 'function') ? portalContext() : (PORTAL || {});
  // The portal socket server authenticates by session_id (resolved to the station server-side); the
  // dashboard WS is separate and private. Both station ids are sent too (agent_id may be blank on a
  // NetWatch-uuid-only URL) so the join carries the identity (idea #1184).
  const _auth = { session_id: ctx.sessionId || '', agent_id: ctx.agentId || '', agent_uid: ctx.agentUid || '' };
  _chatSocket = io(chatWsOrigin(), {
    auth: _auth,
    query: _auth,
    transports: ['websocket'],
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  // Announce our active chat on connect AND every reconnect (idea #1184) so the server tracks
  // presence — a reconnect after a refresh cancels the disconnect's pending "user closed" grace
  // close, while a genuine window close never reconnects and the chat closes server-side.
  _chatSocket.on('connect', function () {
    if (_chatSessionId) _chatSocket.emit('chat_join', { session_id: _chatSessionId });
  });

  _chatSocket.on('chat_message', function (payload) {
    if (!payload || (payload.session_id && payload.session_id !== _chatSessionId)) return;
    renderChatMessage(payload.message, true);
    scrollChatToBottom();
  });

  _chatSocket.on('chat_typing', function (payload) {
    if (!payload || (payload.session_id && payload.session_id !== _chatSessionId)) return;
    showTyping(payload.isTyping, payload.who);
  });

  _chatSocket.on('chat_ticket_attached', function (payload) {
    if (!payload || (payload.session_id && payload.session_id !== _chatSessionId)) return;
    _chatTicketNumber = payload.number || _chatTicketNumber;
    applyTicketBanner(_chatTicketNumber);
  });

  // Relatedness folded this new chat into an existing ticket that already had a conversation
  // (idea #1184). Adopt the resumed session and reload the OLD conversation in place — so instead
  // of a blank new chat the user sees the continued thread. `from` guards it to OUR just-started
  // session so a switch for someone else's station is ignored.
  _chatSocket.on('chat_switch', function (payload) {
    if (!payload || !payload.session_id) return;
    if (payload.from && payload.from !== _chatSessionId) return;
    _chatSessionId = payload.session_id;
    _chatTicketNumber = payload.number || _chatTicketNumber;
    const messagesEl = document.getElementById('chat-messages');
    if (messagesEl) messagesEl.innerHTML = '';
    _chatSeen = {};
    (payload.history || []).forEach(function (m) { renderChatMessage(m, false); });
    applyTicketBanner(_chatTicketNumber);
    scrollChatToBottom();
    // Re-announce presence on the adopted session so delivery + close-detection track it.
    if (_chatSocket && _chatSocket.connected) _chatSocket.emit('chat_join', { session_id: _chatSessionId });
  });

  _chatSocket.on('chat_error', function (payload) {
    if (typeof showToast === 'function' && payload && payload.error) showToast('Chat: ' + payload.error, 'danger');
  });
  // NB (idea #1858): deliberately NO chat_closed → window.close() handler. A chat window must NEVER
  // auto-close on the station — the user can send at any time while it's open. Closing is the user's
  // choice only.

  // On reconnect, re-pull history so nothing dropped during the gap is missed.
  _chatSocket.on('connect', function () {
    if (!_chatSessionId) return;
    fetchChatHistory(_chatSessionId).then(function (res) {
      (res.history || []).forEach(function (m) { renderChatMessage(m, false); });
      scrollChatToBottom();
    }).catch(function () { /* best effort */ });
  });
}

// Send the current input. The server echoes the stored message back to our room, so we don't
// optimistically render — the echo (with its real id) is the single source of truth, deduped.
function submitChat(ev) {
  if (ev) ev.preventDefault();
  const input = document.getElementById('chat-input');
  if (!input) return;
  const body = input.value.trim();
  if (!body || !_chatSessionId) return;
  input.value = '';
  autoGrow(input);
  sendTyping(false);
  const extId = 'c-' + _chatSessionId + '-' + genClientId();
  if (_chatSocket && _chatSocket.connected) {
    _chatSocket.emit('chat_send', { session_id: _chatSessionId, body: body, ext_id: extId });
  } else {
    // Socket down — HTTP fallback still persists + emits server-side.
    sendChatHttp(_chatSessionId, body, extId).catch(function () {
      if (typeof showToast === 'function') showToast('Message failed to send.', 'danger');
    });
  }
}

// Throttled typing ping while the user types; auto-off after a short idle.
function onChatInput() {
  const input = document.getElementById('chat-input');
  if (input) autoGrow(input);
  if (!_chatSessionId) return;
  if (!_chatTypingSendTimer) {
    sendTyping(true);
    _chatTypingSendTimer = setTimeout(function () { _chatTypingSendTimer = null; }, 1500);
  }
  clearTimeout(_chatTypingHideTimer);
  _chatTypingHideTimer = setTimeout(function () { sendTyping(false); }, 2500);
}

function sendTyping(isTyping) {
  if (!_chatSessionId || !_chatSocket || !_chatSocket.connected) return;
  _chatSocket.emit('chat_typing', { session_id: _chatSessionId, is_typing: !!isTyping });
}

// --- rendering (bubble style ported from bundles/messages) -------------------

// Render one message. On the PORTAL the END-USER's own message (server direction 'inbound')
// sits on the RIGHT (bubble side 'outbound'); the technician's ('outbound') sits on the LEFT.
function renderChatMessage(m, animate) {
  if (!m || !m.id) return;
  if (_chatSeen[m.id]) return;
  _chatSeen[m.id] = true;
  const messagesEl = document.getElementById('chat-messages');
  if (!messagesEl) return;
  const emptyEl = document.getElementById('chat-empty');
  if (emptyEl && emptyEl.parentNode) emptyEl.parentNode.removeChild(emptyEl);
  const existingEmpty = messagesEl.querySelector('.empty-state');
  if (existingEmpty) existingEmpty.remove();

  const mine = m.direction === 'inbound';           // inbound == this end-user
  const side = mine ? 'outbound' : 'inbound';        // CSS side from our perspective
  const name = m.senderName || (mine ? 'You' : 'Support');

  const wrapper = document.createElement('div');
  wrapper.className = 'message-wrapper' + (animate ? ' is-new' : '');
  wrapper.setAttribute('data-message-id', m.id);

  const container = document.createElement('div');
  container.className = 'message-container';

  const avatar = document.createElement('div');
  avatar.className = 'user-avatar ' + side;
  avatar.textContent = (name || '?').charAt(0);

  const content = document.createElement('div');
  content.className = 'message-content';

  const info = document.createElement('div');
  info.className = 'user-info';
  const sender = document.createElement('span');
  sender.className = 'sender-name';
  sender.textContent = name;
  const time = document.createElement('span');
  time.className = 'message-timestamp';
  time.textContent = formatChatTime(m.timestamp);
  info.appendChild(sender); info.appendChild(time);

  const bubble = document.createElement('div');
  bubble.className = 'message-bubble ' + side;
  const text = document.createElement('div');
  text.className = 'message-text';
  text.textContent = m.body || '';
  bubble.appendChild(text);

  content.appendChild(info);
  content.appendChild(bubble);
  container.appendChild(avatar);
  container.appendChild(content);
  wrapper.appendChild(container);
  messagesEl.appendChild(wrapper);
}

function showTyping(isTyping, who) {
  const el = document.getElementById('chat-typing');
  if (!el) return;
  if (isTyping) {
    const whoEl = document.getElementById('chat-typing-who');
    if (whoEl && who) whoEl.textContent = who;
    el.classList.remove('is-hidden');
    scrollChatToBottom();
  } else {
    el.classList.add('is-hidden');
  }
}

function applyTicketBanner(number) {
  const banner = document.getElementById('chat-ticket-banner');
  const numEl = document.getElementById('chat-ticket-number');
  if (!banner) return;
  if (number) {
    if (numEl) numEl.textContent = '#' + number;
    banner.classList.remove('is-hidden');
  } else {
    banner.classList.add('is-hidden');
  }
}

// --- helpers ----------------------------------------------------------------

function wireChatOnce() {
  if (_chatWired) return;
  _chatWired = true;
  const form = document.getElementById('chat-form');
  if (form) form.addEventListener('submit', submitChat);
  const input = document.getElementById('chat-input');
  if (input) {
    input.addEventListener('input', onChatInput);
    // Enter sends, Shift+Enter is a newline.
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitChat(); }
    });
  }
}

function buildEmpty(msg) {
  const d = document.createElement('div');
  d.className = 'empty-state';
  d.textContent = msg;
  return d;
}

function scrollChatToBottom() {
  const el = document.getElementById('chat-messages');
  if (el) el.scrollTop = el.scrollHeight;
}

function autoGrow(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 140) + 'px';
}

function formatChatTime(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}

function genClientId() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}
;

// ========== events.js ==========
// events.js — delegated event handling + action controllers. Binds the data
// layer (service.js) to the rendering layer (ui.js). No inline on* handlers.

// Surface API/session errors consistently.
function handlePortalError(error, fallbackMessage) {
  if (error && error.code === 'session_expired') {
    // A dead session must not loop the restore — forget it so reload lands on sign-in.
    if (typeof cpClearPersisted === 'function') cpClearPersisted();
    showModal({
      type: 'warning',
      title: 'Session expired',
      message: 'Your secure session has ended. Please sign in again.',
      persist: true,
      actions: [{ label: 'Sign in', variant: 'primary', onClick: () => location.reload() }]
    });
    return;
  }
  showToast((error && error.message) || fallbackMessage || 'Something went wrong.', 'danger');
}

// Sign out (idea #1078): end the server session, forget the persisted session, and reload
// — which lands on the sign-in screen (no agent_id, nothing persisted).
async function handlePortalLogout() {
  try { await logoutRequest(); } catch (e) { /* best-effort */ }
  if (typeof cpClearPersisted === 'function') cpClearPersisted();
  try { if (window.PORTAL) window.PORTAL.sessionId = ''; } catch (e) { /* ignore */ }
  location.reload();
}

// --- Context (agent/user) ----------------------------------------------------

async function loadPortalContext() {
  try {
    const ctx = await fetchContext();
    setPortalContext(ctx);
    if (ctx.recognised === false) {
      renderUnrecognisedContext();
    } else {
      renderWelcomeContext();
    }
    applyCapabilities(ctx.capabilities);
    // A deep-link that needs a sign-in (idea #3076) → offer it rather than bounce silently.
    if (portalNeedsSignin(parseHash().path)) {
      const wanted = parseHash().path;
      navigate('view-welcome');
      if (typeof cpStartSigninUpgrade === 'function') cpStartSigninUpgrade(wanted);
    }
    // Capabilities are now known — if the user lacks Billing but deep-linked the
    // (optimistically rendered) Billing view, bounce them back to the dashboard.
    if (parseHash().path === 'billing' && !portalCapabilities().billing) {
      navigate('view-welcome');
    }
    // Same for a #remote deep-link: only an authenticated (password/SSO) session with
    // the capability may stay (idea #1078).
    if (parseHash().path === 'remote' && !(portalCapabilities().remote_access && portalAuthenticated())) {
      navigate('view-welcome');
    }
    prefillTicketForm();
  } catch (error) {
    if (error.code === 'session_expired') return handlePortalError(error);
    // Non-fatal: leave the shell usable, just surface it.
    showToast(error.message || 'Could not load your details.', 'warning');
  }
}

// Full page reload that re-supplies the auth params (agent_id / username) which
// init.js scrubs from the URL after boot — without them the reloaded shell can't
// re-mint the session. We still hold them in window.PORTAL, so rebuild the boot
// URL and reload; the current view is preserved via the (untouched) hash, and
// the one-shot open_chat/ticketid intents are deliberately NOT re-added so they
// don't re-fire. Falls back to a plain reload if we have no agent id.
function reloadPortalWithAuth() {
  const p = (typeof PORTAL !== 'undefined' && PORTAL) || null;
  if (p && (p.agentId || p.agentUid)) {
    const params = new URLSearchParams();
    if (p.agentId) params.set('agent_id', p.agentId);
    if (p.agentUid) params.set('agent_uid', p.agentUid);
    if (p.username) params.set('username', p.username);
    location.href = location.pathname + '?' + params.toString() + (location.hash || '');
  } else {
    location.reload();
  }
}

// Open the portal in the system browser, carrying the auth params (agent_id /
// username from window.PORTAL) + current hash so the external tab establishes its
// own session and lands on the same view. Used by the mobile/native-tray button.
function openPortalExternal() {
  const p = (typeof PORTAL !== 'undefined' && PORTAL) || null;
  let url = location.origin + location.pathname;
  if (p && (p.agentId || p.agentUid)) {
    const params = new URLSearchParams();
    if (p.agentId) params.set('agent_id', p.agentId);
    if (p.agentUid) params.set('agent_uid', p.agentUid);
    if (p.username) params.set('username', p.username);
    url += '?' + params.toString();
  }
  url += (location.hash || '');
  window.open(url, '_blank', 'noopener');
}

// --- Tickets -----------------------------------------------------------------

async function loadTickets() {
  setTicketsState('loading');
  try {
    const result = await fetchUserTickets();
    renderTicketList(result.tickets || []);
  } catch (error) {
    if (error.code === 'session_expired') return handlePortalError(error);
    showTicketsError(error.message || 'Failed to load tickets.');
  }
}

async function loadTicketDetails(ticketId) {
  const id = ticketId || currentTicketId();
  if (!id) {
    showTicketDetailsError('No ticket selected.');
    return;
  }
  setTicketDetailsState('loading');
  try {
    const data = await fetchTicketDetails(id);
    renderTicketDetails(data);
  } catch (error) {
    if (error.code === 'session_expired') return handlePortalError(error);
    showTicketDetailsError(error.message || 'Failed to load ticket details.');
  }
}

// --- Users directory ---------------------------------------------------------

// The directory list is hidden behind a button and loaded on first reveal.
var _directoryLoaded = false;
// Pending prefill carried from a directory "Disable" click into the suspend form.
var _suspendPrefill = null;

function toggleDirectory(btn) {
  const panel = document.getElementById('directory-panel');
  if (!panel) return;
  panel.classList.toggle('is-hidden');
  const open = !panel.classList.contains('is-hidden');
  if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open && !_directoryLoaded) { _directoryLoaded = true; loadCompanyUsers(); }
}

// Directory "Disable" → stash the target + jump to the suspend form (prefilled).
function startDisableUser(trigger) {
  const accStr = trigger.getAttribute('data-accounts') || '';
  _suspendPrefill = {
    name: trigger.getAttribute('data-name') || '',
    email: trigger.getAttribute('data-email') || '',
    accounts: accStr ? accStr.split(',').filter(Boolean) : []
  };
  navigate('view-suspend');
}

// Prepare the suspend form when its view opens: load the suspendable-user list
// (once), then apply any prefill from a directory "Disable" click, else reset.
async function prepareSuspendForm() {
  if (_suspendUsers.length === 0) {
    try {
      const r = await fetchSuspendableUsers();
      setSuspendUsers(r.users || [], r.managed_accounts || []);
    } catch (error) {
      if (error.code === 'session_expired') return handlePortalError(error);
      // Non-fatal: the picker just won't have suggestions.
    }
  }
  if (_suspendPrefill) {
    const pf = _suspendPrefill;
    _suspendPrefill = null;
    let u = _suspendUsers.find((x) => (x.email || '').toLowerCase() === (pf.email || '').toLowerCase() && pf.email);
    if (!u) u = { name: pf.name, email: pf.email, accounts: (pf.accounts || []).map((s) => ({ system: s, enabled: true })) };
    selectSuspendUser(u);
  } else {
    resetSuspendPicker();
  }
}

async function loadCompanyUsers() {
  setUsersMessage('<div class="loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading people…</div>');
  try {
    const result = await fetchCompanyUsers();
    renderUsersDirectory(result.users || []);
  } catch (error) {
    if (error.code === 'session_expired') return handlePortalError(error);
    if (error.status === 403) {
      setUsersMessage('<div class="empty-state"><i class="fa-solid fa-lock"></i>' +
        '<p>You don’t have access to the user directory.</p></div>');
      return;
    }
    setUsersMessage('<div class="empty-state"><i class="fa-solid fa-triangle-exclamation cp-text-danger"></i>' +
      '<p>' + escapeHtml(error.message || 'Failed to load users.') + '</p></div>');
  }
}

// --- Live updates (poll the open ticket for new activity) -------------------
// A lightweight poll (the cluster is rarely used, so SSE/websockets are overkill):
// while a ticket detail is open, re-fetch every 12s and surgically append any new
// actions with an animation + "New" badge. Paused while the tab is backgrounded.

var _detailPollTimer = null;
var _detailPollId = null;
var POLL_INTERVAL_MS = 12000;

function startDetailPolling(ticketId) {
  stopDetailPolling();
  _detailPollId = ticketId;
  _detailPollTimer = setInterval(pollTicketDetails, POLL_INTERVAL_MS);
}

function stopDetailPolling() {
  if (_detailPollTimer) { clearInterval(_detailPollTimer); _detailPollTimer = null; }
  _detailPollId = null;
}

async function pollTicketDetails() {
  if (!_detailPollId || document.hidden) return;
  try {
    const data = await fetchTicketDetails(_detailPollId);
    const added = mergeNewActions(data.actions || []);
    updateTicketStatusPill(data.ticket);
    if (added > 0) showToast(added === 1 ? 'New update on this ticket' : added + ' new updates', 'info', 3000);
  } catch { /* silent on poll — the next tick retries */ }
}

// --- Live chat (ScreenConnect) ----------------------------------------------

async function startChatFlow(ticketId) {
  const card = document.getElementById('chat-card');
  const setCardBody = (html) => {
    if (card) {
      const body = card.querySelector('.action-card-body');
      if (body) body.innerHTML = html;
    }
  };

  if (card) {
    card.classList.add('is-busy');
    setCardBody('<i class="fa-solid fa-hourglass-half action-card-icon"></i><span class="action-card-title">Initiating chat…</span>');
  }

  try {
    await requestScChat(ticketId);
    setCardBody('<i class="fa-solid fa-circle-check action-card-icon cp-text-success"></i><span class="action-card-title">Chat started</span><span class="action-card-text">A chat window should now be open on your device.</span>');
    setTimeout(() => { try { window.close(); } catch { /* popup may not be closable */ } }, 3000);
  } catch (error) {
    if (card) card.classList.remove('is-busy');
    setCardBody('<i class="fa-solid fa-triangle-exclamation action-card-icon cp-text-danger"></i><span class="action-card-title">Chat failed</span><span class="action-card-text">' + escapeHtml((error && error.message) || 'Please try again.') + '</span>');
  }
}

// --- Delegated click handling -----------------------------------------------

function handleDelegatedClick(event) {
  const trigger = event.target.closest('[data-action]');
  if (!trigger) return;
  const action = trigger.getAttribute('data-action');

  switch (action) {
    case 'show-view':
      navigate(trigger.getAttribute('data-view'));
      break;
    case 'open-netwatch':
      openNetwatch();
      break;
    case 'remote-connect-rdp':
      openRdpViewer(trigger.getAttribute('data-host'), trigger.getAttribute('data-target'), trigger.getAttribute('data-tname'), trigger.getAttribute('data-company'));
      break;
    case 'remote-connect-shared':
      openSharedViewer(trigger.getAttribute('data-host'), trigger.getAttribute('data-company')); // idea #3397
      break;
    case 'remote-shared-takecontrol':
      sharedTakeControl();
      break;
    case 'rdp-close':
      rdpEndSession();
      break;
    case 'remote-close':
      closeRemoteViewer();
      break;
    case 'remote-cad':
      // The Shared (RFB) session, idea #3397, sends it through noVNC; otherwise the RDP sender (idea #2258).
      if (typeof SHARED !== 'undefined' && SHARED && SHARED.host) sharedSendCad();
      else if (typeof rdpSendCad === 'function') rdpSendCad();
      break;
    case 'remote-release-keys':
      rdpReleaseStuckKeys();
      break;
    case 'remote-show-keyboard':
      // Explicit tap-to-summon for the mobile on-screen keyboard (idea #2259) — a <canvas> can never
      // raise it, however it's focused, so this focuses the hidden relay input instead.
      rdpShowKeyboard();
      break;
    case 'remote-fullscreen':
      toggleRemoteFullscreen();
      break;
    case 'remote-paste':
      if (typeof SHARED !== 'undefined' && SHARED && SHARED.host) sharedPasteToRemote();
      else rdpPasteToRemote();
      break;
    case 'remote-mute':
      rdpAudioToggleMute();
      break;
    case 'remote-zoom-in':
      rdpZoomIn();
      break;
    case 'remote-zoom-out':
      rdpZoomOut();
      break;
    case 'remote-zoom-reset':
      rdpZoomReset();
      break;
    case 'remote-report':
      openRdpReportModal();
      break;
    case 'show-existing':
      navigate('view-existing');
      break;
    case 'retry-tickets':
      loadTickets();
      break;
    case 'refresh-users':
      _directoryLoaded = true;
      loadCompanyUsers();
      break;
    case 'toggle-directory':
      toggleDirectory(trigger);
      break;
    case 'filter-users':
      setUserFilter(trigger.getAttribute('data-ufilter'));
      break;
    case 'disable-user':
      startDisableUser(trigger);
      break;
    case 'suspend-pick':
      selectSuspendUser(_suspendUsers[Number(trigger.getAttribute('data-su-index'))]);
      break;
    case 'refresh-portal':
      reloadPortalWithAuth();
      break;
    case 'open-external':
      openPortalExternal();
      break;
    case 'logout':
      handlePortalLogout();
      break;
    case 'billing-tab':
      location.hash = trigger.getAttribute('data-tab') === 'payments' ? 'billing?tab=payments' : 'billing';
      break;
    case 'open-invoice':
      location.hash = 'billing?inv=' + encodeURIComponent(trigger.getAttribute('data-invoice-id'));
      break;
    case 'billing-back':
      location.hash = 'billing';
      break;
    case 'otp-resend':
      handleOtpResend();
      break;
    case 'open-ticket':
      navigate('view-ticket-details', { id: trigger.getAttribute('data-ticket-id') });
      break;
    case 'filter-tickets':
      setTicketFilter(trigger.getAttribute('data-filter'));
      break;
    case 'refresh-details':
      loadTicketDetails();
      break;
    case 'start-chat':
      startChatFlow(trigger.getAttribute('data-ticket-id') || currentTicketId() || null);
      break;
    // Reopen an onboarding request for correction (idea #2007). Keyed on the ticket NUMBER —
    // that's what the resume form resolves on, and what the client is told to quote.
    case 'update-request': {
      const num = trigger.getAttribute('data-ticket-number');
      if (num) navigate('view-newuser', { ticket: num });
      break;
    }
    default:
      break;
  }

  // Hand the keyboard back to the remote desktop after any viewer-toolbar action (idea #2258). Clicking
  // a button moves DOM focus to that button, and the RDP viewer only receives keys while its canvas has
  // focus — so without this, using the toolbar silently stops typing from working. No-ops when there is
  // no live session (including 'remote-close', which has just ended it).
  if (action && action.indexOf('remote-') === 0 && typeof SHARED !== 'undefined' && SHARED && SHARED.host) {
    sharedFocus(); // the Shared (noVNC) viewer, idea #3397
  } else if (action && action.indexOf('remote-') === 0 && typeof rdpFocusCanvas === 'function') {
    try { rdpFocusCanvas(); } catch { /* */ }
  }
}

// --- Form submissions --------------------------------------------------------

async function handleTicketSubmit(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const submitBtn = form.querySelector('[type="submit"]');

  // Client-side validation before we touch the network.
  clearFieldErrors(form);
  const errors = validateTicketForm(form);
  if (errors.length) {
    errors.forEach(([id, msg]) => setFieldError(id, msg));
    const first = document.getElementById(errors[0][0]);
    if (first) first.focus();
    return;
  }

  const payload = {
    company: form.company.value,
    site: form.site.value,
    user: form.user.value,
    email: form.email.value,
    phone: form.phone.value,
    computerName: form.computerName.value,
    summary: form.summary.value,
    details: form.details.value
  };

  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Submitting…';
  }
  try {
    const result = await submitTicket(payload);
    clearTicketDraft();
    showToast('Ticket #' + (result.number != null ? result.number : result.ticket_id) + ' submitted.', 'success');
    if (submitBtn) submitBtn.innerHTML = '<i class="fa-solid fa-circle-check"></i> Submitted';
    setTimeout(() => navigate('view-welcome'), 1500);
  } catch (error) {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = 'Submit Ticket';
    }
    handlePortalError(error, 'Failed to create ticket.');
  }
}

async function handleReplySubmit(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const ticketId = currentTicketId();
  const textarea = form.querySelector('#reply-message');
  const submitBtn = form.querySelector('[type="submit"]');
  const message = textarea && textarea.value ? textarea.value.trim() : '';

  if (!message) {
    if (textarea) textarea.focus();
    return;
  }
  if (!ticketId) {
    showToast('No ticket selected.', 'danger');
    return;
  }

  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Sending…';
  }
  try {
    await submitTicketReply(ticketId, message);
    if (textarea) textarea.value = '';
    showToast('Reply sent.', 'success');
    // Refetch so the new note appears in the thread (and any status change shows).
    await loadTicketDetails(ticketId);
  } catch (error) {
    handlePortalError(error, 'Failed to send your reply.');
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = '<i class="fa-solid fa-paper-plane"></i> Send Reply';
    }
  }
}

async function handleSuspendSubmit(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const submitBtn = form.querySelector('[type="submit"]');

  // A user must be picked from the directory dropdown (hidden field is set on select).
  if (!form.suspendUser || !form.suspendUser.value) {
    showToast('Please select the user to suspend.', 'warning');
    const s = document.getElementById('su-user-search');
    if (s) s.focus();
    return;
  }

  const payload = collectFormData(form);
  // Which linked accounts to suspend (checkbox class, no form name → gather here).
  payload.accounts = Array.from(document.querySelectorAll('#su-accounts-list .su-account-cb:checked')).map((cb) => cb.value);

  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Submitting…';
  }
  try {
    const result = await submitSuspendRequest(payload);
    showToast('Offboarding request #' + (result.number != null ? result.number : result.ticket_id) + ' submitted.', 'success');
    setTimeout(() => navigate('view-welcome'), 1500);
  } catch (error) {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = 'Submit Suspension Request';
    }
    handlePortalError(error, 'Failed to submit suspension request.');
  }
}

// Collect a form's named fields into a plain object (checkboxes -> 'on'/absent).
function collectFormData(form) {
  const data = {};
  const fd = new FormData(form);
  for (const [key, value] of fd.entries()) {
    data[key] = value;
  }
  return data;
}

// Delegated change handling for the remote-viewer selects (idea #2083). Delegated rather than bound
// directly because the viewer markup is in the static shell but the options are built lazily at
// viewer-open, and a delegated listener survives any re-render of the toolbar.
function handleDelegatedChange(event) {
  const el = event.target;
  if (!el || !el.classList) return;
  if (el.classList.contains('rv-quality-select')) {
    rdpQualityApply(el.value);
    // A <select> keeps focus after a change and swallows every keystroke (arrows/letters re-pick an
    // option), so the remote keyboard would stay dead until the user clicked the screen (idea #2258).
    if (typeof rdpFocusCanvas === 'function') { try { rdpFocusCanvas(); } catch { /* */ } }
  }
}

// NetWatch for the user's own company (idea #3076). A tray session signs in first (password/SSO + 2FA);
// a signed-in one gets a single-use link and the browser follows it into a client-scoped console.
async function openNetwatch() {
  if (!portalAuthenticated()) {
    if (typeof cpStartSigninUpgrade === 'function') cpStartSigninUpgrade('');
    return;
  }
  try {
    const r = await requestNetwatchHandoff();
    if (r && r.url) { location.href = r.url; return; }
    showToast('NetWatch is not available right now.', 'warning');
  } catch (error) {
    showToast((error && error.message) || 'NetWatch is not enabled for your account.', 'warning');
  }
}
;

// ========== init.js ==========
// init.js — application bootstrap. Mints the session, wires delegated events and
// form handlers, and renders the view the URL points at.

// Read agent_id + username from the URL and mint a session. The static shell
// carries no server-injected state, so this establishes window.PORTAL.
async function establishSession() {
  const qs = new URLSearchParams(location.search);
  const agentId = qs.get('agent_id') || '';
  const agentUid = qs.get('agent_uid') || '';
  const username = qs.get('username') || '';
  if (!agentId && !agentUid) throw new Error('missing_agent_id');

  const res = await mintSession(agentId, username, agentUid);
  window.PORTAL = {
    sessionId: (res && res.session_id) || '',
    agentId: agentId,
    agentUid: agentUid,
    username: username,
    // One-shot opener intents captured BEFORE we scrub the URL below.
    openChat: qs.get('open_chat') === '1' || qs.get('open_chat') === 'true',
    ticketId: qs.get('ticketid') || qs.get('ticket_id') || null
  };

  // SECURITY: strip the query string from the address bar so the agent_id /
  // username can't be copied or shared — identity now lives only in the
  // server-side session (referenced by window.PORTAL.sessionId in memory, never
  // the URL). Keep the path + hash; the hash drives in-app navigation.
  try {
    history.replaceState(null, document.title, location.pathname + (location.hash || ''));
  } catch { /* replaceState unavailable — non-fatal */ }
}

async function bootPortal() {
  // Global delegated click handling for all [data-action] triggers.
  document.addEventListener('click', handleDelegatedClick);
  // Delegated change handling for viewer selects (picture quality — idea #2083).
  document.addEventListener('change', handleDelegatedChange);

  // Remote-session full screen (idea #1779): single source of truth for fullscreen enter/exit — swaps
  // the button icon, renegotiates the RDP desktop, and toggles the keyboard lock. Registered once.
  document.addEventListener('fullscreenchange', onRemoteFullscreenChange);

  // Draggable full-screen session pill (idea #2347). In full screen the pill is the only chrome and it
  // sits over the remote window's title bar / ribbon, so it has to be movable. Binds the drag handle
  // plus its own re-clamp on fullscreenchange/resize, and restores the remembered position.
  rdpBannerInit();

  // Remote keyboard safety net (idea #2258). The RDP viewer captures keys on its canvas, so anything that
  // moves focus silently detaches the keyboard while the mouse keeps working — which is exactly how a
  // client hit "unable to type, only able to move/click with mouse". These three make focus loss
  // survivable: release whatever is held when we lose the window (or Windows keeps that modifier down
  // forever), take the keyboard back when we return, and carry keystrokes at the document level while
  // focus is anywhere outside a form control. Registered once; each no-ops without a live RDP session.
  window.addEventListener('blur', rdpOnWindowBlur);
  window.addEventListener('focus', rdpOnWindowFocus);
  document.addEventListener('visibilitychange', rdpOnVisibilityChange);
  document.addEventListener('keydown', rdpOnDocKeyDown);
  document.addEventListener('keyup', rdpOnDocKeyUp);

  // Form handlers (bound directly; forms are present in the static shell).
  const ticketForm = document.getElementById('ticket-form-el');
  if (ticketForm) {
    ticketForm.addEventListener('submit', handleTicketSubmit);
    ticketForm.addEventListener('input', debouncePortal(saveTicketFormDraft, 500));
    // Clear a field's validation error as soon as the user starts fixing it.
    ticketForm.addEventListener('input', function (e) {
      const t = e.target;
      if (t && t.classList && t.classList.contains('has-error')) {
        t.classList.remove('has-error');
        t.removeAttribute('aria-invalid');
        const field = t.closest('.portal-field');
        const err = field && field.querySelector('.field-error');
        if (err) err.remove();
      }
    });
  }
  const suspendForm = document.getElementById('suspend-form-el');
  if (suspendForm) suspendForm.addEventListener('submit', handleSuspendSubmit);

  // Suspend user picker: typing searches the directory; a fresh keystroke clears
  // any prior selection until a result is picked. Results hide on outside click.
  const suSearch = document.getElementById('su-user-search');
  if (suSearch) {
    suSearch.addEventListener('input', debouncePortal(function () {
      const hidden = document.getElementById('su-user');
      if (hidden) hidden.value = '';
      renderSuspendAccounts(null);
      renderSuspendResults(suSearch.value);
    }, 150));
    suSearch.addEventListener('focus', function () { renderSuspendResults(suSearch.value); });
  }
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.su-combo')) hideSuspendResults();
  });

  // Ticket reply composer + list search (both present in the static shell).
  const replyForm = document.getElementById('reply-form-el');
  if (replyForm) replyForm.addEventListener('submit', handleReplySubmit);
  const ticketSearch = document.getElementById('tickets-search');
  if (ticketSearch) ticketSearch.addEventListener('input', debouncePortal(applyTicketFilter, 200));
  const usersSearch = document.getElementById('users-search');
  if (usersSearch) usersSearch.addEventListener('input', debouncePortal(applyUsersFilter, 200));

  // Billing OTP gate forms (present in the static shell).
  const otpRequestForm = document.getElementById('otp-request-form-el');
  if (otpRequestForm) otpRequestForm.addEventListener('submit', handleOtpRequest);
  const otpVerifyForm = document.getElementById('otp-verify-form-el');
  if (otpVerifyForm) otpVerifyForm.addEventListener('submit', handleOtpVerify);

  // Bind hash routing (single source of truth for navigation).
  initRouter();
  // Wire the authenticated-login overlay (idea #1078) — harmless if the overlay
  // markup is absent.
  if (typeof cpInitAuth === 'function') cpInitAuth();

  // Three entry modes:
  //  1. ?set_password=<token>  → show the set-password overlay (emailed link).
  //  2. ?agent_id=<id>         → frictionless device session (native tray) — the
  //     original flow: mint from the URL and boot straight into the app.
  //  3. neither                → the authenticated remote-access flow (idea #1078):
  //     show the login overlay; the app boots only after password + 2FA clear.
  const qs = new URLSearchParams(location.search);
  const setPwToken = qs.get('set_password');
  const agentId = qs.get('agent_id') || qs.get('agent_uid'); // NetWatch uid boots the device flow too (idea #1184)
  const ssoSession = qs.get('portal_session');
  const ssoError = qs.get('sso_error');

  // Returning from an SSO round-trip (idea #1078): adopt the session, then boot or
  // fall through to the email-OTP step.
  if (ssoSession) {
    if (typeof cpHandleSsoReturn === 'function') cpHandleSsoReturn(ssoSession);
    return;
  }
  if (ssoError) {
    if (typeof cpShowSsoError === 'function') cpShowSsoError(ssoError);
    else if (typeof cpStartAuthFlow === 'function') cpStartAuthFlow();
    return;
  }

  if (setPwToken) {
    // Pre-flight the emailed link (idea #1563): if it's expired/invalid, show a
    // "request a new link" screen instead of a dead-end set-password form.
    if (typeof cpBeginSetPassword === 'function') await cpBeginSetPassword(setPwToken);
    else if (typeof cpShowSetPassword === 'function') cpShowSetPassword(setPwToken);
    return;
  }

  if (agentId) {
    // Mint the session FIRST — startPortalApp()'s calls need window.PORTAL.sessionId.
    try {
      await establishSession();
    } catch (error) {
      showToast(error && error.message === 'missing_agent_id'
        ? 'This link is missing its device id. Please reopen the portal from your computer.'
        : 'Could not start a secure session. Please reopen the portal.', 'danger');
      return;
    }
    startPortalApp();
    return;
  }

  // Authenticated flow: restore a persisted session if we have one (so a refresh doesn't
  // force a re-login), otherwise show the login overlay.
  if (typeof cpTryRestore === 'function' && await cpTryRestore()) return;
  if (typeof cpStartAuthFlow === 'function') {
    cpStartAuthFlow();
  } else {
    showToast('Sign-in is unavailable right now. Please try again shortly.', 'danger');
  }
}

// Boot the main app once a session exists (either the agent_id mint or a completed
// authenticated login). Renders the routed view, loads context, and fires one-shot
// opener intents captured (then scrubbed) from the URL.
function startPortalApp() {
  applyRoute();
  loadPortalContext();
  if (window.PORTAL && !location.hash) {
    if (window.PORTAL.ticketId) navigate('view-ticket-details', { id: window.PORTAL.ticketId });
    if (window.PORTAL.openChat) startChatFlow(window.PORTAL.ticketId || null);
  }
}

function saveTicketFormDraft() {
  const form = document.getElementById('ticket-form-el');
  if (!form) return;
  saveTicketDraft({
    company: form.company.value,
    site: form.site.value,
    user: form.user.value,
    email: form.email.value,
    phone: form.phone.value,
    computerName: form.computerName.value,
    summary: form.summary.value,
    details: form.details.value
  });
}

function debouncePortal(fn, wait) {
  let timer;
  return function () {
    clearTimeout(timer);
    timer = setTimeout(fn, wait);
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootPortal);
} else {
  bootPortal();
}
;

// ========== login.js ==========
// login.js — authenticated-login front-end (idea #1078).
//
// Drives the #cp-auth-overlay: password sign-in, email-OTP 2FA (once per device),
// forgot/set-password. Self-contained — it binds its own form + scoped delegated
// click handlers (all its actions are data-action="cp-*"), toggles the overlay vs
// the main .portal-app, and on success hands off to startPortalApp() (init.js).
//
// Vanilla JS, function declarations (bundled with the rest of src/js). No inline
// handlers; errors are shown inline, never via alert.

function cpOverlay() { return document.getElementById('cp-auth-overlay'); }

// Show the overlay and exactly one step form; hide the main app behind it.
function cpShowStep(stepId) {
  var ov = cpOverlay();
  if (!ov) return;
  ov.classList.remove('is-hidden');
  ov.setAttribute('aria-hidden', 'false');
  document.body.classList.add('cp-auth-active');
  var steps = ov.querySelectorAll('.cp-auth-step');
  for (var i = 0; i < steps.length; i++) {
    steps[i].classList.toggle('is-hidden', steps[i].id !== stepId);
  }
  cpHideAllErrors();
}

function cpHideOverlay() {
  var ov = cpOverlay();
  if (ov) { ov.classList.add('is-hidden'); ov.setAttribute('aria-hidden', 'true'); }
  document.body.classList.remove('cp-auth-active');
}

function cpSetError(id, msg) {
  var el = document.getElementById(id);
  if (!el) return;
  el.textContent = msg || 'Something went wrong.';
  el.classList.remove('is-hidden');
}
function cpHideAllErrors() {
  var els = document.querySelectorAll('#cp-auth-overlay .cp-auth-error, #cp-auth-overlay .cp-auth-note');
  for (var i = 0; i < els.length; i++) { els[i].classList.add('is-hidden'); els[i].textContent = ''; }
}
function cpBusy(btn, busy, label) {
  if (!btn) return;
  btn.disabled = !!busy;
  if (busy) { btn.dataset.cpLabel = btn.textContent; btn.textContent = label || 'Please wait…'; }
  else if (btn.dataset.cpLabel) { btn.textContent = btn.dataset.cpLabel; }
}

// --- Entry points (called by init.js) --------------------------------------
function cpStartAuthFlow() {
  if (typeof window.PORTAL === 'undefined' || !window.PORTAL) window.PORTAL = { sessionId: '' };
  cpShowStep('cp-login-form');
  var email = document.getElementById('cp-login-email');
  if (email) email.focus();
  // Reveal the SSO buttons only for providers the server actually has configured.
  cpRevealSso();
}

async function cpRevealSso() {
  try {
    var r = await fetchAuthConfig();
    if (!r.ok || !r.data || !r.data.sso_enabled) return;
    var box = document.getElementById('cp-sso-buttons');
    if (box) box.classList.remove('is-hidden');
    var providers = r.data.providers || [];
    var btns = document.querySelectorAll('#cp-sso-buttons [data-action="cp-sso"]');
    for (var i = 0; i < btns.length; i++) {
      var p = btns[i].getAttribute('data-provider');
      btns[i].classList.toggle('is-hidden', providers.indexOf(p) === -1);
    }
  } catch (e) { /* SSO stays hidden — password login still works */ }
}

// Land back from an SSO round-trip: adopt the minted session, scrub the URL, then
// either boot (trusted device → MFA already satisfied) or run the email OTP step.
async function cpHandleSsoReturn(sessionId) {
  window.PORTAL = window.PORTAL || {};
  window.PORTAL.sessionId = sessionId;
  try { history.replaceState(null, document.title, location.pathname + (location.hash || '')); } catch (e) { /* non-fatal */ }
  var ctx = null;
  try { ctx = await fetchContext(); } catch (e) { ctx = null; }
  if (ctx && ctx.mfa_verified) {
    cpFinishAuth();
    return;
  }
  var sub = document.getElementById('cp-otp-sub');
  if (sub) sub.textContent = 'We sent a code to your email.';
  cpShowStep('cp-otp-form');
  var code = document.getElementById('cp-otp-code');
  if (code) code.focus();
  await request2fa();
}

function cpShowSsoError(codeOrEmpty) {
  cpStartAuthFlow();
  var map = {
    unknown: 'That account isn’t recognised. Please contact support.',
    email: 'We couldn’t read a verified email from that account.',
    unverified: 'We couldn’t read a verified email from that account.',
    tenant: 'That account can’t be used to sign in here. Please use your work account, or sign in with your email and password.',
    company: 'That account can’t be used to sign in here. Please use your work account, or sign in with your email and password.',
    provider: 'Your organisation signs in with Microsoft. Please use “Sign in with Microsoft”.',
    cancelled: 'Sign-in was cancelled.',
    state: 'Your sign-in session expired. Please try again.',
    token: 'Sign-in failed. Please try again.'
  };
  cpSetError('cp-login-error', map[codeOrEmpty] || 'Single sign-on failed. Please try again.');
}

function cpShowSetPassword(token) {
  if (typeof window.PORTAL === 'undefined' || !window.PORTAL) window.PORTAL = { sessionId: '' };
  window.PORTAL._setPwToken = token;
  cpShowStep('cp-setpw-form');
}

// Emailed set-password link entry (idea #1563): pre-flight the token so an expired/invalid
// link shows a "request a new link" screen instead of a dead-end form. Falls through to the
// form if the check can't run (network hiccup) — submit will still surface the real error.
async function cpBeginSetPassword(token) {
  try {
    var r = await validateResetToken(token);
    if (r && r.ok && r.data && r.data.valid === false) { cpShowLinkExpired(); return; }
  } catch (e) { /* fall through to the form */ }
  cpShowSetPassword(token);
}

function cpShowLinkExpired() {
  if (typeof window.PORTAL === 'undefined' || !window.PORTAL) window.PORTAL = { sessionId: '' };
  window.PORTAL._setPwToken = '';
  // Scrub the dead token from the URL so a refresh doesn't re-trigger the expired path.
  try { history.replaceState(null, document.title, location.pathname); } catch (err) { /* non-fatal */ }
  cpShowStep('cp-linkexpired');
}

// Auth cleared (password + 2FA, or trusted device) → tear the overlay down and boot.
function cpFinishAuth() {
  cpPersistSession((window.PORTAL && window.PORTAL.sessionId) || '');
  cpHideOverlay();
  // Signed in from a tray session (idea #3076): the new password/SSO session replaces the tray one,
  // and the user lands on the page they asked for.
  var returnPath = (window.PORTAL && window.PORTAL.signinReturnPath) || '';
  var upgrading = cpSigninUpgradeActive();
  cpEndSigninUpgrade();
  if (!upgrading || !returnPath) {
    if (typeof startPortalApp === 'function') startPortalApp();
    return;
  }
  // Load the NEW session's context before routing: startPortalApp() doesn't await it, so routing
  // straight to the return path would still see the tray context and ask to sign in again.
  cpAdoptContextThen(function () {
    if (typeof startPortalApp === 'function') startPortalApp();
    if (typeof navigate === 'function' && typeof HASH_TO_VIEW !== 'undefined' && HASH_TO_VIEW[returnPath]) {
      navigate(HASH_TO_VIEW[returnPath]);
    }
  });
}

async function cpAdoptContextThen(fn) {
  try {
    var ctx = await fetchContext();
    if (typeof setPortalContext === 'function') setPortalContext(ctx);
  } catch (e) { /* startPortalApp's own context load reports it */ }
  fn();
}

// --- Signing in from a tray session (idea #3076) ------------------------------
// The tray opens the portal password-free (tickets + chat). Directory, billing and remote need a
// real sign-in. Rather than a dead end, those entries open this overlay over the running app. The
// tray session is kept aside so "Back to support" (or a failed / abandoned sign-in) returns the user
// to it untouched — cpHandleLogin overwrites window.PORTAL.sessionId with the new session as soon
// as the password is accepted, before the email code step.
function cpStartSigninUpgrade(path) {
  window.PORTAL = window.PORTAL || {};
  if (cpSigninUpgradeActive()) return; // already open
  window.PORTAL.traySessionId = window.PORTAL.sessionId || '';
  window.PORTAL.signinReturnPath = path || '';
  var cancel = document.getElementById('cp-cancel-signin');
  if (cancel) cancel.classList.remove('is-hidden');
  cpShowStep('cp-login-form');
  var email = document.getElementById('cp-login-email');
  if (email) email.focus();
  cpRevealSso();
}

function cpSigninUpgradeActive() {
  return !!(window.PORTAL && typeof window.PORTAL.traySessionId === 'string');
}

function cpEndSigninUpgrade() {
  if (window.PORTAL) { delete window.PORTAL.traySessionId; delete window.PORTAL.signinReturnPath; }
  var cancel = document.getElementById('cp-cancel-signin');
  if (cancel) cancel.classList.add('is-hidden');
}

function cpCancelSigninUpgrade() {
  if (!cpSigninUpgradeActive()) return;
  window.PORTAL.sessionId = window.PORTAL.traySessionId; // back to the tray session
  cpEndSigninUpgrade();
  cpHideOverlay();
}

// Persist the authenticated session id so a refresh restores it (no re-login). Only
// authenticated (password/SSO) sessions are persisted — the agent_id device flow isn't.
function cpPersistSession(sid) { try { if (sid) localStorage.setItem('cp_session', sid); } catch (e) { /* storage off */ } }
function cpClearPersisted() { try { localStorage.removeItem('cp_session'); } catch (e) { /* storage off */ } }
function cpGetPersisted() { try { return localStorage.getItem('cp_session') || ''; } catch (e) { return ''; } }

// On boot (no agent_id/SSO/set-password), try to restore a saved session. Returns true if
// it restored + booted the app, false if the caller should show the login screen.
async function cpTryRestore() {
  const sid = cpGetPersisted();
  if (!sid) return false;
  window.PORTAL = window.PORTAL || {};
  window.PORTAL.sessionId = sid;
  try {
    const ctx = await fetchContext();
    if (ctx && ctx.authenticated && ctx.mfa_verified) {
      if (typeof startPortalApp === 'function') startPortalApp();
      return true;
    }
  } catch (e) { /* expired / invalid — fall through */ }
  cpClearPersisted();
  window.PORTAL.sessionId = '';
  return false;
}

// --- Handlers ---------------------------------------------------------------
async function cpHandleLogin(e) {
  e.preventDefault();
  cpHideAllErrors();
  var email = (document.getElementById('cp-login-email') || {}).value || '';
  var pw = (document.getElementById('cp-login-password') || {}).value || '';
  var btn = document.getElementById('cp-login-submit');
  if (!email || !pw) { cpSetError('cp-login-error', 'Enter your email and password.'); return; }
  cpBusy(btn, true, 'Signing in…');
  var r = await loginRequest(email, pw);
  cpBusy(btn, false);
  if (!r.ok || !r.data || r.data.error) {
    cpSetError('cp-login-error', (r.data && r.data.message) || 'Incorrect email or password.');
    return;
  }
  window.PORTAL = window.PORTAL || {};
  window.PORTAL.sessionId = r.data.session_id || '';
  if (r.data.mfa_required) {
    var sub = document.getElementById('cp-otp-sub');
    if (sub) sub.textContent = 'We sent a code to ' + (r.data.email_masked || 'your email') + '.';
    cpShowStep('cp-otp-form');
    var code = document.getElementById('cp-otp-code');
    if (code) code.focus();
    await request2fa(); // fire the first code now that we have the session
  } else {
    cpFinishAuth();
  }
}

async function cpHandleOtp(e) {
  e.preventDefault();
  cpHideAllErrors();
  var code = (document.getElementById('cp-otp-code') || {}).value || '';
  var remember = !!(document.getElementById('cp-otp-remember') || {}).checked;
  var btn = document.getElementById('cp-otp-submit');
  if (!/^[0-9]{6}$/.test(code.trim())) { cpSetError('cp-otp-error', 'Enter the 6-digit code.'); return; }
  cpBusy(btn, true, 'Verifying…');
  var r = await verify2fa(code.trim(), remember);
  cpBusy(btn, false);
  if (!r.ok || !r.data || r.data.error) {
    cpSetError('cp-otp-error', (r.data && r.data.message) || 'That code is incorrect.');
    return;
  }
  cpFinishAuth();
}

async function cpHandleResendOtp() {
  cpHideAllErrors();
  var r = await request2fa();
  if (!r.ok || (r.data && r.data.error)) {
    cpSetError('cp-otp-error', (r.data && r.data.message) || 'Could not resend the code.');
  } else if (typeof showToast === 'function') {
    showToast('A new code is on its way.', 'info');
  }
}

async function cpHandleForgot(e) {
  e.preventDefault();
  cpHideAllErrors();
  var email = (document.getElementById('cp-forgot-email') || {}).value || '';
  if (!email) return;
  await forgotPasswordRequest(email);
  var note = document.getElementById('cp-forgot-note');
  if (note) {
    note.textContent = 'If that email matches an account, we’ve sent a link to set your password.';
    note.classList.remove('is-hidden');
  }
}

async function cpHandleSetPassword(e) {
  e.preventDefault();
  cpHideAllErrors();
  var pw = (document.getElementById('cp-setpw-password') || {}).value || '';
  var confirm = (document.getElementById('cp-setpw-confirm') || {}).value || '';
  if (pw.length < 10) { cpSetError('cp-setpw-error', 'Use at least 10 characters.'); return; }
  if (pw !== confirm) { cpSetError('cp-setpw-error', 'Those passwords don’t match.'); return; }
  var token = (window.PORTAL && window.PORTAL._setPwToken) || '';
  var r = await setPasswordRequest(token, pw);
  if (!r.ok || !r.data || r.data.error) {
    // An expired/invalid token is a dead end on this form — send them to the
    // "request a new link" screen instead of a bare inline error (idea #1563).
    if (r.data && r.data.error === 'invalid_token') { cpShowLinkExpired(); return; }
    cpSetError('cp-setpw-error', (r.data && r.data.message) || 'This link is invalid or has expired.');
    return;
  }
  // Password set — scrub the token from the URL and drop the user at sign-in.
  try { history.replaceState(null, document.title, location.pathname); } catch (err) { /* non-fatal */ }
  if (typeof showToast === 'function') showToast('Password set. Please sign in.', 'success');
  cpStartAuthFlow();
}

// Scoped delegated clicks for the overlay's data-action="cp-*" links.
function cpHandleOverlayClick(e) {
  var t = e.target.closest('[data-action]');
  if (!t) return;
  var action = t.getAttribute('data-action');
  if (action === 'cp-show-forgot') { e.preventDefault(); cpShowStep('cp-forgot-form'); }
  else if (action === 'cp-back-to-login') { e.preventDefault(); cpShowStep('cp-login-form'); }
  else if (action === 'cp-resend-otp') { e.preventDefault(); cpHandleResendOtp(); }
  else if (action === 'cp-cancel-signin') { e.preventDefault(); cpCancelSigninUpgrade(); }
  else if (action === 'cp-sso') {
    e.preventDefault();
    var provider = t.getAttribute('data-provider');
    // Server-driven OAuth (Stage 3). Full-page redirect so the provider callback can
    // mint the session, then land back here.
    // Signing in from a tray session (idea #3076): come back to the page that asked for it.
    var ret = (window.PORTAL && window.PORTAL.signinReturnPath) || '';
    location.href = portalApiBase() + '/auth/' + encodeURIComponent(provider) + '/start' +
      (/^[a-z][a-z0-9_-]{0,39}$/.test(ret) ? '?return=' + encodeURIComponent(ret) : '');
  }
}

// Bind the overlay's forms + scoped click handler once.
function cpInitAuth() {
  var ov = cpOverlay();
  if (!ov) return;
  ov.addEventListener('click', cpHandleOverlayClick);
  var login = document.getElementById('cp-login-form');
  if (login) login.addEventListener('submit', cpHandleLogin);
  var otp = document.getElementById('cp-otp-form');
  if (otp) otp.addEventListener('submit', cpHandleOtp);
  var forgot = document.getElementById('cp-forgot-form');
  if (forgot) forgot.addEventListener('submit', cpHandleForgot);
  var setpw = document.getElementById('cp-setpw-form');
  if (setpw) setpw.addEventListener('submit', cpHandleSetPassword);
}
;

// ========== rdp.js ==========
// clientPortal/src/js/rdp.js — native-RDP self-service viewer (idea #1356).
//
// The portal's only remote viewer (idea #1883). Owns the in-page overlay chrome declared in
// index.html (#remote-viewer / #rv-host / #rv-screen / #rv-state) and mounts an ironrdp-web canvas
// into it; remote.js next door renders the station list and the shared Close button. Flow: open the overlay → show ONLY a credential prompt (the user's own Windows login,
// since RDP authenticates with NLA) → on submit, mint a single-use /remote/rdp-ticket, open the
// /api/rdp bridge, and run the RDP session full-frame. Nothing is recorded (client self-service).
//
// ironrdp-web is an ES module loaded in index.html and exposed on window.__ironrdp for the
// concatenated bundle; its classes are only usable after window.__ironrdp.ensure() (lazy WASM init).

var RDP = { host: null, session: null, target: null, targetName: null, companyId: null, onResize: null, cred: null, reconnecting: false,
  // Clipboard redirection (CLIPRDR, idea #1866): clipPoll = local→remote poll interval id; clipSig =
  // signature of the last clipboard we pushed (dedupe); clipSuppress = short window after applying a
  // REMOTE clipboard locally, so the poll doesn't immediately echo it back (copy/paste loop guard).
  clipPoll: null, clipSig: null, clipSuppress: false,
  // Disconnect UX (idea #1878): `intentional` = the user hit Close (so an end is a completion, not a
  // drop to auto-reconnect); `everConnected` = a session was actually established (so closing at the
  // sign-in prompt skips the completion card); `reconnectAttempts`/`reconnectTimer` drive the
  // transport-drop auto-reconnect.
  intentional: false, everConnected: false, reconnectAttempts: 0, reconnectTimer: null,
  // Keyboard (idea #2258). `held` = the scancodes we have told the remote are DOWN and not yet released;
  // it exists because a keyup is not guaranteed — Alt-Tab, the Windows key, a fullscreen switch or a
  // click into browser chrome all steal focus mid-chord, so the keyup never reaches us and Windows holds
  // that modifier down forever (after which every keystroke is a dead chord and the user reports "I can't
  // type" while the mouse still works). `kdiag` counts what actually happened to each keystroke so the
  // next report is answerable from the logs instead of re-investigated. Both are (re)armed per session.
  held: null, kdiag: null, kdiagSent: false,
  // Soft-keyboard IME guard (idea #2259). Own explicit flag, NOT the browser's per-event
  // e.isComposing — some browsers fire a trailing 'input' for the composition's final commit with
  // isComposing already false (right before compositionend), which would otherwise slip past the
  // isComposing check and get double-sent alongside compositionend's own send. This flag's lifetime
  // is fully ours: true from compositionstart, false only once compositionend has finished sending —
  // so that trailing event is reliably still "mid-composition" by our definition and gets skipped.
  composing: false };

// Auto-reconnect cap (idea #1878) — after this many consecutive failed transport reconnects we stop
// and show the "connection lost" completion card (with a manual Reconnect button). The backoff is
// 1500ms × attempt.
var MAX_RDP_RECONNECT = 6;

// Touch-capable, computed once (idea #2259) — a soft keyboard is only reachable through the hidden
// relay input, never through canvas focus, so a touch pointer routes taps there instead. Checked at
// module load rather than per-tap: touch capability doesn't change mid-session, and re-querying
// matchMedia on every mousedown would be wasted work on the hot path.
var RDP_TOUCH_CAPABLE = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) ||
  (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);

// The user can silence the post-session "how did it go?" feedback prompt (idea #1878). We still show
// the thank-you + Back button — only the issue/request box is suppressed. Persisted in localStorage so
// it survives a refresh; the list-level "Report a problem" button is unaffected (it's an explicit action).
function rdpFeedbackSuppressed() {
  try { return localStorage.getItem('rdp_no_feedback_prompt') === '1'; } catch { return false; }
}
function rdpSetFeedbackSuppressed(on) {
  try { if (on) localStorage.setItem('rdp_no_feedback_prompt', '1'); else localStorage.removeItem('rdp_no_feedback_prompt'); } catch { /* */ }
}

// Available workspace size for the remote desktop — fills #rv-screen. Width rounded to a multiple of 4 / height to even (RDP rejects odd dimensions);
// clamped to sane bounds so a hidden/zero-size container can't request a degenerate resolution.
// The negotiated resolution is the measured viewport DIVIDED by the zoom factor (idea #1897 follow-up,
// rdpZoom.js): zoom > 1 → fewer pixels stretched over the same canvas (bigger UI), zoom < 1 → more
// pixels (more real estate). zoom = 1 is the original "match the viewport perfectly" behaviour.
// The negotiated remote desktop size, in CSS pixels.
//
// THIS IS DPI-AWARE, AND DELIBERATELY SO — do not "fix" it by multiplying by devicePixelRatio.
// getBoundingClientRect() reports CSS pixels, which is the DPI-*EFFECTIVE* resolution: a 4K panel run
// at 200% Windows/browser scaling shows a 1920x1080 desktop and reports ~1920x1080 here, so that is
// what we ask the remote for — matching what the user actually sees rather than the panel's physical
// pixel count. The same 4K panel at 100% scaling genuinely IS a 3840x2160 desktop, and asking for
// 3840x2160 is then correct.
//
// The trade-off this encodes: on a hi-DPI display the canvas is one CSS pixel per remote pixel, so the
// browser upscales it by devicePixelRatio when painting and the image is slightly soft. Requesting
// device pixels instead would be pixel-sharp but costs dPR^2 (4x at 200%) the bytes, decode and paint —
// the wrong trade on exactly the slow links this viewer has to survive.
function rdpViewportSize() {
  var screen = document.getElementById('rv-screen');
  var r = screen ? screen.getBoundingClientRect() : null;
  var z = (typeof rdpZoomGet === 'function') ? rdpZoomGet() : 1;
  if (!z || !isFinite(z)) z = 1;
  var w = Math.floor(((r && r.width) || window.innerWidth || 1280) / z);
  var h = Math.floor(((r && r.height) || window.innerHeight || 720) / z);
  w = Math.max(640, Math.min(3840, w - (w % 4)));
  h = Math.max(480, Math.min(2160, h - (h % 2)));
  // Quality profile cap (idea #2083) — applied AFTER zoom so the two compose: zoom picks how big things
  // look, quality picks the ceiling on how many pixels we're willing to push. Re-align to the 4/2 grid
  // afterwards because the aspect-preserving scale can land anywhere.
  if (typeof rdpQualityClamp === 'function') {
    var q = rdpQualityClamp(w, h);
    w = Math.max(640, q.w - (q.w % 4));
    h = Math.max(480, q.h - (q.h % 2));
  }
  return { w: w, h: h };
}

// Trailing-edge debounce (window 'resize' fires in bursts; renegotiating the remote resolution per
// event would thrash the Display Control channel).
function rdpDebounce(fn, ms) {
  var t = null;
  return function () { if (t) clearTimeout(t); t = setTimeout(function () { t = null; fn(); }, ms); };
}

// PS/2 Set 1 scancodes (browser KeyboardEvent.code → scancode; extended keys carry the 0xE0 prefix).
var RDP_SCANCODE = {
  Escape: 0x01, Digit1: 0x02, Digit2: 0x03, Digit3: 0x04, Digit4: 0x05, Digit5: 0x06, Digit6: 0x07,
  Digit7: 0x08, Digit8: 0x09, Digit9: 0x0A, Digit0: 0x0B, Minus: 0x0C, Equal: 0x0D, Backspace: 0x0E,
  Tab: 0x0F, KeyQ: 0x10, KeyW: 0x11, KeyE: 0x12, KeyR: 0x13, KeyT: 0x14, KeyY: 0x15, KeyU: 0x16,
  KeyI: 0x17, KeyO: 0x18, KeyP: 0x19, BracketLeft: 0x1A, BracketRight: 0x1B, Enter: 0x1C,
  ControlLeft: 0x1D, KeyA: 0x1E, KeyS: 0x1F, KeyD: 0x20, KeyF: 0x21, KeyG: 0x22, KeyH: 0x23,
  KeyJ: 0x24, KeyK: 0x25, KeyL: 0x26, Semicolon: 0x27, Quote: 0x28, Backquote: 0x29, ShiftLeft: 0x2A,
  Backslash: 0x2B, KeyZ: 0x2C, KeyX: 0x2D, KeyC: 0x2E, KeyV: 0x2F, KeyB: 0x30, KeyN: 0x31, KeyM: 0x32,
  Comma: 0x33, Period: 0x34, Slash: 0x35, ShiftRight: 0x36, NumpadMultiply: 0x37, AltLeft: 0x38,
  Space: 0x39, CapsLock: 0x3A, F1: 0x3B, F2: 0x3C, F3: 0x3D, F4: 0x3E, F5: 0x3F, F6: 0x40, F7: 0x41,
  F8: 0x42, F9: 0x43, F10: 0x44, NumLock: 0x45, ScrollLock: 0x46, Numpad7: 0x47, Numpad8: 0x48,
  Numpad9: 0x49, NumpadSubtract: 0x4A, Numpad4: 0x4B, Numpad5: 0x4C, Numpad6: 0x4D, NumpadAdd: 0x4E,
  Numpad1: 0x4F, Numpad2: 0x50, Numpad3: 0x51, Numpad0: 0x52, NumpadDecimal: 0x53, F11: 0x57, F12: 0x58,
  NumpadEnter: 0xE01C, ControlRight: 0xE01D, NumpadDivide: 0xE035, PrintScreen: 0xE037, AltRight: 0xE038,
  Home: 0xE047, ArrowUp: 0xE048, PageUp: 0xE049, ArrowLeft: 0xE04B, ArrowRight: 0xE04D, End: 0xE04F,
  ArrowDown: 0xE050, PageDown: 0xE051, Insert: 0xE052, Delete: 0xE053, MetaLeft: 0xE05B,
  MetaRight: 0xE05C, ContextMenu: 0xE05D, Pause: 0xE11D45,
  // Non-US / OEM physical keys (idea #2258). Absent from the original table, so on an ISO or JIS keyboard
  // these silently produced nothing at all — the handler simply returned on an unmapped e.code.
  IntlBackslash: 0x56, IntlRo: 0x73, IntlYen: 0x7D, NumpadEqual: 0x59,
};

// Keys whose browser auto-repeat must NOT reach the remote (idea #3172). A held key fires repeated
// keydowns (e.repeat), and ironrdp-input's Database turns a KeyPressed for an already-down key into
// RELEASE+PRESS — so a held Shift arrived as a burst of separate Shift TAPS, five of which is the
// Windows Sticky Keys hotkey (the prompt came back however often the user dismissed or "disabled" it,
// because we kept sending the taps). Same mechanism made a held Alt flicker the menu bar, a held Win
// toggle Start, and a held lock key toggle on/off. A physical keyboard sends these as ONE press and ONE
// release, so that's what we send; ordinary keys keep their repeat (holding a letter/Backspace repeats).
var RDP_NO_REPEAT = (function () {
  var names = ['ShiftLeft', 'ShiftRight', 'ControlLeft', 'ControlRight', 'AltLeft', 'AltRight',
    'MetaLeft', 'MetaRight', 'CapsLock', 'NumLock', 'ScrollLock'];
  var set = {};
  for (var i = 0; i < names.length; i++) set[RDP_SCANCODE[names[i]]] = true;
  return set;
})();

// Legacy keyCode → e.code fallback (idea #2258). e.code is empty or 'Unidentified' on some IMEs, some
// remote-desktop/kiosk browsers and most soft keyboards. Before, an empty code meant the keystroke was
// dropped without a trace; now we recover the common keys from the legacy keyCode. Deliberately partial:
// letters, digits, and the keys someone typing into a Windows session actually needs.
var RDP_KEYCODE_FALLBACK = {
  8: 'Backspace', 9: 'Tab', 13: 'Enter', 16: 'ShiftLeft', 17: 'ControlLeft', 18: 'AltLeft', 20: 'CapsLock',
  27: 'Escape', 32: 'Space', 33: 'PageUp', 34: 'PageDown', 35: 'End', 36: 'Home', 37: 'ArrowLeft',
  38: 'ArrowUp', 39: 'ArrowRight', 40: 'ArrowDown', 45: 'Insert', 46: 'Delete', 91: 'MetaLeft',
  186: 'Semicolon', 187: 'Equal', 188: 'Comma', 189: 'Minus', 190: 'Period', 191: 'Slash', 192: 'Backquote',
  219: 'BracketLeft', 220: 'Backslash', 221: 'BracketRight', 222: 'Quote',
};

// Resolve a KeyboardEvent to a PS/2 scancode. Returns null when we genuinely cannot map it — the caller
// records that (kdiag.unmapped + the offending code) rather than dropping it silently.
function rdpScancodeFor(e) {
  var code = e && e.code;
  if (code && RDP_SCANCODE[code] != null) return RDP_SCANCODE[code];
  var kc = e && (e.keyCode || e.which);
  if (kc) {
    var alt = RDP_KEYCODE_FALLBACK[kc];
    if (!alt && kc >= 65 && kc <= 90) alt = 'Key' + String.fromCharCode(kc);          // A–Z
    else if (!alt && kc >= 48 && kc <= 57) alt = 'Digit' + String.fromCharCode(kc);   // 0–9
    else if (!alt && kc >= 96 && kc <= 105) alt = 'Numpad' + (kc - 96);               // numpad 0–9
    else if (!alt && kc >= 112 && kc <= 123) alt = 'F' + (kc - 111);                  // F1–F12
    if (alt && RDP_SCANCODE[alt] != null) return RDP_SCANCODE[alt];
  }
  return null;
}

// ── Keyboard plumbing (idea #2258) ────────────────────────────────────────────────────────────────
// The viewer's keys are captured on the <canvas>, which means they only arrive when the canvas holds DOM
// focus — whereas the mouse handlers on that same canvas need no focus at all. That asymmetry is the
// whole bug class here: any ordinary interaction that moves focus (a toolbar button, the quality select,
// another tab) detaches the keyboard with no visible cue, and the user sees a working mouse and a dead
// keyboard. So: track what is held, release it whenever focus is lost, take focus back whenever we can,
// fall back to a document-level listener when we can't, and TELL the user when the keyboard is detached.

function rdpCanvasEl() { return document.getElementById('rdp-canvas'); }
function rdpSoftKbdEl() { return document.getElementById('rdp-soft-kbd'); }

// The relay input is never allowed to sit truly empty (idea #2259): a zero-width-space anchor plus
// the caret parked right after it means Backspace always has something local to delete, so the
// browser reliably fires a deleteContentBackward event even on the very first press. Without this,
// some mobile browsers simply don't fire ANY event for Backspace against an empty field (nothing to
// delete → nothing to report), which would make backspace silently stop working after the first char.
var RDP_SOFT_KBD_ANCHOR = '\u200B'; // zero-width space, written as an escape (not a pasted invisible char)
function rdpSoftKbdReset(el) {
  if (!el) return;
  el.value = RDP_SOFT_KBD_ANCHOR;
  try { el.setSelectionRange(1, 1); } catch { /* not all input types support selection */ }
}

function rdpKeyDiagReset() {
  RDP.held = {};
  RDP.kdiagSent = false;
  RDP.kdiag = { seen: 0, sent: 0, unmapped: 0, errors: 0, viaDoc: 0, refocus: 0, released: 0, codes: [], lastError: '',
    // Soft-keyboard relay counters (idea #2259) — same "count, don't swallow" philosophy as the rest
    // of kdiag. unicodeSent = characters relayed via DeviceEvent.unicode*; compositions = IME commits
    // (compositionend); softKbdSkipped = printable keydowns deliberately NOT scancode-sent on the
    // relay input (left for the input-event/unicode path — see rdpSoftKbdIsPrintable).
    unicodeSent: 0, compositions: 0, softKbdSkipped: 0,
    // Auto-repeat keydowns of modifier/lock keys dropped (idea #3172). Healthy, so not "notable".
    repeatDropped: 0 };
}

// Inject one key transition and keep `held` honest. Returns true when the remote was actually told.
// Injection errors are COUNTED (they used to sit in an empty catch, which is why the original report
// could not be diagnosed after the fact).
function rdpSendScan(sc, down) {
  if (sc == null || !RDP.session) return false;
  var D = window.__ironrdp && window.__ironrdp.DeviceEvent;
  if (!D) return false;
  try {
    rdpApplyOne(down ? D.keyPressed(sc) : D.keyReleased(sc));
    if (down) RDP.held[sc] = true; else delete RDP.held[sc];
    if (RDP.kdiag) RDP.kdiag.sent += 1;
    return true;
  } catch (e) {
    if (RDP.kdiag) {
      RDP.kdiag.errors += 1;
      RDP.kdiag.lastError = String((e && e.message) || e || 'inject failed').slice(0, 120);
    }
    return false;
  }
}

// Press+release a scancode as one atomic tap (idea #2259) — for the relay's Backspace/Delete/Enter,
// which arrive as a soft-keyboard input event with no natural "hold" semantics of their own.
function rdpSendScanPair(sc) { rdpSendScan(sc, true); rdpSendScan(sc, false); }

// Inject one Unicode character via ironrdp's TS_UNICODE_KEYBOARD_EVENT path (idea #2259). A soft
// keyboard hands us CHARACTERS (input/beforeinput/compositionend), not physical keys with a usable
// e.code — so the relay maps characters straight through instead of reverse-engineering a scancode
// (which is locale-dependent and undefined for most of Unicode anyway). Mirrors rdpSendScan: errors
// are counted, never swallowed.
function rdpSendUnicode(ch) {
  if (!ch || !RDP.session) return false;
  var D = window.__ironrdp && window.__ironrdp.DeviceEvent;
  if (!D) return false;
  try {
    rdpApplyOne(D.unicodePressed(ch));
    rdpApplyOne(D.unicodeReleased(ch));
    if (RDP.kdiag) RDP.kdiag.unicodeSent += 1;
    return true;
  } catch (e) {
    if (RDP.kdiag) {
      RDP.kdiag.errors += 1;
      RDP.kdiag.lastError = String((e && e.message) || e || 'unicode inject failed').slice(0, 120);
    }
    return false;
  }
}

// Send a whole committed string one character at a time. Iterates by CODE POINT (Array.from), not
// UTF-16 code unit — a naive str[i]/charCodeAt loop would split a surrogate pair (emoji, astral
// characters) into two invalid halves.
function rdpSendUnicodeString(str) {
  if (!str) return;
  var chars = Array.from(str);
  for (var i = 0; i < chars.length; i++) rdpSendUnicode(chars[i]);
}

// Release every key we have told the remote is down. Called on any focus loss, before a reconnect, and
// from the toolbar. Without this a modifier stranded by Alt-Tab stays down on the Windows side for the
// rest of the session and silently turns every later keystroke into a chord.
function rdpReleaseHeldKeys() {
  if (!RDP.held) { RDP.held = {}; return 0; }
  var codes = Object.keys(RDP.held), n = 0;
  for (var i = 0; i < codes.length; i++) { if (rdpSendScan(Number(codes[i]), false)) n += 1; }
  RDP.held = {};
  if (n && RDP.kdiag) RDP.kdiag.released += n;
  return n;
}

// Give the remote desktop the keyboard back. Safe to call at any time; no-ops without a live canvas.
function rdpFocusCanvas() {
  var c = rdpCanvasEl();
  if (!c || !RDP.session) return;
  if (document.activeElement === c) { rdpUpdateKbdHint(); return; }
  try { c.focus({ preventScroll: true }); } catch { try { c.focus(); } catch { /* */ } }
  if (RDP.kdiag && document.activeElement === c) RDP.kdiag.refocus += 1;
  rdpUpdateKbdHint();
}

// Whether SOME keyboard-capable element currently holds focus (idea #2259): the canvas (physical
// keyboard path) OR the soft-keyboard relay input (touch path). Both are equally valid "attached".
function rdpKeyboardAttached() {
  var a = document.activeElement;
  return !!RDP.session && (a === rdpCanvasEl() || a === rdpSoftKbdEl());
}

// The visible cue. A detached keyboard used to be completely invisible — the session looked healthy and
// keystrokes went nowhere. Now the stage shows a chip telling the user exactly how to get it back.
function rdpUpdateKbdHint() {
  var hint = document.getElementById('rdp-kbd-hint');
  if (!hint) return;
  var attached = rdpKeyboardAttached();
  hint.classList.toggle('is-hidden', attached || !RDP.session);
}

// Explicit "Show keyboard" toolbar action (idea #2259) — a soft keyboard needs a real tap to appear,
// so this is the guaranteed affordance (canvas-tap-on-touch, wired in rdpWireInput, is the implicit
// second path). Focusing a <canvas> never raises a mobile on-screen keyboard, however it's focused —
// only a real <input>/<textarea>/contenteditable does, which is the whole reason this input exists.
function rdpShowKeyboard() {
  var el = rdpSoftKbdEl();
  if (!el || !RDP.session) return;
  rdpSoftKbdReset(el);
  try { el.focus({ preventScroll: true }); } catch { try { el.focus(); } catch { /* */ } }
  rdpUpdateKbdHint();
}

// One keydown/keyup path, shared by the canvas listeners and the document-level fallback. `viaDoc` marks
// the fallback so we can see in the logs how often focus had wandered off the canvas.
function rdpHandleKey(e, down, viaDoc) {
  if (!RDP.session) return;
  if (viaDoc) {
    // Never steal keys from a real form control (the sign-in prompt, the feedback box, the quality select).
    var t = e.target, tag = (t && t.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
    if (t === rdpCanvasEl()) return; // the canvas listener already handled it — don't double-send
    if (RDP.kdiag && down) RDP.kdiag.viaDoc += 1;
  }
  if (RDP.kdiag && down) RDP.kdiag.seen += 1;
  var sc = rdpScancodeFor(e);
  if (sc == null) {
    if (RDP.kdiag && down) {
      RDP.kdiag.unmapped += 1;
      var label = String((e && e.code) || ('kc' + ((e && e.keyCode) || 0)));
      if (RDP.kdiag.codes.length < 12 && RDP.kdiag.codes.indexOf(label) === -1) RDP.kdiag.codes.push(label);
    }
    return; // unmapped: let the browser have it rather than swallowing it
  }
  e.preventDefault(); e.stopPropagation();
  // Auto-repeat of a modifier/lock key (idea #3172) — the remote already has it down; re-sending would
  // become a release+press tap (see RDP_NO_REPEAT). `held` also catches browsers/IMEs that leave
  // e.repeat false on repeats.
  if (down && RDP_NO_REPEAT[sc] && (e.repeat || (RDP.held && RDP.held[sc]))) {
    if (RDP.kdiag) RDP.kdiag.repeatDropped += 1;
    return;
  }
  rdpSendScan(sc, down);
}

// ── Soft-keyboard relay (idea #2259) ──────────────────────────────────────────────────────────────
// Mobile Safari/Chrome only raise the on-screen keyboard for a focused <input>/<textarea>/
// contenteditable — never for a <canvas>, however it is focused. #rdp-soft-kbd is that focusable
// element: a hidden <input> created alongside the canvas (see doConnectRdp), reachable via the
// "Show keyboard" toolbar button and (on a touch pointer) a tap on the remote screen itself.
//
// A soft keyboard reports CHARACTERS via input/beforeinput/compositionend, not physical keys with a
// usable e.code — so typed characters are relayed via rdpSendUnicode, not the scancode table.
// Control/navigation/modified keys (Backspace, Enter, arrows, Ctrl+chord) DO still arrive as real
// keydown/keyup on some soft keyboards (Android more often than iOS) and on any Bluetooth keyboard a
// user has paired — those are routed through the EXISTING scancode path (rdpHandleKey) exactly like
// the canvas, so nothing about physical-keyboard handling changes.
//
// Dedupe (the correctness-critical part): a physical keyboard focused into this same input fires
// BOTH a real keydown AND a trailing input event for a plain character — handling both would type it
// twice. Rule: a keydown carrying a modifier or a non-printable e.key goes through the scancode path
// and preventDefault()s (which also suppresses the browser's own trailing input event — no extra
// bookkeeping needed); a bare printable keydown is deliberately left alone here and picked up by the
// input-event/unicode path instead.

// True for a plain, unmodified, single-character keydown — the ones we leave for the input-event
// path so a physical keyboard doesn't get double-sent (once via scancode, once via unicode).
function rdpSoftKbdIsPrintable(e) {
  return !!e && !e.ctrlKey && !e.altKey && !e.metaKey && typeof e.key === 'string' && e.key.length === 1;
}
function rdpSoftKbdKeyDown(e) {
  if (e.isComposing || e.keyCode === 229) return; // mid-IME-composition — compositionend handles it
  if (rdpSoftKbdIsPrintable(e)) { if (RDP.kdiag) RDP.kdiag.softKbdSkipped += 1; return; }
  rdpHandleKey(e, true, false);
}
function rdpSoftKbdKeyUp(e) {
  if (e.isComposing || e.keyCode === 229) return;
  if (rdpSoftKbdIsPrintable(e)) return;
  rdpHandleKey(e, false, false);
}

// The main relay path: dispatches on InputEvent.inputType, which is what a soft keyboard actually
// gives us (real keydowns for printable characters are rare-to-absent on iOS). The field is reset to
// the single-anchor state after every event (rdpSoftKbdReset) so it never accumulates real text —
// each event is always a clean "insert/delete from the anchor" diff.
function rdpSoftKbdInput(e) {
  if (!RDP.session) return;
  var t = e.inputType || '';
  // RDP.composing (not e.isComposing — see its declaration) covers the whole composition window,
  // including a trailing final-commit 'input' event that some browsers fire just before
  // compositionend with isComposing already false. compositionend is the sole source of truth.
  if (t === 'insertCompositionText' || e.isComposing || RDP.composing) return;
  var el = e.target;
  if (t === 'deleteContentBackward') { rdpSendScanPair(RDP_SCANCODE.Backspace); rdpSoftKbdReset(el); return; }
  if (t === 'deleteContentForward') { rdpSendScanPair(RDP_SCANCODE.Delete); rdpSoftKbdReset(el); return; }
  if (t === 'insertLineBreak') { rdpSendScanPair(RDP_SCANCODE.Enter); rdpSoftKbdReset(el); return; }
  if (t === 'insertText' && e.data) { rdpSendUnicodeString(e.data); rdpSoftKbdReset(el); return; }
  // Unrecognized inputType (predictive-text suggestion tap, autofill, etc.) — best-effort: relay
  // whatever landed in the field beyond the anchor, rather than silently dropping it.
  var val = el && el.value;
  if (val && val !== RDP_SOFT_KBD_ANCHOR) {
    var extra = val.indexOf(RDP_SOFT_KBD_ANCHOR) === 0 ? val.slice(RDP_SOFT_KBD_ANCHOR.length) : val;
    if (extra) rdpSendUnicodeString(extra);
  }
  rdpSoftKbdReset(el);
}

// IME composition (CJK soft keyboards, idea #2259): compositionstart/compositionupdate must inject
// NOTHING — the candidate text isn't committed yet, and every intermediate candidate would otherwise
// land in the remote session as if it were final. Only compositionend's e.data (what the user
// actually committed) is sent.
function rdpSoftKbdCompositionEnd(e) {
  if (RDP.session) {
    if (e.data) { rdpSendUnicodeString(e.data); if (RDP.kdiag) RDP.kdiag.compositions += 1; }
    rdpSoftKbdReset(e.target);
  }
  RDP.composing = false; // only NOW does a later 'input' event stop being treated as mid-composition
}

// Wire the relay input once, at session start (doConnectRdp). Focus/blur mirror the canvas's own
// handling (idea #2258): losing focus must release anything held and update the visible cue.
function rdpWireSoftKeyboard(kbd) {
  if (!kbd) return;
  RDP.composing = false;
  rdpSoftKbdReset(kbd);
  kbd.addEventListener('focus', function () { rdpSoftKbdReset(kbd); rdpUpdateKbdHint(); });
  kbd.addEventListener('blur', function () { rdpReleaseHeldKeys(); rdpUpdateKbdHint(); });
  kbd.addEventListener('keydown', rdpSoftKbdKeyDown);
  kbd.addEventListener('keyup', rdpSoftKbdKeyUp);
  kbd.addEventListener('input', rdpSoftKbdInput);
  kbd.addEventListener('compositionstart', function () { RDP.composing = true; });
  kbd.addEventListener('compositionend', rdpSoftKbdCompositionEnd);
}

// Ctrl+Alt+Del for the RDP path (idea #2258) — the toolbar button used to call the old RFB viewer's
// sendCtrlAltDel, which had been a no-op ever since #1883 made RDP the only client method.
// The button was ALSO hidden by a remote.css selector inverted since #1356, so that fix stayed
// unreachable in every real session — tracked and corrected separately as idea #2550.
function rdpSendCad() {
  if (!RDP.session) return false;
  var seq = [RDP_SCANCODE.ControlLeft, RDP_SCANCODE.AltLeft, RDP_SCANCODE.Delete];
  for (var i = 0; i < seq.length; i++) rdpSendScan(seq[i], true);
  for (var j = seq.length - 1; j >= 0; j--) rdpSendScan(seq[j], false);
  rdpFocusCanvas();
  return true;
}

// Toolbar "Release stuck keys" — the user-facing escape hatch for a modifier stranded by Alt-Tab.
function rdpReleaseStuckKeys() {
  if (!RDP.session) return;
  // Release what we know is held, then explicitly clear the four modifiers: the strand can predate this
  // code (or this session's canvas), in which case `held` doesn't know about it but Windows still does.
  rdpReleaseHeldKeys();
  var mods = [RDP_SCANCODE.ControlLeft, RDP_SCANCODE.ControlRight, RDP_SCANCODE.AltLeft, RDP_SCANCODE.AltRight,
    RDP_SCANCODE.ShiftLeft, RDP_SCANCODE.ShiftRight, RDP_SCANCODE.MetaLeft, RDP_SCANCODE.MetaRight];
  for (var i = 0; i < mods.length; i++) rdpSendScan(mods[i], false);
  rdpFocusCanvas();
  try { showToast('Keyboard reset — try typing again.', 'success'); } catch { /* */ }
}

// Focus-loss hooks, registered once in bootPortal(). Any of these means keystrokes stop reaching us, so
// anything still held must be released or it stays down on the Windows side.
function rdpOnWindowBlur() { if (RDP.session) rdpReleaseHeldKeys(); rdpUpdateKbdHint(); }
function rdpOnWindowFocus() {
  if (!RDP.session) return;
  // Coming back to the tab: if focus landed on the body (the usual case) take it back for the remote.
  var a = document.activeElement;
  if (!a || a === document.body || a === document.documentElement) rdpFocusCanvas();
  else rdpUpdateKbdHint();
}
function rdpOnVisibilityChange() { if (document.hidden && RDP.session) rdpReleaseHeldKeys(); }

// Document-level fallback (idea #2258): while a session is live and focus is NOT in a form control, keys
// reach the remote even though the canvas lost focus. This is what makes a detached keyboard non-fatal.
function rdpOnDocKeyDown(e) { rdpHandleKey(e, true, true); }
function rdpOnDocKeyUp(e) { rdpHandleKey(e, false, true); }

// Post the keyboard counters once per session. Silent when nothing interesting happened, so this adds no
// log noise to healthy sessions — it fires when keys were unmapped, injection threw, the document
// fallback carried traffic, or keys had to be force-released.
function rdpKeyDiagFlush(hostname) {
  var d = RDP.kdiag;
  if (!d || RDP.kdiagSent) return;
  RDP.kdiagSent = true;
  if (!d.seen && !d.errors && !d.unicodeSent) return;     // nobody typed — nothing to report
  // unicodeSent/compositions/softKbdSkipped are deliberately NOT part of "notable" (idea #2259): a
  // tablet user typing normally through the soft keyboard is the healthy case, same as a desktop
  // user typing normally through the canvas — it shouldn't generate a report of its own. They still
  // ride along in the message below whenever a report fires for another reason.
  var notable = d.errors || d.unmapped || d.viaDoc || d.released || d.refocus;
  if (!notable) return;                                   // a clean typing session
  try {
    if (typeof reportRdpDiag === 'function') {
      reportRdpDiag(hostname || RDP.host, 'keyboard',
        'seen=' + d.seen + ' sent=' + d.sent + ' unmapped=' + d.unmapped + ' errors=' + d.errors +
        ' viaDoc=' + d.viaDoc + ' refocus=' + d.refocus + ' released=' + d.released +
        ' unicode=' + d.unicodeSent + ' compositions=' + d.compositions + ' softKbdSkipped=' + d.softKbdSkipped + ' repeatDropped=' + d.repeatDropped +
        (d.codes.length ? ' codes=' + d.codes.join(',') : '') +
        (d.lastError ? ' last=' + d.lastError : ''));
    }
  } catch { /* best-effort */ }
}

function rdSetState(text, cls) {
  var el = document.getElementById('rv-state');
  if (el) { el.textContent = text; el.className = 'rv-state' + (cls ? ' ' + cls : ''); }
}

// Open the full-window viewer and AUTO-CONNECT — no Connect button, no credential prompt. The station
// is provisioned non-NLA; if a credential is tied it's injected server-side (auto-login), otherwise the
// client just gets the Windows sign-in screen inside the session. The top bar shows only "host · app".
function openRdpViewer(hostname, targetId, targetName, companyId) {
  if (!window.__ironrdp) { showToast('The remote viewer failed to load. Please refresh.', 'danger'); return; }
  RDP.host = hostname;
  RDP.target = targetId || 'desktop';
  RDP.targetName = targetName || '';
  RDP.companyId = (companyId != null && companyId !== '') ? companyId : null; // #1619: station's company for ticket mint
  RDP.intentional = false;      // fresh open (idea #1878)
  RDP.everConnected = false;
  RDP.reconnectAttempts = 0;
  if (RDP.reconnectTimer) { try { clearTimeout(RDP.reconnectTimer); } catch { /* */ } RDP.reconnectTimer = null; }
  var ov = document.getElementById('remote-viewer');
  var hostEl = document.getElementById('rv-host');
  var title = (RDP.targetName && RDP.targetName.toLowerCase() !== 'desktop') ? (hostname + ' · ' + RDP.targetName) : hostname;
  if (hostEl) hostEl.textContent = title;
  var bannerHost = document.getElementById('rv-session-host'); // full-screen session indicator (idea #1779)
  if (bannerHost) bannerHost.textContent = title;
  if (ov) { ov.classList.remove('is-hidden'); ov.setAttribute('aria-hidden', 'false'); ov.classList.add('rv-rdp'); }
  var screen = document.getElementById('rv-screen');
  if (screen) screen.innerHTML = '';
  rdSetState('Connecting…', '');
  // Remote audio (idea #1897): create/resume the AudioContext HERE — this is the connect click, a
  // real user gesture, so the autoplay policy grants playback for the sound that arrives later.
  rdpAudioStart();
  // Resolution zoom (idea #1897 follow-up): reflect the persisted zoom on the toolbar; the first
  // connect already honours it via rdpViewportSize().
  try { if (typeof rdpZoomSyncButtons === 'function') rdpZoomSyncButtons(); } catch { /* */ }
  try { if (typeof rdpQualitySyncButtons === 'function') rdpQualitySyncButtons(); } catch { /* */ } // idea #2083
  connectRdp(hostname, RDP.target);
}

function rdpApplyOne(ev) {
  var tx = new window.__ironrdp.InputTransaction();
  tx.addEvent(ev);
  RDP.session.applyInputs(tx);
}

function rdpWireInput(canvas) {
  var D = window.__ironrdp.DeviceEvent;
  canvas.addEventListener('keydown', function (e) { rdpHandleKey(e, true, false); });
  canvas.addEventListener('keyup', function (e) { rdpHandleKey(e, false, false); });
  // Focus is what the keyboard rides on, so track it (idea #2258): losing it must release anything held
  // (or Windows keeps that modifier down) and must tell the user the keyboard is detached.
  canvas.addEventListener('focus', rdpUpdateKbdHint);
  canvas.addEventListener('blur', function () { rdpReleaseHeldKeys(); rdpUpdateKbdHint(); });
  // Mouse motion is COALESCED to one input PDU per animation frame (idea #2083). A gaming mouse fires
  // mousemove at its polling rate (125–1000 Hz); previously every one of those became its own
  // InputTransaction → its own fast-path PDU → its own WebSocket frame → its own 32KB-max tunnel frame
  // on the agent's shared control socket. That is a flood of tiny upstream writes that the remote can't
  // consume faster than it redraws anyway, and on a congested tunnel it delays the pixels coming back.
  // Only the LAST position in a frame matters for a move, so we keep that one and drop the rest.
  //
  // We also stash raw clientX/clientY and defer getBoundingClientRect() into the frame callback: reading
  // it per event forced a synchronous layout on every mouse movement.
  var pendingMove = null, moveScheduled = false;
  var flushMove = function () {
    var m = pendingMove; pendingMove = null;
    if (!m || !RDP.session) return;
    var r = canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var x = Math.round((m.cx - r.left) * (canvas.width / r.width));
    var y = Math.round((m.cy - r.top) * (canvas.height / r.height));
    try { rdpApplyOne(D.mouseMove(x, y)); } catch { /* */ }
  };
  canvas.addEventListener('mousemove', function (e) {
    if (!RDP.session) return;
    pendingMove = { cx: e.clientX, cy: e.clientY };
    if (moveScheduled) return;
    moveScheduled = true;
    requestAnimationFrame(function () { moveScheduled = false; flushMove(); });
  });
  // Buttons and wheel flush any queued motion FIRST — a click must land where the pointer actually is,
  // not where the last emitted frame left it, or a fast click-after-move would hit the wrong target.
  canvas.addEventListener('mousedown', function (e) {
    e.preventDefault();
    // Canvas focus can NEVER raise a soft keyboard (idea #2259) — on a touch pointer, route the tap
    // straight to the relay input instead, so tapping the remote screen is itself a path to typing.
    // Desktop/mouse behaviour (rdpFocusCanvas) is completely unchanged — zero regression risk to the
    // canvas-focus fix that just shipped in #2258.
    if (RDP_TOUCH_CAPABLE) rdpShowKeyboard(); else rdpFocusCanvas();
    if (!RDP.session) return;
    flushMove();
    try { rdpApplyOne(D.mouseButtonPressed(e.button)); } catch { /* */ }
  });
  canvas.addEventListener('mouseup', function (e) {
    e.preventDefault(); if (!RDP.session) return;
    flushMove();
    try { rdpApplyOne(D.mouseButtonReleased(e.button)); } catch { /* */ }
  });
  canvas.addEventListener('wheel', function (e) {
    e.preventDefault(); if (!RDP.session) return;
    flushMove();
    try {
      if (e.deltaY !== 0) rdpApplyOne(D.wheelRotations(true, e.deltaY > 0 ? -1 : 1, 1));
      if (e.deltaX !== 0) rdpApplyOne(D.wheelRotations(false, e.deltaX > 0 ? -1 : 1, 1));
    } catch { /* */ }
  }, { passive: false });
  canvas.addEventListener('contextmenu', function (e) { e.preventDefault(); });
}

// ── Clipboard redirection (CLIPRDR, idea #1866) ───────────────────────────────────────────────────
// ironrdp-web already speaks CLIPRDR over the RDP channel; we just bridge it to the browser Clipboard
// API. Text + images, both directions. Requirements/limits: a secure context (HTTPS) and the browser's
// clipboard-read permission — so reads can be denied without a user gesture. Every call is best-effort
// and never throws into the WASM. Files are out of scope: the async Clipboard API can't read OS file
// references from a browser, so local→remote file paste is impossible (tracked separately, idea #1867).

// Remote → local: the remote session's clipboard changed; mirror it into the browser clipboard. `data`
// is an ironrdp ClipboardData (its items() are ironrdp ClipboardItems, NOT DOM ClipboardItems). We take
// the first text and/or PNG image item. To stop the local poll from immediately echoing this straight
// back to the remote, we prime clipSig (text case) and set a short clipSuppress window (covers images).
function rdpApplyRemoteClipboard(data) {
  if (!data || !navigator.clipboard) return;
  var items = [];
  try { items = data.items() || []; } catch { return; }
  var text = null, imgBytes = null, imgMime = null;
  for (var i = 0; i < items.length; i++) {
    var mime = '', val = null;
    try { mime = items[i].mimeType() || ''; val = items[i].value(); } catch { continue; }
    if (mime.indexOf('text') === 0 && text == null && typeof val === 'string') text = val;
    else if (mime.indexOf('image/') === 0 && imgBytes == null && val && val.byteLength != null) { imgBytes = val; imgMime = mime; }
  }
  if (text == null && imgBytes == null) return;
  // Anti-echo: prime the dedupe key (text) and open a brief suppress window (covers the image case).
  if (text != null) RDP.clipSig = 'T:' + text;
  RDP.clipSuppress = true;
  setTimeout(function () { RDP.clipSuppress = false; }, 1200);
  // Prefer a rich write (image, plus text) where supported; fall back to writeText.
  if (imgBytes != null && window.ClipboardItem && navigator.clipboard.write) {
    try {
      var payload = {};
      payload[imgMime] = new Blob([imgBytes], { type: imgMime });
      if (text != null) payload['text/plain'] = new Blob([text], { type: 'text/plain' });
      navigator.clipboard.write([new window.ClipboardItem(payload)]).catch(function () {
        if (text != null) { try { navigator.clipboard.writeText(text).catch(function () {}); } catch { /* */ } }
      });
      return;
    } catch { /* fall through to text-only */ }
  }
  if (text != null) { try { navigator.clipboard.writeText(text).catch(function () {}); } catch { /* */ } }
}

// Local → remote: read the browser clipboard and announce it to the session (onClipboardPaste). Uses
// the rich read() (text + image/png) when available, else readText(). In the poll path (viaGesture
// false) a push is skipped when its signature matches the last one (clipSig); the toolbar button
// (viaGesture true) always pushes. Image bytes are only decoded when the cheap text/types signature
// changed, so a static image sitting on the clipboard isn't re-read every tick.
function rdpReadLocalAndPush(viaGesture) {
  if (!RDP.session || !navigator.clipboard) return;
  var I = window.__ironrdp;
  if (!I || !I.ClipboardData) return;
  if (navigator.clipboard.read) {
    navigator.clipboard.read().then(function (clipItems) {
      clipItems = clipItems || [];
      var textJob = null, imgItem = null, typesSig = '';
      clipItems.forEach(function (ci) {
        (ci.types || []).forEach(function (t) {
          typesSig += t + ';';
          if (t === 'text/plain' && !textJob) textJob = ci.getType(t).then(function (b) { return b.text(); });
          else if (t === 'image/png' && !imgItem) imgItem = ci;
        });
      });
      (textJob || Promise.resolve(null)).then(function (txt) {
        var hasImg = typesSig.indexOf('image/png') !== -1;
        var sig = hasImg ? ('IMG:' + typesSig + '|T:' + (txt || '')) : ('T:' + (txt == null ? '' : txt));
        if (!viaGesture && sig === RDP.clipSig) return; // unchanged since last push
        var data = new I.ClipboardData();
        if (txt != null) data.addText('text/plain', txt);
        var finish = function () {
          try { if (data.isEmpty()) return; } catch { /* */ }
          RDP.clipSig = sig;
          try { RDP.session.onClipboardPaste(data).catch(function () {}); } catch { /* */ }
        };
        if (imgItem) {
          imgItem.getType('image/png').then(function (b) { return b.arrayBuffer(); })
            .then(function (ab) { try { data.addBinary('image/png', new Uint8Array(ab)); } catch { /* */ } finish(); })
            .catch(finish);
        } else { finish(); }
      }).catch(function () { /* */ });
    }).catch(function () { rdpReadTextAndPush(viaGesture); });
  } else {
    rdpReadTextAndPush(viaGesture);
  }
}

// Text-only fallback (no async read(), e.g. older Firefox) — read the clipboard text and push it.
function rdpReadTextAndPush(viaGesture) {
  if (!navigator.clipboard || !navigator.clipboard.readText) return;
  var I = window.__ironrdp; if (!I || !I.ClipboardData) return;
  navigator.clipboard.readText().then(function (txt) {
    if (txt == null) return;
    var sig = 'T:' + txt;
    if (!viaGesture && sig === RDP.clipSig) return;
    var data = new I.ClipboardData();
    data.addText('text/plain', txt);
    RDP.clipSig = sig;
    try { RDP.session.onClipboardPaste(data).catch(function () {}); } catch { /* */ }
  }).catch(function () { /* denied / no focus — best-effort */ });
}

// Explicit "Paste to remote" toolbar action — a real user gesture, so clipboard reads (incl. images)
// are reliably permitted here even where the passive poll is denied. Toasts a hint on either outcome.
function rdpPasteToRemote() {
  if (!RDP.session) return;
  if (!window.isSecureContext || !navigator.clipboard) {
    try { showToast('Clipboard needs a secure (HTTPS) connection.', 'warning'); } catch { /* */ }
    return;
  }
  try { rdpReadLocalAndPush(true); showToast('Clipboard sent to the remote session.', 'success'); }
  catch { try { showToast('Could not read your clipboard.', 'danger'); } catch { /* */ } }
}

// Wire the local→remote auto-sync poll for a connected canvas. The poll runs only while the RDP canvas
// has focus (a user-activation context, so reads are permitted) AND the tab is focused; it stops on
// blur. The canvas is destroyed on close/reconnect, so its listeners die with it — we only need to
// clear the interval (rdpStopClipPoll, called from closeRdpViewer/rdpReconnectAtSize).
function rdpWireClipboard(canvas) {
  if (!canvas) return;
  if (!window.isSecureContext || !navigator.clipboard) return; // no clipboard without HTTPS + the API
  canvas.addEventListener('focus', rdpStartClipPoll);
  canvas.addEventListener('blur', rdpStopClipPoll);
  if (document.activeElement === canvas) rdpStartClipPoll();
}
function rdpStartClipPoll() {
  if (RDP.clipPoll || !RDP.session) return;
  RDP.clipPoll = setInterval(function () {
    if (!RDP.session) { rdpStopClipPoll(); return; }
    if (RDP.clipSuppress || !document.hasFocus()) return;
    try { rdpReadLocalAndPush(false); } catch { /* */ }
  }, 700);
}
function rdpStopClipPoll() {
  if (RDP.clipPoll) { try { clearInterval(RDP.clipPoll); } catch { /* */ } RDP.clipPoll = null; }
}

// Connecting overlay — uses the shared #rv-overlay (it lives in .rv-stage, so it survives the
// #rv-screen innerHTML churn) plus a spinner, so the 1–2s NLA/connect handshake never looks frozen.
function rdShowConnecting(hostname, label) {
  var ov = document.getElementById('rv-overlay');
  if (!ov) return;
  if (!document.getElementById('rdp-spinner')) {
    var sp = document.createElement('div'); sp.id = 'rdp-spinner'; sp.className = 'rdp-spinner';
    ov.insertBefore(sp, ov.firstChild); ov.classList.add('rv-overlay-rdp');
  }
  var t = document.getElementById('rv-overlay-text');
  if (t) t.textContent = label || ('Connecting to ' + hostname + '…');
  ov.classList.remove('is-hidden'); ov.setAttribute('aria-hidden', 'false');
}
function rdHideConnecting() {
  var ov = document.getElementById('rv-overlay');
  if (!ov) return;
  var sp = document.getElementById('rdp-spinner'); if (sp && sp.parentNode) sp.parentNode.removeChild(sp);
  ov.classList.remove('rv-overlay-rdp'); ov.classList.add('is-hidden'); ov.setAttribute('aria-hidden', 'true');
}

// Set the viewer title bar from the mint response (host, or "host · App" for a published app).
function rdpSetTitle(hostname, res) {
  if (!res || !res.target_name) return;
  var hostEl = document.getElementById('rv-host');
  if (hostEl) hostEl.textContent = (res.target_name.toLowerCase() !== 'desktop') ? (hostname + ' · ' + res.target_name) : hostname;
}

// Classify an ironrdp connect failure (IronError.kind() is a numeric IronErrorKind) into a user-facing
// message + whether it's a credential rejection (→ re-prompt) vs a hard failure (→ toast). detail=backtrace.
// Windows account-state NTSTATUS codes the DC returns through CredSSP (idea #3056). ironrdp surfaces
// these as a General-kind error whose TEXT carries "STATUS_… [0xc00000xx]", so they never hit the
// WrongPassword/LogonFailure kinds — before this they fell through to a raw toast over a black canvas.
// Each is an ACCOUNT problem the user can't fix by retyping the password, so none of them re-prompt.
var RDP_ACCOUNT_STATUS = {
  'c0000071': { code: 'password_expired', title: 'Your Windows password has expired', message: 'The password for this computer’s sign-in has expired, so Windows won’t let it connect. Let us know below and we’ll get it sorted — then try again.' },
  'c0000224': { code: 'password_must_change', title: 'Your Windows password needs to be changed', message: 'Windows requires this account to set a new password before it can sign in remotely. Let us know below and we’ll reset it for you.' },
  'c0000234': { code: 'account_locked', title: 'This account is locked', message: 'Too many sign-in attempts have locked this Windows account. Let us know below and we’ll unlock it.' },
  'c0000072': { code: 'account_disabled', title: 'This account is disabled', message: 'The Windows account used to sign in to this computer has been disabled. Let us know below if you still need access.' },
  'c0000193': { code: 'account_expired', title: 'This account has expired', message: 'The Windows account used to sign in to this computer has expired. Let us know below if you still need access.' },
  'c000006f': { code: 'logon_hours', title: 'Sign-in isn’t allowed right now', message: 'This Windows account is restricted to certain hours and can’t sign in at this time.' },
  'c000006e': { code: 'account_restricted', title: 'This account can’t sign in remotely', message: 'A Windows policy is blocking this account from signing in to this computer remotely. Let us know below and we’ll take a look.' },
};
function rdpAccountStatus(text) {
  var m = /0x(c0000[0-9a-f]{3})/i.exec(String(text || ''));
  return m ? (RDP_ACCOUNT_STATUS[m[1].toLowerCase()] || null) : null;
}

function rdpErrorInfo(err) {
  var K = { WRONG_PASSWORD: 1, LOGON_FAILURE: 2, ACCESS_DENIED: 3, PROXY_CONNECT: 5, NEGOTIATION: 6 };
  var kind = null, detail = '';
  try { if (err && typeof err.kind === 'function') kind = err.kind(); } catch (e) { /* */ }
  try { if (err && typeof err.backtrace === 'function') detail = err.backtrace(); } catch (e) { /* */ }
  var account = rdpAccountStatus(detail) || rdpAccountStatus(err && err.message);
  if (account) {
    if (!detail) detail = (err && err.message) ? String(err.message) : ('kind=' + kind);
    return { kind: kind, authReject: false, accessDenied: false, account: account.code, title: account.title, message: account.message, detail: detail };
  }
  var authReject = (kind === K.WRONG_PASSWORD || kind === K.LOGON_FAILURE);
  var accessDenied = (kind === K.ACCESS_DENIED);
  var message;
  if (authReject) message = 'Sign-in was rejected — check your username and password, then try again.';
  else if (accessDenied) message = 'Your account isn’t permitted to sign in to this computer.';
  else if (kind === K.PROXY_CONNECT) message = 'Couldn’t reach the remote computer — it may be offline.';
  else if (kind === K.NEGOTIATION) message = 'The remote computer refused the connection.';
  // A plain Error (kind null) is a ticket-mint failure whose message is already user-facing; an unmapped
  // IronError's text is a Rust backtrace, so it gets a friendly line (the raw text goes to rdp-diag).
  else if (kind == null && err && err.message) message = err.message;
  else message = 'The remote computer couldn’t start the session. Please try again — if it keeps happening, let us know below.';
  if (!detail) detail = (err && err.message) ? String(err.message) : ('kind=' + kind);
  var title = accessDenied ? 'You can’t sign in to this computer'
    : (kind === K.PROXY_CONNECT ? 'Couldn’t reach the computer' : 'Couldn’t connect');
  return { kind: kind, authReject: authReject, accessDenied: accessDenied, account: null, title: title, message: message, detail: detail };
}

// Mint a ticket, then run the RDP session. ironrdp-web mandates NLA/CredSSP, so we need real credentials:
// the tied docs credential is returned by the mint (res.cred) for zero-click auto-login; if none is tied
// (res.needs_cred) — or a sign-in was rejected — the client signs in via a prompt. opts.forcePrompt skips
// the tied cred and goes straight to the prompt (opts.promptError shows a rejection banner, user-only).
function connectRdp(hostname, targetId, opts) {
  opts = opts || {};
  if (opts.forcePrompt) { showRdpSignInPrompt(hostname, targetId, opts.promptError || null, opts.prefillUser || null); return; }
  rdShowConnecting(hostname);
  requestRdpTicket(hostname, targetId, RDP.companyId)
    .then(function (res) {
      if (RDP.host !== hostname) return; // closed while minting
      if (!res || !res.ticket) throw new Error((res && res.message) || 'Could not start the session.');
      rdpSetTitle(hostname, res);
      if (res.cred && res.cred.username) { doConnectRdp(hostname, res, res.cred, { userEntered: false }); return; }
      showRdpSignInPrompt(hostname, targetId, null); // none tied → first-time sign-in (no banner)
    })
    .catch(function (err) { RDP.reconnecting = false; rdpShowConnectFailure(hostname, rdpErrorInfo(err)); });
}

// Fetch the station sign-in context (domain-join + last-user), then show the credential prompt. The
// rejection banner (errorMsg) is passed through ONLY for a user-supplied credential — never for a bad
// tied credential (the user didn't provide those, so "rejected" would be confusing). prefillUser (the
// username that was just rejected) is carried into the retry so it prefills the field as a hint.
function showRdpSignInPrompt(hostname, targetId, errorMsg, prefillUser) {
  rdShowConnecting(hostname, 'Preparing sign-in…');
  var open = function (ctx) {
    rdHideConnecting();
    if (RDP.host !== hostname) return;
    promptRdpCreds(hostname, ctx || {}, errorMsg, function (cred) { mintThenConnect(hostname, targetId, cred, { userEntered: true }); }, prefillUser);
  };
  requestRdpSigninContext(hostname, RDP.companyId).then(open).catch(function () { open({}); });
}

// Mint a fresh ticket for a user-supplied credential (prompt submit / retry), then connect. meta carries
// { userEntered } so a successful connect saves the credential and a rejection re-prompts with a banner.
function mintThenConnect(hostname, targetId, cred, meta) {
  rdShowConnecting(hostname);
  requestRdpTicket(hostname, targetId, RDP.companyId)
    .then(function (res) {
      if (RDP.host !== hostname) return;
      if (!res || !res.ticket) throw new Error((res && res.message) || 'Could not start the session.');
      rdpSetTitle(hostname, res);
      doConnectRdp(hostname, res, cred, meta || {});
    })
    .catch(function (err) {
      RDP.reconnecting = false;
      // A ticket-mint failure mid auto-reconnect is still a transport blip (server briefly
      // unreachable) — keep retrying with backoff rather than dead-ending on a toast (idea #1878).
      if (meta && meta.isReconnect) { rdpScheduleReconnect(hostname); return; }
      rdpShowConnectFailure(hostname, rdpErrorInfo(err));
    });
}

// Sign-in prompt — makes clear we want the username & password for THIS computer. On a non-domain station
// the last logged-in user is prefilled (ctx.last_user); on a domain station we hint the domain instead.
// errorMsg (rejection banner) is shown only when passed (user-supplied credential was rejected). If a
// prefillUser is passed (the username that was just rejected on a retry) it takes precedence over the
// last_user fallback — so the user keeps their guess as a hint, and this also fills the field on a
// domain station (where last_user is never used).
function promptRdpCreds(hostname, ctx, errorMsg, cb, prefillUser) {
  var screen = document.getElementById('rv-screen');
  if (!screen) return;
  ctx = ctx || {};
  rdSetState('Sign in', '');
  var note = ctx.domain_joined
    ? ('Enter your <strong>' + rdEsc(ctx.domain_netbios || 'domain') + '</strong> username and password.')
    : 'Enter the username and password you use to sign in to this computer.';
  var prefill = prefillUser ? String(prefillUser) : ((!ctx.domain_joined && ctx.last_user) ? String(ctx.last_user) : '');
  screen.innerHTML =
    '<form id="rdp-login" class="rdp-login" autocomplete="off">' +
    '  <div class="rdp-login-title">Sign in to ' + rdEsc(hostname) + '</div>' +
    '  <div class="rdp-login-note">' + note + '</div>' +
    (errorMsg ? '  <div class="rdp-login-error">' + rdEsc(errorMsg) + '</div>' : '') +
    '  <label>Username</label><input id="rdp-user" type="text" placeholder="' + (ctx.domain_joined ? 'DOMAIN\\user' : 'username') + '" autocomplete="off" value="' + rdEsc(prefill) + '">' +
    '  <label>Password</label><input id="rdp-pass" type="password" autocomplete="off">' +
    '  <button type="submit" class="rdp-connect-btn"><i class="fa-solid fa-plug"></i> Connect</button>' +
    '</form>';
  var form = document.getElementById('rdp-login');
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var u = (document.getElementById('rdp-user') || {}).value || '';
    var p = (document.getElementById('rdp-pass') || {}).value || '';
    if (!u) { showToast('Enter your username.', 'warning'); return; }
    if (!p) { showToast('Enter your password.', 'warning'); return; }
    cb({ username: u, password: p, domain: '' });
  });
  var focusId = prefill ? 'rdp-pass' : 'rdp-user';
  var ff = document.getElementById(focusId); if (ff) try { ff.focus(); } catch { /* */ }
}
function rdEsc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

// Build + run the RDP session with credentials (NLA/CredSSP). enable_credssp is REQUIRED — ironrdp-web
// has no SSL-only mode. The server still injects the alt-shell (single-app kiosk) after CredSSP.
function doConnectRdp(hostname, res, cred, meta) {
  meta = meta || {};
  var screen = document.getElementById('rv-screen');
  rdSetState('Connecting…', '');
  window.__ironrdp.ensure().then(function () {
    if (RDP.host !== hostname) return;
    if (screen) screen.innerHTML = '';
    var vp = rdpViewportSize();
    var canvas = document.createElement('canvas');
    canvas.id = 'rdp-canvas';
    canvas.width = vp.w; canvas.height = vp.h;
    canvas.setAttribute('tabindex', '0');
    if (screen) screen.appendChild(canvas);

    // Soft-keyboard relay input (idea #2259) — session-scoped like the canvas: created here, torn
    // down with #rv-screen on close/reconnect, never leaks state across sessions. Deliberately NOT
    // display:none/visibility:hidden (both block iOS/Android from raising the on-screen keyboard);
    // see remote.css .rdp-soft-kbd for the rest of the "invisible but truly focusable" styling.
    var softKbd = document.createElement('input');
    softKbd.type = 'text';
    softKbd.id = 'rdp-soft-kbd';
    softKbd.className = 'rdp-soft-kbd';
    softKbd.autocomplete = 'off';
    softKbd.setAttribute('autocorrect', 'off');
    softKbd.setAttribute('autocapitalize', 'off');
    softKbd.spellcheck = false;
    softKbd.setAttribute('inputmode', 'text');
    softKbd.setAttribute('enterkeyhint', 'enter');
    softKbd.setAttribute('aria-hidden', 'true');
    softKbd.setAttribute('tabindex', '-1'); // reachable only via rdpShowKeyboard / a canvas tap, never Tab
    if (screen) screen.appendChild(softKbd);

    // Claim the 2D context BEFORE the WASM does (idea #2083). ironrdp-web's canvas.rs asks for it with a
    // bare get_context('2d') and no options; getContext returns the SAME context object on every later
    // call and ignores the attributes of the second call — so whoever asks FIRST decides the attributes.
    // alpha:false drops per-blit alpha compositing (every put_image_data would otherwise be blended
    // against a transparent backdrop), and desynchronized:true lets the browser bypass a compositing
    // round trip and paint closer to the wire. Both are pure wins for an opaque, full-surface remote
    // desktop. Wrapped in try/catch: if a browser rejects the options we simply let the WASM create it.
    try {
      var ctx2d = canvas.getContext('2d', { alpha: false, desynchronized: true });
      if (typeof rdpStatsAttachCanvas === 'function') rdpStatsAttachCanvas(ctx2d);
    } catch { /* fall back to the WASM's own get_context('2d') */ }
    // Must be installed before the WASM opens its WebSocket, or we miss the session.
    try { if (typeof rdpStatsInstallSocketCounter === 'function') rdpStatsInstallSocketCounter(); } catch { /* */ }

    // No session id in the URL (idea #3509): it is the portal's long-lived bearer token and a URL can
    // reach access logs. The single-use ticket is bound to this session server-side.
    var proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    var proxyAddress = proto + '//' + location.host + '/api/rdp?hostname=' + encodeURIComponent(hostname) +
      '&ticket=' + encodeURIComponent(res.ticket);

    var I = window.__ironrdp;
    var b = new I.SessionBuilder();
    b.username(cred.username || '');
    b.password(cred.password || '');
    if (cred.domain) b.serverDomain(cred.domain);
    b.destination(hostname + ':3389');   // ignored server-side (bridge dials the tunnel), API requires it
    b.proxyAddress(proxyAddress);
    b.authToken('none');
    b.desktopSize(new I.DesktopSize(vp.w, vp.h));
    b.renderCanvas(canvas);
    // Keep the canvas backing store matched to the (re)negotiated remote resolution — fires on the
    // initial size and after every session.resize() the remote acks (Display Control channel).
    b.canvasResizedCallback(function () {
      try { if (!RDP.session) return; var ds = RDP.session.desktopSize(); if (ds && ds.width) { canvas.width = ds.width; canvas.height = ds.height; } } catch (e) { /* */ }
    });
    b.extension(new I.Extension('enable_credssp', true)); // ironrdp-web mandates NLA/CredSSP

    // RDP performance flags (idea #2083) — the mstsc "Experience" knobs: hide the desktop wallpaper,
    // drop theming, stop animating menus, skip cursor shadow/blink settings, disable ClearType font
    // smoothing. Upstream ironrdp hardcodes PerformanceFlags::default() (only full-window-drag +
    // menu-animations off, and font smoothing explicitly ON), so none of the expensive ones were ever
    // being sent. The exact bitmask comes from the user's chosen quality profile (rdpQuality.js).
    // Guarded: a WASM build predating the performance-flags patch simply ignores an unknown Extension,
    // so this is a no-op there rather than a connect failure.
    try {
      if (typeof rdpQualityGet === 'function') {
        b.extension(new I.Extension('performance_flags', rdpQualityGet().perf));
      }
    } catch { /* older WASM without the performance-flags patch — non-fatal */ }

    // Local printing (idea #1655): ironrdp-web advertises a redirected virtual printer inside the
    // remote session. We request the "Microsoft Print to PDF" driver so the remote renders each job
    // straight to PDF bytes (built into Win10+/Server 2016+) — the streamed chunks are already a PDF,
    // so no PostScript→PDF step is needed. When the user prints in the session, the job streams back
    // here and we hand them a downloadable/printable PDF (which they print to their real local printer).
    var printJobs = {}; // fileId → array of copied Uint8Array chunks
    b.extension(new I.Extension('printer_driver_name', 'Microsoft Print to PDF'));
    b.extension(new I.Extension('print_job_stream_callbacks', {
      onJobStart: function (id) { printJobs[id] = []; },
      onJobData: function (id, chunk) {
        // `chunk` is a view into WASM memory that is reused after this call — COPY it now.
        if (!printJobs[id]) printJobs[id] = [];
        try { printJobs[id].push(new Uint8Array(chunk)); } catch (e) { /* */ }
      },
      onJobComplete: function (id) {
        var chunks = printJobs[id] || []; delete printJobs[id];
        try { deliverRdpPrintPdf(chunks); } catch (e) { /* */ }
      },
      onJobError: function (id) {
        delete printJobs[id];
        try { showToast('The print job could not be completed.', 'danger'); } catch (e) { /* */ }
      },
    }));

    b.setCursorStyleCallbackContext(canvas);
    // ironrdp hands us a ready CSS cursor string ('default', 'none', or a `url(data:…) hotx hoty, auto`
    // for a server bitmap). Guard against a non-string so we never assign an invalid value.
    b.setCursorStyleCallback(function (style) {
      try { canvas.style.cursor = (typeof style === 'string' && style) ? style : 'default'; } catch (e) { /* */ }
    });

    // Clipboard redirection (CLIPRDR, idea #1866). remoteClipboardChanged → mirror the remote clipboard
    // into the browser; forceClipboardUpdate → the remote asked us to (re)send the local clipboard now.
    // Local→remote is otherwise driven by a focus-scoped poll + the toolbar button (see rdpWireClipboard).
    // Guarded so a clipboard error never breaks the session; the channel is only advertised because we
    // register these callbacks, so no-op-safe if the far end doesn't support CLIPRDR.
    try {
      b.remoteClipboardChangedCallback(function (data) { try { rdpApplyRemoteClipboard(data); } catch { /* */ } });
      b.forceClipboardUpdateCallback(function () { try { rdpReadLocalAndPush(true); } catch { /* */ } }); // remote explicitly asked → always push
    } catch { /* older WASM without clipboard builder methods — non-fatal */ }

    // Remote audio playback (RDPSND / MS-RDPEA, idea #1897). The rebuilt ironrdp-web backend streams
    // the remote desktop's sound to these callbacks (PCM); rdpAudio.js schedules it to the speakers.
    // Guarded: a WASM build predating the audio backend just ignores the Extension → silent no-op.
    try {
      b.extension(new I.Extension('audio_stream_callbacks', {
        onFormat: function (rate, channels, bits) { try { rdpAudioOnFormat(rate, channels, bits); } catch { /* */ } },
        onData: function (chunk) { try { rdpAudioOnData(chunk); } catch { /* */ } },
      }));
    } catch { /* older WASM without the audio backend — non-fatal, no sound */ }

    return b.connect().then(function (session) {
      if (RDP.host !== hostname) { try { session.shutdown(); } catch { /* */ } return; }
      RDP.session = session;
      RDP.cred = cred;            // stash for reconnect-at-size on fullscreen (idea #1779)
      RDP.reconnecting = false;   // this connect satisfied any pending reconnect
      RDP.reconnectAttempts = 0;  // a good connect clears the transport-drop backoff (idea #1878)
      RDP.everConnected = true;   // a session was established → Close should show the completion card
      RDP.clipSuppress = false;   // clear any leftover clipboard suppress from a prior session (idea #1866)
      var ds = session.desktopSize();
      canvas.width = ds.width; canvas.height = ds.height;
      rdHideConnecting();
      rdSetState('', 'ok');
      try { if (typeof rdpStatsStart === 'function') rdpStatsStart(); } catch { /* */ } // perf HUD (#2083, opt-in)
      // Save-on-success (idea #1356): the user's own credential just authenticated, so persist it to the
      // docs vault (create+link or update) — the next connect is then zero-click. Only for user-entered
      // creds (a tied cred is already saved); fire-and-forget so it never affects the live session.
      if (meta.userEntered && typeof saveRdpCredential === 'function') {
        try { saveRdpCredential(hostname, cred.username, cred.password, RDP.companyId); } catch (e) { /* */ }
      }
      rdpKeyDiagReset();          // fresh keyboard state + counters for this session (idea #2258)
      rdpWireInput(canvas);
      rdpWireSoftKeyboard(softKbd); // soft-keyboard relay (idea #2259)
      rdpFocusCanvas();           // after wiring, so the focus handler can settle the on-screen cue
      RDP.clipSig = null; // fresh session — don't carry a prior clipboard signature
      rdpWireClipboard(canvas); // local→remote clipboard auto-sync (idea #1866)
      // Re-fit the remote desktop to the workspace when the browser window resizes (debounced). The
      // remote acks via the Display Control channel → canvasResizedCallback re-sizes the canvas.
      if (RDP.onResize) { try { window.removeEventListener('resize', RDP.onResize); } catch { /* */ } }
      RDP.onResize = rdpDebounce(function () {
        if (!RDP.session) return;
        var v = rdpViewportSize();
        try { RDP.session.resize(v.w, v.h); } catch (e) { /* best-effort: most hosts lack Display Control */ }
      }, 300);
      window.addEventListener('resize', RDP.onResize);
      // Pass the session so a stale run()-resolution from a reconnected-away session can't null the new
      // one. Capture the OUTCOME (idea #1878): a resolved SessionTerminationInfo means the session ended
      // gracefully (Windows logoff / server disconnect); a rejection means the transport died (network /
      // WS / TLS drop) → auto-reconnect. rdpOnEnded branches on which fired.
      session.run().then(function (info) { rdpOnEnded(session, info, null); }).catch(function (err) { rdpOnEnded(session, null, err); });
    });
  }).catch(function (err) {
    RDP.reconnecting = false; // a failed (re)connect must not wedge future reconnects (idea #1779)
    var info = rdpErrorInfo(err);
    // Surface the real ironrdp error server-side (idea #1356) so connect failures are diagnosable.
    try { if (typeof reportRdpDiag === 'function') reportRdpDiag(hostname, 'connect', info.detail || info.message); } catch (e) { /* */ }
    // Mid-auto-reconnect (idea #1878): a valid credential that fails to reconnect is a transport blip,
    // not a bad password — keep retrying with backoff instead of toasting or re-prompting. Auth rejects
    // still fall through to the prompt (creds may have changed since the drop).
    // An account-state failure (expired/locked/disabled, idea #3056) won't heal by retrying — stop and explain.
    if (meta.isReconnect && !info.authReject && !info.account) { rdpScheduleReconnect(hostname); return; }
    rdHideConnecting();
    // A rejected/incorrect credential → re-open the sign-in prompt so they can re-enter, rather than
    // dead-ending on a toast. The rejection banner is shown ONLY if the user supplied those creds (a bad
    // TIED credential re-prompts with no "rejected" banner — they never entered it). Other failures
    // (offline, refused, protocol) → toast.
    if (info.authReject) { connectRdp(hostname, RDP.target, { forcePrompt: true, promptError: meta.userEntered ? info.message : null, prefillUser: (cred && cred.username) || null }); return; }
    // Everything else → an explanatory card IN the viewer (idea #3056). A toast over the empty canvas
    // read as "black screen" to clients: the toast faded and the black stage was all that was left.
    rdpShowConnectFailure(hostname, info);
  });
}

// The centered connect-failure card (idea #3056), styled like the session-complete card. Account-state
// problems (expired password, locked, disabled…) say so in plain words and offer a one-click "let IT
// know" (the same support-ticket path as the session-complete feedback box, pre-filled so the ticket
// names the problem). Try again re-mints from scratch, so a fix made meanwhile is picked up.
function rdpShowConnectFailure(hostname, info) {
  info = info || {};
  var host = hostname || RDP.host, target = RDP.target;
  rdHideConnecting();
  try { if (document.fullscreenElement) document.exitFullscreen().catch(function () { /* */ }); } catch { /* */ }
  rdSetState('Failed', 'bad');
  var screen = document.getElementById('rv-screen');
  if (!screen) { showToast(info.message || 'Remote connection failed.', 'danger'); return; }
  var icon = info.account ? 'fa-user-lock' : (info.accessDenied ? 'fa-ban' : 'fa-plug-circle-xmark');
  var canReport = (typeof submitRdpSessionFeedback === 'function');
  screen.innerHTML =
    '<div class="rdp-complete rdp-fail">' +
    '  <div class="rdp-complete-icon rdp-fail-icon"><i class="fa-solid ' + icon + '"></i></div>' +
    '  <div class="rdp-complete-title">' + rdEsc(info.title || 'Couldn’t connect') + '</div>' +
    '  <div class="rdp-complete-sub">' + rdEsc(info.message || 'Remote connection failed.') + '</div>' +
    (canReport ? '  <div class="rdp-complete-fb" id="rdp-fail-fb"></div>' : '') +
    '  <div class="rdp-complete-actions">' +
    (canReport ? '    <button type="button" class="rdp-connect-btn" id="rdp-fail-report"><i class="fa-solid fa-life-ring"></i> Let us know</button>' : '') +
    '    <button type="button" class="rdp-complete-back" id="rdp-fail-retry"><i class="fa-solid fa-rotate-right"></i> Try again</button>' +
    '    <button type="button" class="rdp-complete-back" id="rdp-fail-back"><i class="fa-solid fa-arrow-left"></i> Back to computers</button>' +
    '  </div>' +
    '</div>';
  var back = document.getElementById('rdp-fail-back');
  if (back) back.addEventListener('click', function () { closeRdpViewer(); });
  var retry = document.getElementById('rdp-fail-retry');
  if (retry) retry.addEventListener('click', function () { RDP.reconnectAttempts = 0; RDP.intentional = false; connectRdp(host, target); });
  var rep = document.getElementById('rdp-fail-report');
  if (rep) rep.addEventListener('click', function () {
    rep.disabled = true;
    var msg = 'Couldn’t connect to ' + host + ': ' + (info.title || 'connection failed') + (info.account ? (' (' + info.account + ')') : '') + '.';
    submitRdpSessionFeedback(host, target, msg, 'connect-failure').then(function (r) {
      var fb = document.getElementById('rdp-fail-fb');
      if (r && r.ok) {
        var ref = r.number ? (' (ref #' + rdEsc(String(r.number)) + ')') : '';
        if (fb) fb.innerHTML = '<div class="rdp-complete-thanks"><i class="fa-solid fa-circle-check"></i> Thanks — we’ve been notified' + ref + ' and will follow up.</div>';
        rep.classList.add('is-hidden');
      } else {
        showToast('Couldn’t send that — please try again or contact support.', 'danger');
        rep.disabled = false;
      }
    }).catch(function () { showToast('Couldn’t send that — please try again or contact support.', 'danger'); rep.disabled = false; });
  });
}

// The RDP run-loop ended (idea #1878). `info` (SessionTerminationInfo) is set when run() RESOLVED — a
// graceful end (Windows logoff / server disconnect / our own shutdown()); `err` is set when it REJECTED
// — a transport failure (network / WS / TLS drop). The resolve-vs-reject split is the transport-vs-logout
// signal we act on: a drop auto-reconnects, a graceful end shows the completion card.
function rdpOnEnded(endedSession, info, err) {
  // A reconnect-at-size (idea #1779) shuts the old session down while a new one is coming up; ignore the
  // old session's run()-resolution so it can't tear down the replacement.
  if (endedSession && RDP.session && RDP.session !== endedSession) return;
  if (RDP.reconnecting) return; // mid-reconnect: the new connect will re-establish state
  if (!RDP.session) return;
  // Transport drop (rejected) and not a deliberate close → keep host/target/cred and auto-reconnect.
  if (err && !RDP.intentional && RDP.host) {
    RDP.session = null; // this session object is dead; the reconnect builds a fresh one
    rdpScheduleReconnect(RDP.host);
    return;
  }
  // Graceful end (logoff / server disconnect / user Close) → completion card. `intentional` = user Close.
  RDP.session = null;
  var reason = '';
  try { if (info && typeof info.reason === 'function') reason = info.reason(); } catch { /* */ }
  try { if (typeof reportRdpDiag === 'function') reportRdpDiag(RDP.host, 'terminate', reason || 'ended'); } catch { /* */ }
  rdpShowSessionComplete({ variant: RDP.intentional ? 'closed' : 'ended' });
}

// Auto-reconnect after a transport drop (idea #1878). Backoff is
// 1500ms × attempt; after MAX_RDP_RECONNECT consecutive failures we give up and show the "connection
// lost" completion card (which offers a manual Reconnect). Re-uses the stashed RDP.cred for a zero-click
// re-auth via mintThenConnect; the isReconnect meta makes doConnectRdp's failure path re-schedule here.
function rdpScheduleReconnect(hostname) {
  if (RDP.intentional || RDP.host !== hostname) return; // closed / superseded mid-drop
  rdpStopClipPoll(); // the dead session's poll (its canvas is gone) — the reconnect re-wires it
  if (RDP.onResize) { try { window.removeEventListener('resize', RDP.onResize); } catch { /* */ } RDP.onResize = null; }
  RDP.reconnectAttempts += 1;
  if (RDP.reconnectAttempts > MAX_RDP_RECONNECT) { RDP.reconnecting = false; rdpShowSessionComplete({ variant: 'lost' }); return; }
  RDP.reconnecting = true;
  rdSetState('Reconnecting…', '');
  rdShowConnecting(hostname, 'Reconnecting… (' + RDP.reconnectAttempts + ')');
  if (RDP.reconnectTimer) { try { clearTimeout(RDP.reconnectTimer); } catch { /* */ } }
  RDP.reconnectTimer = setTimeout(function () {
    RDP.reconnectTimer = null;
    if (RDP.intentional || RDP.host !== hostname) { RDP.reconnecting = false; return; }
    if (RDP.cred) mintThenConnect(hostname, RDP.target, RDP.cred, { userEntered: false, isReconnect: true });
    else { RDP.reconnecting = false; connectRdp(hostname, RDP.target); } // no stashed cred → normal (re)prompt
  }, 1500 * RDP.reconnectAttempts);
}

// Full screen (idea #1779) — RDP path only. Toggle OS fullscreen on the whole viewer overlay. The
// resolution auto-resize is free: entering/exiting fullscreen changes #rv-screen's bounds exactly
// like a window resize, and onRemoteFullscreenChange kicks the existing debounced RDP.onResize so the
// remote desktop renegotiates via the Display Control channel (RDP.session.resize()).
function toggleRemoteFullscreen() {
  var ov = document.getElementById('remote-viewer');
  if (!ov) return;
  if (!document.fullscreenElement) {
    if (ov.requestFullscreen) ov.requestFullscreen().catch(function () { /* denied/unsupported */ });
  } else if (document.exitFullscreen) {
    document.exitFullscreen().catch(function () { /* */ });
  }
}

// Make the remote desktop fill a NEW size (idea #1779). These kiosk/alt-shell hosts don't open the RDP
// Display Control Virtual Channel, so live session.resize() is a silent no-op — but the initial
// resolution IS honored at connect time. So to resize we RECONNECT at the new size: shut the current
// session down and re-mint+re-connect, which resumes the same Windows session at the current
// #rv-screen dimensions. Used on fullscreen enter/exit only (a discrete action) — NEVER on window-drag.
// Guards: only when connected, creds stashed, and not already mid-reconnect. Double-rAF so the
// fullscreen transition has re-laid-out #rv-screen before doConnectRdp measures it (network mint
// latency also covers this, but the rAF makes it deterministic).
function rdpReconnectAtSize() {
  if (!RDP.session || !RDP.cred || RDP.reconnecting) return;
  RDP.reconnecting = true;
  var host = RDP.host, target = RDP.target, cred = RDP.cred;
  var old = RDP.session;
  rdpReleaseHeldKeys();  // the new session starts with a clean keyboard, not this one's held keys (#2258)
  RDP.session = null;
  rdpStopClipPoll(); // stop the local→remote clipboard poll; the new connect re-wires it (idea #1866)
  if (RDP.onResize) { try { window.removeEventListener('resize', RDP.onResize); } catch { /* */ } RDP.onResize = null; }
  try { old.shutdown(); } catch { /* */ }
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      if (RDP.host !== host) { RDP.reconnecting = false; return; } // closed mid-transition
      mintThenConnect(host, target, cred, { userEntered: false });
    });
  });
}

// Fires on BOTH enter and exit — including when the user leaves fullscreen via the browser's own
// chrome — so it's the single source of truth for fullscreen state. Registered once in bootPortal().
function onRemoteFullscreenChange() {
  var full = !!document.fullscreenElement;
  var ov = document.getElementById('remote-viewer');
  if (ov) ov.classList.toggle('rv-fullscreen', full);
  var icon = document.querySelector('.rv-fullscreen-btn i');
  if (icon) icon.className = full ? 'fa-solid fa-compress' : 'fa-solid fa-expand';
  var btn = document.querySelector('.rv-fullscreen-btn');
  if (btn) btn.setAttribute('data-cnstooltip', full ? 'Exit full screen' : 'Full screen');
  // Capture system key chords for the remote while full screen (released on exit). Feature-detected;
  // requires a secure context and may be denied — never fatal. NOTE: with Escape locked, the browser
  // no longer single-press-exits fullscreen — the toolbar button / banner Exit are the exit paths.
  try {
    if (navigator.keyboard && typeof navigator.keyboard.lock === 'function') {
      if (full) navigator.keyboard.lock(['Escape', 'Tab', 'MetaLeft', 'MetaRight', 'AltLeft', 'AltRight']);
      else if (typeof navigator.keyboard.unlock === 'function') navigator.keyboard.unlock();
    }
  } catch { /* keyboard lock unsupported/denied — non-fatal */ }
  // A fullscreen transition moves focus and eats the keyup of whatever triggered it (F11, or the Escape
  // that leaves fullscreen), so release anything held and take the keyboard back (idea #2258).
  rdpReleaseHeldKeys();
  rdpFocusCanvas();
  // Make the remote fill the new size by reconnecting at it (live resize isn't supported by these
  // hosts — see rdpReconnectAtSize). Keeps keystrokes flowing to the canvas once it re-establishes.
  if (typeof RDP !== 'undefined' && RDP && RDP.session) {
    rdpReconnectAtSize();
  }
}

// Assemble the streamed virtual-printer chunks (already PDF bytes — we request the "Microsoft Print
// to PDF" driver) into a Blob and hand the user a printable PDF. Opens the browser's PDF viewer in a
// new tab (from which they Ctrl+P to their real local printer); falls back to a download if the tab
// is blocked. Fired from the RDP print-job callback when the user prints inside the session (#1655).
function deliverRdpPrintPdf(chunks) {
  if (!chunks || !chunks.length) return;
  var total = 0;
  for (var i = 0; i < chunks.length; i++) total += chunks[i].length;
  if (!total) return;
  var buf = new Uint8Array(total), off = 0;
  for (var j = 0; j < chunks.length; j++) { buf.set(chunks[j], off); off += chunks[j].length; }
  var url;
  try { url = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' })); }
  catch (e) { showToast('Could not prepare the print output.', 'danger'); return; }
  var opened = null;
  try { opened = window.open(url, '_blank'); } catch (e) { opened = null; }
  if (!opened) {
    try {
      var a = document.createElement('a');
      a.href = url; a.download = 'print-' + Date.now() + '.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e) { /* */ }
  }
  try { showToast('Your print is ready as a PDF — print it to your local printer.', 'success'); } catch (e) { /* */ }
  setTimeout(function () { try { URL.revokeObjectURL(url); } catch (e) { /* */ } }, 60000);
}

// The Close BUTTON (idea #1878). Unlike closeRdpViewer (the final teardown-to-list), this shows the
// completion card first — the user chose to end, so they get the same thank-you + optional feedback
// as a server-side logoff. If nothing was ever connected (they hit Close at the sign-in prompt) there's
// nothing to thank them for, so tear straight down to the list. Renders the card immediately rather
// than waiting on the async run()-resolve from shutdown(), so it's deterministic.
function rdpEndSession() {
  // No live session → nothing to thank them for: closing at the sign-in prompt, or while a completion
  // card / "Reconnecting…" overlay is already showing → go straight back to the computer list.
  if (!RDP.session) { closeRdpViewer(); return; }
  RDP.intentional = true;
  if (RDP.reconnectTimer) { try { clearTimeout(RDP.reconnectTimer); } catch { /* */ } RDP.reconnectTimer = null; }
  RDP.reconnecting = false;
  rdpStopClipPoll();
  var s = RDP.session; RDP.session = null;
  if (RDP.onResize) { try { window.removeEventListener('resize', RDP.onResize); } catch { /* */ } RDP.onResize = null; }
  if (s) { try { s.shutdown(); } catch { /* */ } }
  rdpShowSessionComplete({ variant: 'closed' });
}

// The centered "session complete" card (idea #1878). Rendered into #rv-screen (like the sign-in prompt)
// after the session has ended. variant: 'ended' (server logoff/disconnect), 'closed' (user hit Close),
// 'lost' (auto-reconnect gave up). Offers an optional issue/request box (creates a support ticket +
// logs a beacon), a "don't ask again" toggle, and a Back-to-computers button (the real teardown). The
// 'lost' variant also offers a manual Reconnect.
function rdpShowSessionComplete(opts) {
  opts = opts || {};
  var variant = opts.variant || 'ended';
  var host = RDP.host, target = RDP.target;
  if (RDP.reconnectTimer) { try { clearTimeout(RDP.reconnectTimer); } catch { /* */ } RDP.reconnectTimer = null; }
  RDP.reconnecting = false;
  rdpStopClipPoll();
  rdpKeyDiagFlush(host);      // one-shot keyboard counters for this session (idea #2258)
  rdpUpdateKbdHint();         // no session → no "click to type" chip over the completion card
  try { if (typeof rdpStatsStop === 'function') rdpStatsStop(); } catch { /* */ } // perf HUD (#2083)
  rdHideConnecting();
  // Leave OS fullscreen so the card isn't hidden behind an immersive canvas (idea #1779 handler also
  // releases any keyboard lock). Guard: exitFullscreen throws if not currently fullscreen.
  try { if (document.fullscreenElement) document.exitFullscreen().catch(function () { /* */ }); } catch { /* */ }
  var lost = (variant === 'lost');
  rdSetState(lost ? 'Connection lost' : 'Session ended', lost ? 'bad' : '');
  var screen = document.getElementById('rv-screen');
  if (!screen) return;
  var icon = lost ? 'fa-plug-circle-xmark' : 'fa-circle-check';
  var title = lost ? 'Connection lost' : 'Your remote session has ended';
  var sub = lost
    ? ('We couldn’t stay connected to ' + rdEsc(host || 'the computer') + '. You can try again, or head back to your computers.')
    : 'Thanks for using remote access.';
  var suppressed = rdpFeedbackSuppressed();
  var fbBlock = suppressed ? '' : (
    '<div class="rdp-complete-fb" id="rdp-complete-fb">' +
    '  <label class="rdp-complete-fb-label" for="rdp-fb-msg">Any issues or requests about this session? Let us know and we’ll follow up.</label>' +
    '  <textarea id="rdp-fb-msg" class="rdp-complete-fb-input" rows="3" placeholder="Optional — describe anything that went wrong or that you need."></textarea>' +
    '  <div class="rdp-complete-fb-row">' +
    '    <button type="button" class="rdp-connect-btn rdp-complete-send" id="rdp-fb-send"><i class="fa-solid fa-paper-plane"></i> Send to support</button>' +
    '    <label class="rdp-complete-noask"><input type="checkbox" id="rdp-fb-noask"> Don’t ask me again</label>' +
    '  </div>' +
    '</div>'
  );
  screen.innerHTML =
    '<div class="rdp-complete">' +
    '  <div class="rdp-complete-icon"><i class="fa-solid ' + icon + '"></i></div>' +
    '  <div class="rdp-complete-title">' + rdEsc(title) + '</div>' +
    '  <div class="rdp-complete-sub">' + sub + '</div>' +
    fbBlock +
    '  <div class="rdp-complete-actions">' +
    (lost ? '<button type="button" class="rdp-connect-btn rdp-complete-reconnect" id="rdp-complete-reconnect"><i class="fa-solid fa-rotate-right"></i> Reconnect</button>' : '') +
    '    <button type="button" class="rdp-complete-back" id="rdp-complete-back"><i class="fa-solid fa-arrow-left"></i> Back to computers</button>' +
    '  </div>' +
    '</div>';
  var backBtn = document.getElementById('rdp-complete-back');
  if (backBtn) backBtn.addEventListener('click', function () { closeRdpViewer(); });
  var reBtn = document.getElementById('rdp-complete-reconnect');
  if (reBtn) reBtn.addEventListener('click', function () {
    RDP.reconnectAttempts = 0; RDP.intentional = false;
    rdShowConnecting(host);
    if (RDP.cred) mintThenConnect(host, target, RDP.cred, { userEntered: false, isReconnect: true });
    else connectRdp(host, target);
  });
  var noask = document.getElementById('rdp-fb-noask');
  if (noask) noask.addEventListener('change', function () { rdpSetFeedbackSuppressed(!!noask.checked); });
  var sendBtn = document.getElementById('rdp-fb-send');
  if (sendBtn) sendBtn.addEventListener('click', function () {
    var msg = ((document.getElementById('rdp-fb-msg') || {}).value || '').trim();
    if (!msg) { showToast('Type a quick note first, or just head back.', 'warning'); return; }
    sendBtn.disabled = true;
    if (typeof submitRdpSessionFeedback !== 'function') { showToast('Couldn’t send right now.', 'danger'); sendBtn.disabled = false; return; }
    submitRdpSessionFeedback(host, target, msg).then(function (r) {
      var fb = document.getElementById('rdp-complete-fb');
      if (r && r.ok) {
        var ref = r.number ? (' (ref #' + rdEsc(String(r.number)) + ')') : '';
        if (fb) fb.innerHTML = '<div class="rdp-complete-thanks"><i class="fa-solid fa-circle-check"></i> Thanks — your request was logged' + ref + '. We’ll follow up.</div>';
      } else {
        showToast('Couldn’t send that — please try again or contact support.', 'danger');
        sendBtn.disabled = false;
      }
    }).catch(function () { showToast('Couldn’t send that — please try again or contact support.', 'danger'); sendBtn.disabled = false; });
  });
}

// List-level "Report a problem" (idea #1878) — the same purpose as the session-complete feedback box,
// but reachable any time from the top of the computer list (no active/just-ended session needed). Opens
// a small modal (reusing the shared cm-* modal classes) with a message box; posts to the same feedback
// endpoint (native-PSA ticket + log). Not gated by the "don't ask again" toggle — it's an explicit action.
function openRdpReportModal() {
  var root = document.getElementById('modal-root');
  if (!root) return;
  var backdrop = document.createElement('div');
  backdrop.className = 'cm-backdrop cm-type-info';
  var dialog = document.createElement('div');
  dialog.className = 'cm-dialog';
  dialog.innerHTML =
    '<div class="cm-header"><div class="cm-title">Report a problem</div></div>' +
    '<div class="cm-body cm-body-form">' +
    '  <label class="rdp-report-label" for="rdp-report-msg">Tell us about any issue or request with remote access and we’ll follow up.</label>' +
    '  <textarea id="rdp-report-msg" class="rdp-complete-fb-input" rows="4" placeholder="Describe what went wrong or what you need."></textarea>' +
    '</div>' +
    '<div class="cm-footer"></div>';
  var footer = dialog.querySelector('.cm-footer');
  var cancel = document.createElement('button');
  cancel.className = 'cm-btn cm-btn-secondary'; cancel.textContent = 'Cancel';
  var send = document.createElement('button');
  send.className = 'cm-btn cm-btn-primary'; send.textContent = 'Send to support';
  footer.appendChild(cancel); footer.appendChild(send);
  function close() { backdrop.classList.remove('show'); setTimeout(function () { try { backdrop.remove(); } catch { /* */ } }, 200); }
  cancel.addEventListener('click', close);
  send.addEventListener('click', function () {
    var msg = ((document.getElementById('rdp-report-msg') || {}).value || '').trim();
    if (!msg) { showToast('Type a quick description first.', 'warning'); return; }
    if (typeof submitRdpSessionFeedback !== 'function') { showToast('Couldn’t send right now.', 'danger'); return; }
    send.disabled = true;
    submitRdpSessionFeedback('', '', msg).then(function (r) {
      if (r && r.ok) {
        var ref = r.number ? (' (ref #' + String(r.number) + ')') : '';
        showToast('Thanks — your report was logged' + ref + '. We’ll follow up.', 'success');
        close();
      } else { showToast('Couldn’t send that — please try again or contact support.', 'danger'); send.disabled = false; }
    }).catch(function () { showToast('Couldn’t send that — please try again or contact support.', 'danger'); send.disabled = false; });
  });
  backdrop.appendChild(dialog);
  root.appendChild(backdrop);
  requestAnimationFrame(function () { backdrop.classList.add('show'); });
  var ta = document.getElementById('rdp-report-msg'); if (ta) try { ta.focus(); } catch { /* */ }
}

function closeRdpViewer() {
  var s = RDP.session;
  rdpKeyDiagFlush(RDP.host);  // covers the paths that skip the completion card (idea #2258)
  rdpReleaseHeldKeys();
  RDP.session = null;
  rdpUpdateKbdHint();
  RDP.host = null;
  RDP.target = null;
  RDP.targetName = null;
  RDP.cred = null;            // drop stashed reconnect credential (idea #1779)
  RDP.reconnecting = false;
  RDP.intentional = false;    // reset disconnect-UX flags (idea #1878)
  RDP.everConnected = false;
  RDP.reconnectAttempts = 0;
  if (RDP.reconnectTimer) { try { clearTimeout(RDP.reconnectTimer); } catch { /* */ } RDP.reconnectTimer = null; }
  rdpStopClipPoll();          // stop the clipboard poll (idea #1866)
  rdpAudioStop();             // release the remote-audio AudioContext (idea #1897)
  try { if (typeof rdpStatsStop === 'function') rdpStatsStop(); } catch { /* */ } // stop the perf HUD (idea #2083)
  if (RDP._zoomTimer) { try { clearTimeout(RDP._zoomTimer); } catch { /* */ } RDP._zoomTimer = null; } // cancel a pending zoom reconnect
  RDP.clipSig = null;         // don't leak one session's clipboard signature into the next
  RDP.clipSuppress = false;
  if (RDP.onResize) { try { window.removeEventListener('resize', RDP.onResize); } catch (e) { /* */ } RDP.onResize = null; }
  // Leave OS fullscreen when the session ends (idea #1779) — also releases any keyboard lock via the
  // fullscreenchange handler. Guard: exitFullscreen throws if not currently fullscreen.
  try { if (document.fullscreenElement) document.exitFullscreen().catch(function () { /* */ }); } catch { /* */ }
  if (s) { try { s.shutdown(); } catch { /* */ } }
  var ov = document.getElementById('remote-viewer');
  if (ov) { ov.classList.add('is-hidden'); ov.classList.remove('rv-rdp'); ov.setAttribute('aria-hidden', 'true'); }
  var screen = document.getElementById('rv-screen');
  if (screen) screen.innerHTML = '';
  if (typeof parseHash === 'function' && parseHash().path === 'remote' && typeof loadRemoteStations === 'function') loadRemoteStations();
}
;

// ========== rdpAudio.js ==========
// clientPortal/src/js/rdpAudio.js — RDP remote-audio playback (RDPSND / MS-RDPEA, idea #1897).
//
// Plays the REMOTE desktop's sound through the browser's speakers. Speaker-only — there is no
// microphone (browser→remote) path. The vendored ironrdp-web build (once rebuilt with the real
// RDPSND backend — see clientPortal/.gitea/workflows/ironrdp-wasm.yml) invokes the callbacks we
// register on the SessionBuilder via Extension('audio_stream_callbacks', …):
//   onFormat(sampleRate, channels, bitsPerSample) — the negotiated PCM format (we advertise
//     WAVE_FORMAT_PCM only, so this is always 16-bit LE PCM).
//   onData(pcmChunk) — a WAVE PDU of raw PCM samples (a Uint8Array VIEW into WASM memory that is
//     reused after the call, so we copy/convert immediately).
//
// We convert each chunk to a Web Audio AudioBuffer and SCHEDULE it sequentially against the
// AudioContext clock (a running `nextTime` cursor) so playback is gapless; a small jitter margin
// absorbs network variance and a drift-guard resyncs if we ever fall behind. Mute rides a GainNode
// (we keep decoding while muted so unmute is instant) and its preference persists in localStorage.
//
// Every entry point is guarded so an audio failure can never break the RDP session; if the WASM
// build predates the audio backend, the Extension is simply ignored and this stays a silent no-op.

var RDP_AUDIO = { ctx: null, gain: null, muted: false, fmt: null, nextTime: 0 };

// Lead time (s) we schedule ahead of the AudioContext clock so back-to-back chunks never underrun.
var RDP_AUDIO_JITTER = 0.08;

function rdpAudioMutedPref() {
  try { return localStorage.getItem('rdp_audio_muted') === '1'; } catch { return false; }
}
function rdpAudioSetMutedPref(on) {
  try { if (on) localStorage.setItem('rdp_audio_muted', '1'); else localStorage.removeItem('rdp_audio_muted'); } catch { /* */ }
}

// Create/resume the AudioContext. Call it from a user-gesture path (the connect click) so the
// browser autoplay policy grants "sticky activation" and playback is allowed when audio arrives.
function rdpAudioStart() {
  try {
    if (!RDP_AUDIO.ctx) {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return; // no Web Audio → silent no-op
      RDP_AUDIO.ctx = new AC();
      RDP_AUDIO.gain = RDP_AUDIO.ctx.createGain();
      RDP_AUDIO.gain.connect(RDP_AUDIO.ctx.destination);
    }
    if (RDP_AUDIO.ctx.state === 'suspended') { try { RDP_AUDIO.ctx.resume(); } catch { /* */ } }
    RDP_AUDIO.muted = rdpAudioMutedPref();
    RDP_AUDIO.gain.gain.value = RDP_AUDIO.muted ? 0 : 1;
    RDP_AUDIO.nextTime = 0; // (re)prime the schedule cursor for a fresh/reconnected session
    rdpAudioSyncButton();
  } catch { /* */ }
}

// The WASM backend negotiated the audio format (PCM). Reset the schedule cursor for the new stream.
function rdpAudioOnFormat(rate, channels, bits) {
  RDP_AUDIO.fmt = { rate: rate || 44100, channels: channels || 2, bits: bits || 16 };
  RDP_AUDIO.nextTime = 0;
}

// A WAVE PDU of 16-bit LE PCM. Convert to float per channel, build an AudioBuffer, schedule it.
function rdpAudioOnData(chunk) {
  try {
    var A = RDP_AUDIO;
    if (!A.ctx || !A.gain || !A.fmt) return;
    if (A.ctx.state === 'suspended') { try { A.ctx.resume(); } catch { /* */ } }
    var bits = A.fmt.bits, ch = A.fmt.channels || 1, rate = A.fmt.rate || 44100;
    if (bits !== 16 || ch < 1) return; // PCM-only contract (backend advertises WAVE_FORMAT_PCM 16-bit)
    var bytes = (chunk instanceof Uint8Array) ? chunk : new Uint8Array(chunk);
    var bytesPerFrame = 2 * ch;
    var frames = Math.floor(bytes.byteLength / bytesPerFrame);
    if (frames <= 0) return;
    var buf = A.ctx.createBuffer(ch, frames, rate);
    // DataView over the exact sample region — bytes is a view into WASM memory, so read it now.
    var dv = new DataView(bytes.buffer, bytes.byteOffset, frames * bytesPerFrame);
    for (var c = 0; c < ch; c++) {
      var out = buf.getChannelData(c);
      for (var i = 0; i < frames; i++) {
        var s = dv.getInt16((i * ch + c) * 2, true); // interleaved, little-endian
        out[i] = s < 0 ? s / 0x8000 : s / 0x7fff;
      }
    }
    var src = A.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(A.gain);
    var now = A.ctx.currentTime;
    // Prime, or resync if we've drifted behind real time (underrun) — never schedule in the past.
    if (A.nextTime < now + 0.01) A.nextTime = now + RDP_AUDIO_JITTER;
    src.start(A.nextTime);
    A.nextTime += buf.duration;
  } catch { /* */ }
}

// Tear the context down (Close / viewer teardown). Kept ALIVE across reconnects — only closed here.
function rdpAudioStop() {
  try {
    if (RDP_AUDIO.ctx) { try { RDP_AUDIO.ctx.close(); } catch { /* */ } }
  } catch { /* */ }
  RDP_AUDIO.ctx = null; RDP_AUDIO.gain = null; RDP_AUDIO.fmt = null; RDP_AUDIO.nextTime = 0;
}

// Toolbar mute toggle. Rides the GainNode (decoding continues) so unmute is instant; persisted.
function rdpAudioToggleMute() {
  var A = RDP_AUDIO;
  A.muted = !A.muted;
  rdpAudioSetMutedPref(A.muted);
  try { if (A.gain) A.gain.gain.value = A.muted ? 0 : 1; } catch { /* */ }
  if (!A.muted && A.ctx && A.ctx.state === 'suspended') { try { A.ctx.resume(); } catch { /* */ } }
  rdpAudioSyncButton();
}

// Reflect mute state on EVERY mute button (the toolbar one AND the full-screen banner one) — icon +
// tooltip + muted class. No-op if none are mounted.
function rdpAudioSyncButton() {
  var muted = RDP_AUDIO.muted;
  var btns = document.querySelectorAll('.rv-mute-btn');
  for (var i = 0; i < btns.length; i++) {
    var btn = btns[i];
    var icon = btn.querySelector('i');
    if (icon) icon.className = muted ? 'fa-solid fa-volume-xmark' : 'fa-solid fa-volume-high';
    btn.setAttribute('data-cnstooltip', muted ? 'Unmute remote sound' : 'Mute remote sound');
    btn.classList.toggle('rv-muted', muted);
  }
}
;

// ========== rdpBanner.js ==========
// clientPortal/src/js/rdpBanner.js — draggable full-screen session pill (idea #2347).
//
// In OS full screen the viewer's docked toolbar (.rv-bar) is hidden and the ONLY chrome left is the
// top-center pill #rv-session-banner (idea #1779): "Remote session · host" + zoom / mute / Exit /
// Close. It was CSS-pinned to top-center, which is exactly where a Windows title bar, ribbon or app
// menu lives — so it permanently covered the part of the remote desktop the user needed. This module
// lets them drag it out of the way.
//
// Mechanics worth knowing:
//   * POINTER CAPTURE is not optional. The RDP canvas underneath swallows pointermove aggressively;
//     without setPointerCapture on the handle the pill stops following the cursor the moment it
//     leaves the pill.
//   * The position persists as a FRACTION of the stage, not pixels. Fullscreen enter/exit and every
//     zoom step reconnect the session at a new resolution (rdpReconnectAtSize), so pixel coordinates
//     would drift; fractions keep the pill where the user visually put it, and the clamp keeps it
//     on-screen regardless.
//   * Dragging must not cost the user their keyboard. pointerdown is preventDefault()ed (so focus
//     never leaves the canvas) and rdpFocusCanvas() runs on release as a belt-and-braces — a
//     silently detached keyboard is the exact failure mode idea #2258 exists for.

var RDP_BANNER_POS_KEY = 'rdp_banner_pos';
var RDP_BANNER_MARGIN = 8;   // px kept between the pill and the edge of the stage
var RDP_BANNER_THRESHOLD = 4; // px of movement before a press counts as a drag (so taps stay taps)

// Live drag state. Null whenever no drag is in flight.
var RDP_BANNER_DRAG = null;

// Stored position as {x, y} fractions (0..1) of the stage, addressing the pill's CENTER.
// Returns null when the user has never moved it (the CSS default, top-center, then applies).
function rdpBannerPosGet() {
  try {
    var raw = localStorage.getItem(RDP_BANNER_POS_KEY);
    if (!raw) return null;
    var p = JSON.parse(raw);
    if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') return null;
    if (!isFinite(p.x) || !isFinite(p.y)) return null;
    return { x: Math.max(0, Math.min(1, p.x)), y: Math.max(0, Math.min(1, p.y)) };
  } catch { return null; }
}

function rdpBannerPosSet(x, y) {
  try { localStorage.setItem(RDP_BANNER_POS_KEY, JSON.stringify({ x: x, y: y })); } catch { /* storage off */ }
}

function rdpBannerPosClear() {
  try { localStorage.removeItem(RDP_BANNER_POS_KEY); } catch { /* */ }
}

// The two elements this module moves things within. Either may be absent (the viewer markup only
// exists on the portal shell), so every caller null-checks.
function rdpBannerEl() { return document.getElementById('rv-session-banner'); }
function rdpBannerStage() {
  var b = rdpBannerEl();
  return b ? b.closest('.rv-stage') : null;
}

// A hidden element has a 0x0 rect, so any clamp computed against it is garbage. The pill is
// display:none outside full screen — bail until it's actually on screen (rdpBannerApply re-runs on
// fullscreenchange, which is when it appears).
function rdpBannerVisible(el) {
  if (!el) return false;
  if (el.offsetParent === null) return false;
  return el.offsetWidth > 0 && el.offsetHeight > 0;
}

// Turn center-fractions into a clamped top-left px offset inside the stage. Falls back to pinning at
// the margin when the pill is wider/taller than the stage (tiny viewports), which beats a negative
// offset that would push half the controls off-screen.
function rdpBannerClampPx(stageW, stageH, w, h, cx, cy) {
  var m = RDP_BANNER_MARGIN;
  var maxL = stageW - w - m, maxT = stageH - h - m;
  var left = cx * stageW - w / 2;
  var top = cy * stageH - h / 2;
  left = (maxL < m) ? m : Math.max(m, Math.min(maxL, left));
  top = (maxT < m) ? m : Math.max(m, Math.min(maxT, top));
  return { left: left, top: top };
}

// Apply the stored position (or restore the CSS default when there isn't one). Safe to call at any
// time: it no-ops while the pill is hidden and re-clamps whenever the stage has changed size.
function rdpBannerApply() {
  var el = rdpBannerEl();
  if (!el) return;
  var pos = rdpBannerPosGet();
  if (!pos) {
    // Back to the stylesheet's top-center pin.
    el.classList.remove('rv-banner-moved');
    el.style.left = '';
    el.style.top = '';
    return;
  }
  if (!rdpBannerVisible(el)) return; // not in full screen yet — re-applied on fullscreenchange
  var stage = rdpBannerStage();
  if (!stage) return;
  var sr = stage.getBoundingClientRect();
  var br = el.getBoundingClientRect();
  var p = rdpBannerClampPx(sr.width, sr.height, br.width, br.height, pos.x, pos.y);
  el.classList.add('rv-banner-moved'); // drops the translateX(-50%) centring so left/top win
  el.style.left = Math.round(p.left) + 'px';
  el.style.top = Math.round(p.top) + 'px';
}

// Put it back where it started (double-click the handle). Clearing the pref rather than storing
// "0.5, top" means a future CSS change to the default position is still honoured.
function rdpBannerRecentre() {
  rdpBannerPosClear();
  rdpBannerApply();
}

function rdpBannerOnPointerDown(e) {
  var handle = e.target && e.target.closest ? e.target.closest('.rv-session-drag') : null;
  if (!handle) return;
  if (e.button != null && e.button !== 0) return; // primary press only (right-click stays a right-click)
  var el = rdpBannerEl();
  var stage = rdpBannerStage();
  if (!el || !stage || !rdpBannerVisible(el)) return;
  var br = el.getBoundingClientRect();
  var sr = stage.getBoundingClientRect();
  RDP_BANNER_DRAG = {
    id: e.pointerId,
    handle: handle,
    startX: e.clientX,
    startY: e.clientY,
    // Where the pill's top-left sits inside the stage right now — including while it's still
    // translateX-centred, so taking over positioning can't make it jump.
    baseLeft: br.left - sr.left,
    baseTop: br.top - sr.top,
    w: br.width,
    h: br.height,
    stageW: sr.width,
    stageH: sr.height,
    moved: false
  };
  try { handle.setPointerCapture(e.pointerId); } catch { /* capture unsupported — drag still mostly works */ }
  // Keeps the press from selecting the label text AND from moving focus off the RDP canvas.
  e.preventDefault();
}

function rdpBannerOnPointerMove(e) {
  var d = RDP_BANNER_DRAG;
  if (!d || e.pointerId !== d.id) return;
  var dx = e.clientX - d.startX, dy = e.clientY - d.startY;
  if (!d.moved) {
    if (Math.abs(dx) < RDP_BANNER_THRESHOLD && Math.abs(dy) < RDP_BANNER_THRESHOLD) return;
    d.moved = true;
    var el0 = rdpBannerEl();
    if (el0) {
      el0.classList.add('rv-banner-moved', 'rv-banner-dragging');
      el0.style.left = Math.round(d.baseLeft) + 'px';
      el0.style.top = Math.round(d.baseTop) + 'px';
    }
  }
  var el = rdpBannerEl();
  if (!el) return;
  var m = RDP_BANNER_MARGIN;
  var maxL = d.stageW - d.w - m, maxT = d.stageH - d.h - m;
  var left = d.baseLeft + dx, top = d.baseTop + dy;
  left = (maxL < m) ? m : Math.max(m, Math.min(maxL, left));
  top = (maxT < m) ? m : Math.max(m, Math.min(maxT, top));
  el.style.left = Math.round(left) + 'px';
  el.style.top = Math.round(top) + 'px';
  e.preventDefault();
}

function rdpBannerOnPointerUp(e) {
  var d = RDP_BANNER_DRAG;
  if (!d || (e.pointerId != null && e.pointerId !== d.id)) return;
  RDP_BANNER_DRAG = null;
  try { d.handle.releasePointerCapture(e.pointerId); } catch { /* */ }
  var el = rdpBannerEl();
  if (el) el.classList.remove('rv-banner-dragging');
  if (d.moved && el && d.stageW > 0 && d.stageH > 0) {
    // Persist as center-fractions so the pill survives the resolution changes a zoom/fullscreen
    // reconnect brings with it.
    var br = el.getBoundingClientRect();
    var stage = rdpBannerStage();
    var sr = stage ? stage.getBoundingClientRect() : null;
    if (sr && sr.width > 0 && sr.height > 0) {
      rdpBannerPosSet((br.left - sr.left + br.width / 2) / sr.width,
        (br.top - sr.top + br.height / 2) / sr.height);
    }
  }
  // Hand the keyboard back to the session (idea #2258) — a drag must never leave it detached.
  if (typeof rdpFocusCanvas === 'function') rdpFocusCanvas();
}

function rdpBannerOnDblClick(e) {
  if (!e.target || !e.target.closest || !e.target.closest('.rv-session-drag')) return;
  rdpBannerRecentre();
  if (typeof rdpFocusCanvas === 'function') rdpFocusCanvas();
}

// Registered once from bootPortal(). The pill is static markup in the shell, so listeners can be
// bound at boot; pointermove/up go on the document because pointer capture retargets events to the
// capturing element's document when the pointer leaves it.
function rdpBannerInit() {
  var el = rdpBannerEl();
  if (!el) return;
  el.addEventListener('pointerdown', rdpBannerOnPointerDown);
  el.addEventListener('dblclick', rdpBannerOnDblClick);
  document.addEventListener('pointermove', rdpBannerOnPointerMove);
  document.addEventListener('pointerup', rdpBannerOnPointerUp);
  document.addEventListener('pointercancel', rdpBannerOnPointerUp);
  // The pill only becomes measurable when full screen reveals it, and the stage resizes on every
  // fullscreen transition / window resize — re-clamp on both so a stored spot can never strand it
  // off-screen. rAF lets the fullscreen relayout land before we measure.
  document.addEventListener('fullscreenchange', function () {
    requestAnimationFrame(rdpBannerApply);
  });
  var t = null;
  window.addEventListener('resize', function () {
    if (t) clearTimeout(t);
    t = setTimeout(function () { t = null; rdpBannerApply(); }, 150);
  });
  rdpBannerApply();
}
;

// ========== rdpQuality.js ==========
// clientPortal/src/js/rdpQuality.js — remote-desktop quality profile (idea #2083).
//
// The ONE control that trades visual fidelity for speed. It is deliberately separate from the zoom
// buttons (rdpZoom.js): zoom answers "how big do you want things to look", quality answers "how much
// work should the remote and your browser do to draw it". They compose — the cap is applied to the
// already-zoomed viewport size.
//
// Each profile carries TWO levers:
//
//   cap  — the maximum negotiated desktop size. Every pixel above this costs bandwidth on the wire,
//          decode time in the WASM and paint time in the canvas, all three at once. A portal user on a
//          4K monitor was previously negotiating a 3840x2160 desktop by default, which is ~4x the
//          pixels of 1080p for no readability gain once the canvas is scaled to fit anyway.
//
//   perf — the RDP performance flags (MS-RDPBCGR TS_UI_INFO.performanceFlags), i.e. the "hide the
//          wallpaper, drop the theming, stop animating menus" knobs that mstsc exposes under
//          Experience. NOTE: these only take effect once the vendored ironrdp-web build carries the
//          performance-flags patch; until then rdp.js's Extension call is ignored by the older WASM and
//          only `cap` bites. That is why the values live here and not in the Rust patch — one control,
//          picked by the user, rather than a hardcoded build-time decision.
//
// Persisted in localStorage so it survives a refresh and applies to the very first connect. Changing it
// reconnects at the new size via the same debounced path zoom uses (the resolution is only honoured at
// CONNECT time — the kiosk/alt-shell hosts never open the Display Control channel).

// MS-RDPBCGR 2.2.1.11.1.1.1 TS_UI_INFO performanceFlags bits.
var RDP_PERF_DISABLE_WALLPAPER = 0x01;
var RDP_PERF_DISABLE_FULLWINDOWDRAG = 0x02;
var RDP_PERF_DISABLE_MENUANIMATIONS = 0x04;
var RDP_PERF_DISABLE_THEMING = 0x08;
var RDP_PERF_DISABLE_CURSOR_SHADOW = 0x20;
var RDP_PERF_DISABLE_CURSORSETTINGS = 0x40;
var RDP_PERF_ENABLE_FONT_SMOOTHING = 0x80;

// Only LOW caps the resolution. Medium and High negotiate the desktop the browser actually asks for —
// which is already the DPI-EFFECTIVE size, not the panel's physical pixel count (see rdpViewportSize).
// Capping medium/high was fighting a problem that mostly doesn't exist: a 4K panel scaled to a 1080p
// effective desktop already reports ~1920x1080 here.
var RDP_QUALITY_PROFILES = [
  {
    id: 'low',
    label: 'Low',
    tip: 'Low — 720p, plain background, no theming. Best on a slow or busy connection.',
    cap: { w: 1280, h: 720 },
    // Everything off, including ClearType font smoothing (it ships subpixel glyph data over the wire).
    perf: RDP_PERF_DISABLE_WALLPAPER | RDP_PERF_DISABLE_FULLWINDOWDRAG | RDP_PERF_DISABLE_MENUANIMATIONS |
      RDP_PERF_DISABLE_THEMING | RDP_PERF_DISABLE_CURSOR_SHADOW | RDP_PERF_DISABLE_CURSORSETTINGS,
  },
  {
    id: 'medium',
    label: 'Medium',
    tip: 'Medium — full resolution, no desktop wallpaper, Windows still looks normal. Recommended.',
    cap: null,
    // Theming KEPT so Windows still looks like Windows; wallpaper and the animation/cursor extras
    // dropped because they cost the most for the least.
    perf: RDP_PERF_DISABLE_WALLPAPER | RDP_PERF_DISABLE_FULLWINDOWDRAG | RDP_PERF_DISABLE_MENUANIMATIONS |
      RDP_PERF_DISABLE_CURSOR_SHADOW | RDP_PERF_DISABLE_CURSORSETTINGS,
  },
  {
    id: 'high',
    label: 'High',
    tip: 'High — full resolution, wallpaper and font smoothing on. Best on a fast local connection.',
    cap: null,
    // The historical behaviour (upstream ironrdp's PerformanceFlags::default()).
    perf: RDP_PERF_DISABLE_FULLWINDOWDRAG | RDP_PERF_DISABLE_MENUANIMATIONS | RDP_PERF_ENABLE_FONT_SMOOTHING,
  },
];

var RDP_QUALITY_DEFAULT = 'medium';

function rdpQualityGet() {
  var id = null;
  try { id = localStorage.getItem('rdp_quality'); } catch { /* */ }
  return rdpQualityProfile(id);
}

// Resolve an id to a profile, always returning a valid one (unknown/missing → the default).
function rdpQualityProfile(id) {
  for (var i = 0; i < RDP_QUALITY_PROFILES.length; i++) {
    if (RDP_QUALITY_PROFILES[i].id === id) return RDP_QUALITY_PROFILES[i];
  }
  for (var j = 0; j < RDP_QUALITY_PROFILES.length; j++) {
    if (RDP_QUALITY_PROFILES[j].id === RDP_QUALITY_DEFAULT) return RDP_QUALITY_PROFILES[j];
  }
  return RDP_QUALITY_PROFILES[0];
}

// Clamp a desktop size to the active profile's cap, preserving aspect ratio so the remote desktop is
// never distorted — we scale the LONGER constraint down and let the other follow. Returns a new object.
function rdpQualityClamp(w, h) {
  var cap = rdpQualityGet().cap;
  if (!cap) return { w: w, h: h };
  if (w <= cap.w && h <= cap.h) return { w: w, h: h };
  var scale = Math.min(cap.w / w, cap.h / h);
  return { w: Math.max(640, Math.floor(w * scale)), h: Math.max(480, Math.floor(h * scale)) };
}

// Fill the toolbar <select> with the profile list. Called once when the viewer opens (the markup ships
// an empty select) so the options can never drift from RDP_QUALITY_PROFILES.
function rdpQualityPopulate() {
  var sels = document.querySelectorAll('.rv-quality-select');
  for (var s = 0; s < sels.length; s++) {
    var sel = sels[s];
    if (sel.options.length === RDP_QUALITY_PROFILES.length) continue; // already built
    sel.innerHTML = '';
    for (var i = 0; i < RDP_QUALITY_PROFILES.length; i++) {
      var p = RDP_QUALITY_PROFILES[i];
      var o = document.createElement('option');
      o.value = p.id;
      o.textContent = p.label;
      sel.appendChild(o);
    }
  }
}

// Persist + reflect, then re-apply to the live session by reconnecting at the new size (debounced, so
// rapid clicks through the cycle coalesce into ONE reconnect). Mirrors rdpZoomApply.
function rdpQualityApply(id) {
  var prev = rdpQualityGet().id;
  try {
    if (id === RDP_QUALITY_DEFAULT) localStorage.removeItem('rdp_quality');
    else localStorage.setItem('rdp_quality', id);
  } catch { /* */ }
  rdpQualitySyncButtons();
  if (id === prev) return;
  if (typeof RDP === 'undefined' || !RDP || !RDP.session) return;
  if (RDP._qualityTimer) { try { clearTimeout(RDP._qualityTimer); } catch { /* */ } }
  RDP._qualityTimer = setTimeout(function () {
    RDP._qualityTimer = null;
    if (RDP.session && typeof rdpReconnectAtSize === 'function') rdpReconnectAtSize();
  }, 500);
}

// Reflect the active profile on every quality control (toolbar row AND full-screen banner): build the
// options if needed, select the active one, and move the tooltip onto the wrapper (a tooltip on the
// <select> itself would fight the native option popup).
function rdpQualitySyncButtons() {
  rdpQualityPopulate();
  var p = rdpQualityGet();
  var sels = document.querySelectorAll('.rv-quality-select');
  for (var i = 0; i < sels.length; i++) {
    sels[i].value = p.id;
    var wrap = sels[i].closest ? sels[i].closest('.rv-quality') : null;
    if (wrap) wrap.setAttribute('data-cnstooltip', p.tip);
  }
}
;

// ========== rdpStats.js ==========
// clientPortal/src/js/rdpStats.js — RDP session performance HUD (idea #2083).
//
// WHY: "the ironrdp client seems slow on some stations" was unfalsifiable. The server records only
// duration_ms per session (bytes are now counted too — netwatch/rdpBridge.js), but that still cannot
// separate "the station/tunnel is starving us of pixels" from "the pixels arrive fine and the BROWSER
// is slow to paint them". Those two have opposite fixes, so we measure both, client-side, live.
//
// WHAT IT MEASURES (all sampled over a 1s window):
//   down / up  — kilobits/sec on the session WebSocket. Down is pixels arriving from the station.
//   fps        — painted frames/sec. A "frame" is a burst of put_image_data blits; ironrdp blits each
//                dirty region separately, so counting blits would wildly overcount. We coalesce every
//                blit that lands in the same animation frame into one.
//   px/s       — pixels actually repainted per second. This is the honest "how much screen is moving"
//                number; fps alone lies when only a tiny caret rectangle is updating.
//   paint      — milliseconds/sec spent inside put_image_data. If this approaches 1000 the browser is
//                the bottleneck, not the network.
//
// HOW TO READ IT (the whole point):
//   low down + low px/s + low paint   → starved. The station or the tunnel is the bottleneck.
//   high down + high paint            → the browser/canvas is the bottleneck (lower the resolution).
//   high down + low px/s              → bytes are arriving but not becoming pixels: decode-bound in WASM.
//
// It is OFF by default and invisible to normal portal users. Enable it by loading the portal once with
// ?rdpdiag=1 (the choice is then persisted in localStorage); ?rdpdiag=0 turns it back off.

var RDP_STATS = {
  on: false,
  wrapped: false,      // window.WebSocket patched (once per page load)
  bytesDown: 0, bytesUp: 0,
  blitPixels: 0, blitMs: 0,
  frames: 0, framePending: false,
  timer: null, el: null,
  sock: null,          // the live session socket, so a reconnect can reset counters
};

// Enabled when ?rdpdiag=1 has ever been passed on this browser. Reading the query string every call is
// cheap and lets the flag be flipped without a code change.
function rdpStatsEnabled() {
  try {
    var q = String(location.search || '');
    if (q.indexOf('rdpdiag=1') !== -1) { localStorage.setItem('rdp_stats', '1'); return true; }
    if (q.indexOf('rdpdiag=0') !== -1) { localStorage.removeItem('rdp_stats'); return false; }
    return localStorage.getItem('rdp_stats') === '1';
  } catch { return false; }
}

// Count bytes on the session WebSocket by wrapping the constructor ONCE. ironrdp-web opens its socket
// from inside the WASM (gloo-net → the browser's own WebSocket), so there is no JS-side handle to hook;
// intercepting construction is the only seam. We only touch sockets whose URL is the RDP bridge, and we
// never alter behaviour — just tally sizes and pass everything through untouched.
function rdpStatsInstallSocketCounter() {
  if (RDP_STATS.wrapped) return;
  var Native = window.WebSocket;
  if (typeof Native !== 'function') return;
  RDP_STATS.wrapped = true;
  var Wrapped = function (url, protocols) {
    var ws = (arguments.length > 1) ? new Native(url, protocols) : new Native(url);
    try {
      if (String(url).indexOf('/api/rdp') !== -1) {
        RDP_STATS.sock = ws;
        RDP_STATS.bytesDown = 0; RDP_STATS.bytesUp = 0; // fresh session → fresh counters
        ws.addEventListener('message', function (ev) {
          var d = ev && ev.data;
          if (!d) return;
          if (d.byteLength != null) RDP_STATS.bytesDown += d.byteLength;
          else if (d.size != null) RDP_STATS.bytesDown += d.size;
          else if (typeof d === 'string') RDP_STATS.bytesDown += d.length;
        });
        var send = ws.send;
        ws.send = function (data) {
          try {
            if (data && data.byteLength != null) RDP_STATS.bytesUp += data.byteLength;
            else if (data && data.size != null) RDP_STATS.bytesUp += data.size;
            else if (typeof data === 'string') RDP_STATS.bytesUp += data.length;
          } catch { /* never let accounting break the session */ }
          return send.apply(ws, arguments);
        };
      }
    } catch { /* accounting is best-effort */ }
    return ws;
  };
  // Preserve the constants/prototype so anything doing `ws instanceof WebSocket` or reading
  // WebSocket.OPEN keeps working.
  try {
    Wrapped.prototype = Native.prototype;
    Wrapped.CONNECTING = Native.CONNECTING; Wrapped.OPEN = Native.OPEN;
    Wrapped.CLOSING = Native.CLOSING; Wrapped.CLOSED = Native.CLOSED;
    window.WebSocket = Wrapped;
  } catch { RDP_STATS.wrapped = false; }
}

// Wrap put_image_data on OUR context instance (not the prototype — we don't want to tax every canvas on
// the page). ironrdp blits one dirty region at a time, so we coalesce per animation frame for fps.
function rdpStatsAttachCanvas(ctx) {
  if (!ctx || ctx.__rdpStatsWrapped) return;
  var orig = ctx.putImageData;
  if (typeof orig !== 'function') return;
  ctx.__rdpStatsWrapped = true;
  ctx.putImageData = function (img) {
    var t0 = 0;
    try { t0 = performance.now(); } catch { /* */ }
    var out = orig.apply(ctx, arguments);
    try {
      RDP_STATS.blitMs += (performance.now() - t0);
      if (img && img.width) RDP_STATS.blitPixels += img.width * img.height;
      if (!RDP_STATS.framePending) {
        RDP_STATS.framePending = true;
        requestAnimationFrame(function () { RDP_STATS.framePending = false; RDP_STATS.frames++; });
      }
    } catch { /* */ }
    return out;
  };
}

function rdpStatsEl() {
  if (RDP_STATS.el && RDP_STATS.el.isConnected) return RDP_STATS.el;
  var stage = document.querySelector('.rv-stage') || document.getElementById('rv-screen');
  if (!stage) return null;
  var el = document.createElement('div');
  el.id = 'rdp-stats';
  el.className = 'rdp-stats';
  stage.appendChild(el);
  RDP_STATS.el = el;
  return el;
}

// Begin sampling. Called once a session goes live; safe to call repeatedly.
function rdpStatsStart() {
  if (!rdpStatsEnabled()) return;
  RDP_STATS.on = true;
  RDP_STATS.blitPixels = 0; RDP_STATS.blitMs = 0; RDP_STATS.frames = 0;
  if (RDP_STATS.timer) return;
  var last = Date.now();
  RDP_STATS.timer = setInterval(function () {
    var el = rdpStatsEl();
    if (!el) return;
    var now = Date.now();
    var secs = Math.max(0.001, (now - last) / 1000);
    last = now;
    var kbpsDown = Math.round((RDP_STATS.bytesDown * 8) / secs / 1000);
    var kbpsUp = Math.round((RDP_STATS.bytesUp * 8) / secs / 1000);
    var fps = Math.round(RDP_STATS.frames / secs);
    var mpxs = (RDP_STATS.blitPixels / secs / 1e6);
    var paintMs = Math.round(RDP_STATS.blitMs / secs);
    RDP_STATS.bytesDown = 0; RDP_STATS.bytesUp = 0;
    RDP_STATS.blitPixels = 0; RDP_STATS.blitMs = 0; RDP_STATS.frames = 0;
    // Resolution is reported with the device-pixel ratio so it's obvious the negotiated desktop is the
    // DPI-EFFECTIVE size, not the panel's physical pixels: "1920×1080 @2x" on a 4K panel scaled to
    // 1080p means we correctly asked for 1080p rather than 3840×2160.
    var res = '';
    try {
      var c = document.getElementById('rdp-canvas');
      if (c) {
        res = c.width + '×' + c.height;
        var dpr = window.devicePixelRatio || 1;
        if (dpr !== 1) res += ' @' + (Math.round(dpr * 100) / 100) + 'x';
      }
    } catch { /* */ }
    // paint ms/sec is the tell for a browser-bound session — warn past ~50% of the budget.
    var paintCls = paintMs > 500 ? ' rdp-stats-bad' : (paintMs > 250 ? ' rdp-stats-warn' : '');
    el.innerHTML =
      '<span class="rdp-stats-k">down</span> ' + kbpsDown + ' kbps' +
      ' <span class="rdp-stats-k">up</span> ' + kbpsUp + ' kbps' +
      ' <span class="rdp-stats-k">fps</span> ' + fps +
      ' <span class="rdp-stats-k">px</span> ' + mpxs.toFixed(1) + ' MP/s' +
      ' <span class="rdp-stats-k">paint</span> <span class="' + paintCls.trim() + '">' + paintMs + ' ms/s</span>' +
      (res ? ' <span class="rdp-stats-k">res</span> ' + res : '');
  }, 1000);
}

function rdpStatsStop() {
  RDP_STATS.on = false;
  if (RDP_STATS.timer) { try { clearInterval(RDP_STATS.timer); } catch { /* */ } RDP_STATS.timer = null; }
  if (RDP_STATS.el) { try { RDP_STATS.el.remove(); } catch { /* */ } RDP_STATS.el = null; }
}
;

// ========== rdpZoom.js ==========
// clientPortal/src/js/rdpZoom.js — remote-desktop resolution zoom (idea #1897 follow-up).
//
// Instead of always matching the browser viewport 1:1 ("match perfect"), the user can zoom in/out to
// trade resolution for size. The factor is the APPARENT size multiplier, like a browser's zoom:
//   1.0  = match the viewport (the original behaviour)
//   >1.0 = zoom IN  → LOWER negotiated resolution (fewer, larger pixels — bigger UI, easier to read)
//   <1.0 = zoom OUT → HIGHER negotiated resolution (more screen real estate)
// rdpViewportSize() (in rdp.js) divides the measured viewport by this factor, so this module only
// owns the factor, its persistence, and the toolbar buttons.
//
// APPLYING a new resolution: the kiosk/alt-shell hosts don't open the RDP Display Control channel, so
// a live session.resize() is a silent no-op — the resolution is only honoured at CONNECT time. So a
// zoom change reconnects at the new size (rdpReconnectAtSize, the same path fullscreen uses). A burst
// of clicks is debounced into a single reconnect. The factor is persisted in localStorage so it
// survives a refresh AND is applied to the very first connect.

var RDP_ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];
var RDP_ZOOM_DEFAULT = 1.0;

// Current zoom, clamped into the ladder range. Reads localStorage so it's stable across a refresh.
function rdpZoomGet() {
  var z = NaN;
  try { z = parseFloat(localStorage.getItem('rdp_zoom')); } catch { /* */ }
  if (!z || !isFinite(z)) z = RDP_ZOOM_DEFAULT;
  var min = RDP_ZOOM_STEPS[0], max = RDP_ZOOM_STEPS[RDP_ZOOM_STEPS.length - 1];
  return Math.max(min, Math.min(max, z));
}

// Persist (default is stored as "no key" so a cleared pref falls back to match-perfect).
function rdpZoomSetPref(z) {
  try {
    if (z === RDP_ZOOM_DEFAULT) localStorage.removeItem('rdp_zoom');
    else localStorage.setItem('rdp_zoom', String(z));
  } catch { /* */ }
}

// Step along the ladder from the nearest current stop; dir +1 = zoom in, -1 = zoom out.
function rdpZoomStep(dir) {
  var cur = rdpZoomGet();
  var idx = 0, best = Infinity;
  for (var i = 0; i < RDP_ZOOM_STEPS.length; i++) {
    var d = Math.abs(RDP_ZOOM_STEPS[i] - cur);
    if (d < best) { best = d; idx = i; }
  }
  idx = Math.max(0, Math.min(RDP_ZOOM_STEPS.length - 1, idx + dir));
  rdpZoomApply(RDP_ZOOM_STEPS[idx]);
}

function rdpZoomIn() { rdpZoomStep(1); }    // bigger UI, lower resolution
function rdpZoomOut() { rdpZoomStep(-1); }  // smaller UI, higher resolution
function rdpZoomReset() { rdpZoomApply(RDP_ZOOM_DEFAULT); }

// Persist + reflect the new zoom, then re-apply the resolution to the live session by reconnecting at
// the new size (debounced so rapid clicks coalesce into ONE reconnect). No-op reconnect when the
// value didn't actually change or when not connected (the next connect picks up the persisted value).
function rdpZoomApply(z) {
  var prev = rdpZoomGet();
  rdpZoomSetPref(z);
  rdpZoomSyncButtons();
  if (z === prev) return;
  if (typeof RDP === 'undefined' || !RDP || !RDP.session) return;
  if (RDP._zoomTimer) { try { clearTimeout(RDP._zoomTimer); } catch { /* */ } }
  RDP._zoomTimer = setTimeout(function () {
    RDP._zoomTimer = null;
    if (RDP.session && typeof rdpReconnectAtSize === 'function') rdpReconnectAtSize();
  }, 500);
}

// Reflect the current zoom on EVERY zoom control (the toolbar row AND the full-screen banner):
// percentage label(s) + disable the +/- buttons at the ladder ends.
function rdpZoomSyncButtons() {
  var z = rdpZoomGet();
  var pct = Math.round(z * 100) + '%';
  var labels = document.querySelectorAll('.rv-zoom-level-val');
  for (var i = 0; i < labels.length; i++) labels[i].textContent = pct;
  var min = RDP_ZOOM_STEPS[0], max = RDP_ZOOM_STEPS[RDP_ZOOM_STEPS.length - 1];
  var outs = document.querySelectorAll('[data-action="remote-zoom-out"]');
  var ins = document.querySelectorAll('[data-action="remote-zoom-in"]');
  for (var o = 0; o < outs.length; o++) outs[o].disabled = (z <= min + 1e-6);
  for (var n = 0; n < ins.length; n++) ins[n].disabled = (z >= max - 1e-6);
}
;

// ========== remote.js ==========
// remote.js — client remote-access view (idea #1078).
//
// Lists the stations the signed-in user is permitted to reach (allow-list ∩ NetWatch
// presence), each with online + active/idle state and whether another PORTAL user is
// already connected, and a Join button per enabled RDP target. Joining opens the viewer
// as an IN-PAGE overlay (never a new tab) with a Close button; the viewer itself lives in
// rdp.js. Native RDP is the ONLY client remote-access method (idea #1883) — the noVNC/RFB
// viewer that used to live here was removed in idea #2548 (it had been unreachable since
// #1883; nothing rendered a button for it).

function loadRemoteStations() {
  var box = document.getElementById('remote-stations');
  if (box) box.innerHTML = '<div class="loading">Loading your computers…</div>';
  fetchRemoteStations()
    .then(function (data) { renderRemoteStations(data || {}); })
    .catch(function (err) {
      if (box) {
        var msg = (err && err.code === 'mfa_required') ? 'Please finish signing in to use remote access.'
          : (err && err.code === 'login_required') ? 'Please sign in to use remote access.'
          : (err && err.code === 'not_authorized') ? 'You don’t have remote access permission. Contact support if you need it.'
          : (err && err.message) || 'Could not load your computers.';
        box.innerHTML = '<div class="empty-state">' + escapeRemote(msg) + '</div>';
      }
    });
}

// One station card. Each button carries data-company (idea #1619) so a cross-company login mints the
// ticket against the RIGHT company (a hostname may exist in more than one company the user can reach).
function remoteStationCard(s) {
  var online = !!s.online;
  var state = online ? (s.activity_state || 'active') : 'offline';
  var statusLabel = online ? (s.activity_label || 'Online') : 'Offline';
  var co = (s.company_id != null) ? String(s.company_id) : '';
  var others = (s.other_portal_users || []);
  var otherHtml = others.length
    ? '<span class="remote-inuse"><i class="fa-solid fa-user"></i> ' +
      escapeRemote(others.length === 1 ? (others[0] + ' is connected') : (others.length + ' users connected')) + '</span>'
    : '';
  // RDP is the ONLY client remote-access method (idea #1883): show one button per enabled RDP target
  // (desktop + apps). A station is auto-provisioned the moment it's granted, so an online station
  // normally always has targets; if one somehow isn't provisioned yet, show a "being set up" state —
  // there is no other transport to fall back to.
  var rdp = s.rdp;
  var btn;
  if (!online) {
    btn = '<button type="button" class="remote-join is-disabled" disabled>Offline</button>';
  } else if (rdp && rdp.provisioned && (rdp.targets || []).length) {
    btn = rdp.targets.map(function (t) {
      return '<button type="button" class="remote-join remote-join-rdp" data-action="remote-connect-rdp" ' +
        'data-host="' + escapeRemote(s.hostname) + '" data-company="' + escapeRemote(co) + '" data-target="' + escapeRemote(t.id) + '" data-tname="' + escapeRemote(t.name) + '">' +
        '<i class="' + escapeRemote(t.icon || 'fa-solid fa-window-maximize') + '"></i> ' + escapeRemote(t.name) + '</button>';
    }).join('');
  } else {
    btn = '<button type="button" class="remote-join is-disabled" disabled><i class="fa-solid fa-clock"></i> Being set up</button>';
  }
  // The one exception to RDP-only (idea #3397): an opt-in Shared button — the station's LIVE console,
  // alongside the person at the keyboard. Only sent as true when an admin turned it on for this user on
  // this computer (and the fleet switch is on), so for everyone else nothing changes.
  if (online && s.shared) {
    btn += '<button type="button" class="remote-join remote-join-rdp" data-action="remote-connect-shared" ' +
      'data-host="' + escapeRemote(s.hostname) + '" data-company="' + escapeRemote(co) + '" ' +
      'data-cnstooltip="Shared session: see and use this computer\u2019s screen together with the person sitting at it. ' +
      'You can both use the mouse and keyboard, and they see everything you do. Shared sessions are not recorded." ' +
      'data-cnstooltip-pos="bottom" data-cnstooltip-wrap>' +
      '<i class="fa-solid fa-people-arrows"></i> Shared</button>';
  }
  return '' +
    '<div class="remote-card">' +
    '  <div class="remote-card-main">' +
    '    <span class="remote-host"><i class="fa-solid fa-display"></i> ' + escapeRemote(s.hostname) + '</span>' +
    '    <span class="remote-badge state-' + state + '">' + escapeRemote(statusLabel) + '</span>' +
    '    ' + otherHtml +
    '  </div>' +
    '  <div class="remote-card-actions">' + btn + '</div>' +
    '</div>';
}

// Render the computer list. #1619: when the login is assigned computers at MORE THAN ONE company, the
// list is grouped under a company header per group ("separated by company"). A single-company user is
// rendered flat, exactly as before. Accepts the API shape { companies:[{company_name,stations}], stations }.
function renderRemoteStations(data) {
  var box = document.getElementById('remote-stations');
  if (!box) return;
  var d = data || {};
  var groups = (d.companies || []).filter(function (g) { return g && (g.stations || []).length; });
  var flat = d.stations || [];
  if (!groups.length && !flat.length) {
    box.innerHTML = '<div class="empty-state">No computers have been shared with you yet.</div>';
    return;
  }
  // Single company (or a client that only sent the flat list) → no header, unchanged look.
  if (groups.length <= 1) {
    var list = groups.length ? groups[0].stations : flat;
    box.innerHTML = list.map(remoteStationCard).join('');
    return;
  }
  box.innerHTML = groups.map(function (g) {
    return '' +
      '<div class="remote-company-group">' +
      '  <div class="remote-company-header"><i class="fa-solid fa-building"></i> ' + escapeRemote(g.company_name || 'Company') + '</div>' +
      '  <div class="remote-company-stations">' + g.stations.map(remoteStationCard).join('') + '</div>' +
      '</div>';
  }).join('');
}

// --- In-page viewer ---------------------------------------------------------
// The overlay shell (#remote-viewer / #rv-host / #rv-screen / #rv-overlay) is declared in index.html and
// driven entirely by rdp.js; only the shared Close button is handled here.
function closeRemoteViewer() {
  // The overlay's single Close button predates RDP (idea #1356) and is still the only close affordance.
  // An RDP session hands off to rdpEndSession (idea #1878), which shows the "session complete" card
  // first — its Back button does the real teardown, rather than dropping straight back to the list.
  if (typeof RDP !== 'undefined' && RDP && RDP.host) { rdpEndSession(); return; }
  // The opt-in Shared (RFB live-console) session, idea #3397, owns it otherwise.
  if (typeof SHARED !== 'undefined' && SHARED && SHARED.host) { closeSharedViewer(); return; }
  // Defensive: no session owns the overlay, so just put the shell back to its closed state.
  var ov = document.getElementById('remote-viewer');
  if (ov) { ov.classList.add('is-hidden'); ov.setAttribute('aria-hidden', 'true'); }
  var screen = document.getElementById('rv-screen');
  if (screen) screen.innerHTML = '';
  // Refresh the list so "in use by" indicators update.
  if (parseHash && parseHash().path === 'remote') loadRemoteStations();
}

// Local escaper (service/ui helpers don't expose one to this layer).
function escapeRemote(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}
;

// ========== rfbShared.js ==========
// rfbShared.js — the opt-in "Shared" session viewer (idea #3397).
//
// The portal is RDP-only (idea #1883) with ONE exception: where an admin has ticked "Shared session" for
// this user on this computer, its card also shows a Shared button. That opens the station's LIVE console
// over RFB (noVNC), so the portal user and the person at the keyboard see and use the same screen — RDP
// would open a separate Windows session instead. The server re-checks the toggle + the fleet kill-switch
// at ticket mint AND at the /api/vnc upgrade; this file only drives the viewer.
//
// The viewer joins the netwatch-hub StationHub as a 'portal' viewer: whoever attached first drives, a
// watcher takes over with "NWCTL takecontrol" (a CNS tech always outranks the portal), and the hub refuses
// any portal attempt to block the station's input, blank its screen or lock it. Nothing is recorded.
//
// noVNC (281 KB) is loaded on demand, only when Shared is clicked — never on every portal page (idea #2548).
// It is the staff console's own build, served by the portal at /vendor/novnc.js (router.js).
//
// It reuses the RDP viewer's overlay shell (#remote-viewer); the .rv-shared class switches the toolbar to
// the buttons that mean something here (Ctrl+Alt+Del, Paste while driving, Take control while watching,
// Full screen, Close).

var SHARED = { host: null, companyId: null, rfb: null, intentional: false, attempts: 0, timer: null, everConnected: false, cursor: { x: -1, y: -1 }, controlling: false, loading: null };

function sharedEnsureNoVNC() {
  if (window.NoVNC && window.NoVNC.RFB) return Promise.resolve();
  if (SHARED.loading) return SHARED.loading;
  SHARED.loading = new Promise(function (resolve, reject) {
    var s = document.createElement('script');
    s.src = '/vendor/novnc.js';
    s.async = true;
    s.onload = function () {
      if (window.NoVNC && window.NoVNC.RFB) resolve();
      else { SHARED.loading = null; reject(new Error('viewer missing')); }
    };
    s.onerror = function () { SHARED.loading = null; reject(new Error('viewer failed to load')); };
    document.head.appendChild(s);
  });
  return SHARED.loading;
}

function sharedSetState(text, cls) {
  var el = document.getElementById('rv-state');
  if (el) { el.textContent = text; el.className = 'rv-state' + (cls ? ' ' + cls : ''); }
}

function sharedOverlay(show, text) {
  var ov = document.getElementById('rv-overlay');
  if (!ov) return;
  ov.classList.toggle('is-hidden', !show);
  if (show && text) { var t = document.getElementById('rv-overlay-text'); if (t) t.textContent = text; }
}

// NWCTL commands ALWAYS go as a classic, inline ClientCutText — never through noVNC's clipboardPasteFrom
// (idea #3509; the staff console's sendCtl, idea #2823). Once Extended Clipboard is negotiated,
// clipboardPasteFrom sends only a Notify and hands the text over later in a zlib Provide, and the hub
// reads takecontrol out of the plain body, so Take control silently did nothing. Falls back to the old
// path if noVNC's internals ever move, rather than dropping the command.
function sharedPushNwctl(cmd) {
  var rfb = SHARED.rfb;
  if (!rfb || rfb._rfbConnectionState !== 'connected') return;
  var text = 'NWCTL ' + cmd;
  try {
    var M = window.NoVNC && window.NoVNC.RFB && window.NoVNC.RFB.messages;
    if (!M || typeof M.clientCutText !== 'function' || !rfb._sock) { rfb.clipboardPasteFrom(text); return; }
    var bytes = new Uint8Array(text.length);
    for (var i = 0; i < text.length; i++) { var c = text.charCodeAt(i); bytes[i] = c > 255 ? 63 : c; }
    M.clientCutText(rfb._sock, bytes);
  } catch { /* closed */ }
}

// The bar's Paste button (idea #3509): shown only while THIS viewer drives (the hub drops clipboard from a
// watcher). A click is a real user gesture, so the browser lets us read the clipboard; the text goes to the
// station's clipboard (ordinary clipboard, so the extended path is right here) and the person pastes it
// there with Ctrl+V. Nothing comes back the other way.
function sharedPasteToRemote() {
  if (!SHARED.rfb || !SHARED.controlling) return;
  if (!window.isSecureContext || !navigator.clipboard || typeof navigator.clipboard.readText !== 'function') {
    try { showToast('Clipboard needs a secure (HTTPS) connection.', 'warning'); } catch { /* */ }
    return;
  }
  navigator.clipboard.readText().then(function (text) {
    if (!text) { showToast('Your clipboard has no text to send.', 'info'); return; }
    if (!SHARED.rfb || !SHARED.controlling) return;
    SHARED.rfb.clipboardPasteFrom(text);
    showToast('Clipboard sent. Press Ctrl+V on the computer to paste it.', 'success');
    sharedFocus();
  }).catch(function () {
    try { showToast('Could not read your clipboard. Allow clipboard access for this site and try again.', 'danger'); } catch { /* */ }
  });
}

// Open the overlay for a station and start connecting.
function openSharedViewer(hostname, companyId) {
  if (!hostname) return;
  if (typeof RDP !== 'undefined' && RDP && RDP.host) { showToast('Close the current remote session first.', 'warning'); return; }
  SHARED.host = hostname;
  SHARED.companyId = (companyId != null && companyId !== '') ? companyId : null; // #1619: station's company
  SHARED.intentional = false;
  SHARED.attempts = 0;
  SHARED.everConnected = false;
  var ov = document.getElementById('remote-viewer');
  var hostEl = document.getElementById('rv-host');
  if (hostEl) hostEl.textContent = hostname + ' · Shared';
  if (ov) { ov.classList.remove('is-hidden'); ov.setAttribute('aria-hidden', 'false'); ov.classList.add('rv-shared'); }
  var screen = document.getElementById('rv-screen');
  if (screen) screen.innerHTML = '';
  sharedOverlay(false);
  sharedSetState('Loading viewer…', '');
  sharedEnsureNoVNC()
    .then(function () { if (SHARED.host === hostname) sharedConnect(hostname); })
    .catch(function () {
      sharedSetState('Couldn’t load the viewer', 'bad');
      sharedOverlay(true, 'The shared-session viewer failed to load. Please refresh the page and try again.');
    });
}

// Mint a fresh single-use ticket and (re)connect. Called on open and on each auto-reconnect.
function sharedConnect(hostname) {
  sharedSetState('Connecting…', '');
  requestSharedTicket(hostname, SHARED.companyId)
    .then(function (res) {
      if (SHARED.host !== hostname) return; // closed while minting
      // No session id in the URL (idea #3509): it is the portal's long-lived bearer token and a URL can
      // reach access logs. The single-use ticket is bound to this session server-side.
      var proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      var url = proto + '//' + location.host + '/api/vnc?hostname=' + encodeURIComponent(hostname) +
        '&ticket=' + encodeURIComponent(res.ticket);
      var screen = document.getElementById('rv-screen');
      if (screen) screen.innerHTML = '';
      var rfb = new window.NoVNC.RFB(screen, url, { credentials: { password: res.vnc_password || '' } });
      rfb.scaleViewport = true;
      rfb.background = '#000';
      SHARED.cursor = { x: -1, y: -1 };
      SHARED.controlling = false;
      sharedSyncControl();
      rfb.addEventListener('connect', function () {
        SHARED.attempts = 0; SHARED.everConnected = true;
        sharedOverlay(false);
        sharedSyncControl();
        try { rfb.focus(); } catch { /* */ }
      });
      rfb.addEventListener('disconnect', function () { sharedHideCursor(); if (!SHARED.intentional) sharedScheduleReconnect(hostname); });
      // The hub's out-of-band channel: the real station pointer + this viewer's control state arrive as
      // ServerCutText prefixed 'NWCTL '. Ordinary clipboard text is ignored (no clipboard sync here).
      rfb.addEventListener('clipboard', function (e) {
        var text = (e && e.detail && e.detail.text) || '';
        if (text.indexOf('NWCTL ') === 0) sharedHandleNwctl(text.slice(6));
      });
      rfb.addEventListener('credentialsrequired', function () { try { rfb.sendCredentials({ password: res.vnc_password || '' }); } catch { /* */ } });
      rfb.addEventListener('securityfailure', function () { sharedSetState('Couldn’t authenticate', 'bad'); });
      SHARED.rfb = rfb;
    })
    .catch(function (err) {
      var msg = (err && err.code === 'forbidden') ? ((err && err.message) || 'Shared sessions aren’t enabled for you on this computer.')
        : (err && err.message) || 'Could not start the session.';
      sharedSetState(msg, 'bad');
      if (err && err.code === 'forbidden') { sharedOverlay(true, msg); return; } // a retry won't change the answer
      if (!SHARED.intentional) sharedScheduleReconnect(hostname);
    });
}

function sharedScheduleReconnect(hostname) {
  if (SHARED.intentional || SHARED.host !== hostname) return;
  var cap = SHARED.everConnected ? 5 : 2;
  if (SHARED.attempts >= cap) {
    if (SHARED.everConnected) {
      sharedSetState('Disconnected', 'bad');
      sharedOverlay(true, 'Disconnected. Close and reconnect to try again.');
    } else {
      sharedSetState('Couldn’t connect', 'bad');
      sharedOverlay(true, 'Couldn’t start a shared session on ' + hostname + '. It may be offline, or not ready for shared sessions yet. Please try again shortly or contact support.');
    }
    return;
  }
  SHARED.attempts++;
  sharedOverlay(true, (SHARED.everConnected ? 'Reconnecting… (' : 'Connecting… (') + SHARED.attempts + ')');
  clearTimeout(SHARED.timer);
  SHARED.timer = setTimeout(function () { if (!SHARED.intentional && SHARED.host === hostname) sharedConnect(hostname); }, 1500 * SHARED.attempts);
}

function sharedHandleNwctl(body) {
  var m = /^cursor=(-?\d+),(-?\d+)/.exec(body);
  if (m) { SHARED.cursor = { x: parseInt(m[1], 10), y: parseInt(m[2], 10) }; sharedUpdateCursor(); return; }
  m = /^controller=([01])/.exec(body);
  if (m) { SHARED.controlling = m[1] === '1'; sharedSyncControl(); }
}

// Toolbar + state line reflect whether THIS viewer drives. A watcher sees "Take control"; the hub decides
// (a CNS tech who is driving keeps control).
function sharedSyncControl() {
  var ov = document.getElementById('remote-viewer');
  if (ov) ov.classList.toggle('rv-shared-watching', !SHARED.controlling);
  if (SHARED.rfb && SHARED.everConnected) sharedSetState(SHARED.controlling ? 'Shared · you have control' : 'Shared · watching', SHARED.controlling ? 'ok' : '');
  sharedUpdateCursor();
}

function sharedTakeControl() { sharedPushNwctl('takecontrol'); }

function sharedSendCad() {
  if (SHARED.rfb) { try { SHARED.rfb.sendCtrlAltDel(); } catch { /* */ } }
}

function sharedFocus() {
  if (SHARED.rfb) { try { SHARED.rfb.focus(); } catch { /* */ } }
}

// The hub absorbs noVNC's cursor encoding, so the station pointer is drawn from NWCTL cursor=x,y —
// framebuffer coords → rendered canvas coords. Hidden while THIS viewer drives (its own browser cursor is
// the instant pointer) or when the pointer is off-frame.
function sharedUpdateCursor() {
  var stage = document.getElementById('rv-screen');
  var cv = stage && stage.querySelector('canvas');
  if (stage) stage.classList.toggle('rv-controlling', !!SHARED.controlling);
  var dot = document.getElementById('rv-cursor');
  var c = SHARED.cursor;
  if (!stage || !cv || !cv.width || !cv.height || !c || c.x < 0 || c.y < 0 || SHARED.controlling) {
    if (dot) dot.classList.add('is-hidden');
    return;
  }
  if (!dot) {
    dot = document.createElement('div');
    dot.id = 'rv-cursor';
    dot.className = 'rv-cursor';
    dot.innerHTML = '<i class="fa-solid fa-arrow-pointer"></i>';
    stage.appendChild(dot);
  }
  dot.classList.remove('is-hidden');
  dot.style.left = Math.round(cv.offsetLeft + (c.x / cv.width) * cv.offsetWidth) + 'px';
  dot.style.top = Math.round(cv.offsetTop + (c.y / cv.height) * cv.offsetHeight) + 'px';
}

function sharedHideCursor() {
  SHARED.cursor = { x: -1, y: -1 };
  var dot = document.getElementById('rv-cursor');
  if (dot) dot.classList.add('is-hidden');
  var stage = document.getElementById('rv-screen');
  if (stage) stage.classList.remove('rv-controlling');
}

// Close. Never locks the station — the person at the keyboard is still using it.
function closeSharedViewer() {
  SHARED.intentional = true;
  clearTimeout(SHARED.timer);
  SHARED.host = null;
  sharedHideCursor();
  var rfb = SHARED.rfb;
  SHARED.rfb = null;
  if (rfb) { try { rfb.disconnect(); } catch { /* */ } }
  var ov = document.getElementById('remote-viewer');
  if (ov) { ov.classList.add('is-hidden'); ov.classList.remove('rv-shared', 'rv-shared-watching'); ov.setAttribute('aria-hidden', 'true'); }
  sharedOverlay(false);
  var screen = document.getElementById('rv-screen');
  if (screen) screen.innerHTML = '';
  if (typeof parseHash === 'function' && parseHash().path === 'remote' && typeof loadRemoteStations === 'function') loadRemoteStations();
}
;

// ========== route.js ==========
// route.js — hash-based navigation (the address bar IS the breadcrumb).
//
// Navigation state lives entirely in location.hash, so every view is
// deep-linkable and survives a refresh with no sessionStorage involved:
//   #               / #welcome      -> welcome
//   #new                            -> new-issue options
//   #new/ticket | #new/user | #new/suspend
//   #tickets                        -> ticket list
//   #tickets?id=12345               -> ticket details (id is the open ticket)
//
// hashchange is the single source of truth: navigate() just rewrites the hash
// and applyRoute() renders whatever the hash says.

function parseHash() {
  const raw = (location.hash || '').replace(/^#/, '');
  const qIdx = raw.indexOf('?');
  const path = (qIdx >= 0 ? raw.slice(0, qIdx) : raw).replace(/^\/+|\/+$/g, '');
  const query = qIdx >= 0 ? raw.slice(qIdx + 1) : '';
  const params = {};
  query.split('&').forEach((pair) => {
    if (!pair) return;
    const kv = pair.split('=');
    params[decodeURIComponent(kv[0])] = decodeURIComponent(kv[1] || '');
  });
  return { path, params };
}

// The open ticket id comes straight from the URL — no separate state.
function currentTicketId() {
  const { path, params } = parseHash();
  return path === 'tickets' && params.id ? params.id : null;
}

// Build a hash string for a view (+ optional ticket id) and navigate to it.
// Setting location.hash fires hashchange, which drives applyRoute().
function navigate(viewId, params) {
  let path = VIEW_TO_HASH[viewId];
  if (path === undefined) path = '';
  let hash = path;
  if (params && params.id) hash += '?id=' + encodeURIComponent(params.id);
  // #new/user?ticket=<number> — resuming an existing onboarding request (idea #2007). The
  // address bar IS the breadcrumb, so "update this request" has to be a location, not hidden
  // state: a refresh or a shared URL must land back on the same request.
  else if (params && params.ticket) hash += '?ticket=' + encodeURIComponent(params.ticket);

  if ((location.hash || '').replace(/^#/, '') === hash) {
    applyRoute(); // same hash (e.g. re-open) — re-render explicitly
  } else {
    location.hash = hash;
  }
}

// Which top-level nav section a hash path belongs to (default: dashboard).
function navSectionForPath(path) {
  return NAV_SECTION_BY_PATH[path] || 'dashboard';
}

// Render whatever the current hash points at.
function applyRoute() {
  const { path, params } = parseHash();
  // Signed-in tier (idea #3076): a tray session that opens directory / billing / remote is asked to
  // sign in instead. The hash goes back to the dashboard so the address bar matches what's shown.
  if (portalNeedsSignin(path)) {
    navigate('view-welcome');
    if (typeof cpStartSigninUpgrade === 'function') cpStartSigninUpgrade(path);
    return;
  }
  setActiveNav(navSectionForPath(path));
  // Leaving any view stops live polling; the ticket-details branch restarts it.
  stopDetailPolling();

  // Ticket details: #tickets?id=<id>
  if (path === 'tickets' && params.id) {
    showView('view-ticket-details');
    loadTicketDetails(params.id);
    startDetailPolling(params.id);
    return;
  }
  if (path === 'tickets') {
    showView('view-existing');
    loadTickets();
    return;
  }

  // Billing: #billing (invoices) · #billing?tab=payments · #billing?inv=<id>.
  // Gated on the explicit Billing capability. On a
  // deep-link/refresh applyRoute() runs BEFORE context loads, so only bounce
  // once capabilities are actually known (portalRecognised() !== null);
  // loadPortalContext re-checks after the fetch resolves.
  if (path === 'billing') {
    if (portalRecognised() !== null && !portalCapabilities().billing) {
      navigate('view-welcome');
      return;
    }
    showView('view-billing');
    enterBilling(params);
    return;
  }

  // Remote access: #remote. Gated on the explicit Remote capability (idea #1078);
  // like billing, only bounce once capabilities are known.
  if (path === 'remote') {
    // Only for an authenticated (password/SSO) session with the capability — never the
    // agent_id device flow. Bounce once context is known.
    if (portalRecognised() !== null && !(portalCapabilities().remote_access && portalAuthenticated())) {
      navigate('view-welcome');
      return;
    }
    showView('view-remote');
    if (typeof loadRemoteStations === 'function') loadRemoteStations();
    return;
  }

  // Native websocket chat: #chat (idea #1184). Available to any recognised session
  // (agent_id device flow included — this replaces ScreenConnect chat). enterChat
  // opens the socket + hydrates; leaveChat (in chat.js) tears down on navigate away.
  if (path === 'chat') {
    showView('view-chat');
    if (typeof enterChat === 'function') enterChat(params);
    return;
  }
  if (typeof leaveChat === 'function') leaveChat();

  const viewId = HASH_TO_VIEW[path] || 'view-welcome';
  showView(viewId);
  if (viewId === 'view-ticket-form') prefillTicketForm();
  if (viewId === 'view-newuser') ensureNewUserFrame(params && params.ticket);
  if (viewId === 'view-suspend') prepareSuspendForm();
  // Note: the Users directory loads on demand when its "Directory" button is
  // toggled (events.js toggleDirectory), not on view open.
}

// Lazily point the New User iframe at the static onboarding form (/newuser),
// carrying the session so it resolves company/user context without re-auth.
function ensureNewUserFrame(ticketNumber) {
  const frame = document.getElementById('newuser-frame');
  if (!frame) return;
  const sid = portalContext().sessionId || '';
  // Resuming a specific request is a DIFFERENT document than the blank form, so the
  // already-loaded guard has to key on the target, not merely on "has loaded once" —
  // otherwise clicking Update on a ticket just re-shows an empty new-user form.
  const want = '/newuser?session=' + encodeURIComponent(sid)
    + (ticketNumber ? '&ticket=' + encodeURIComponent(ticketNumber) : '');
  if (frame.getAttribute('data-loaded') === want) return;
  frame.src = want;
  frame.setAttribute('data-loaded', want);
}

function initRouter() {
  window.addEventListener('hashchange', applyRoute);
}
;

// ========== ui.js ==========
// ui.js — DOM rendering, view navigation, and presentation helpers.
// No network calls (those live in service.js); no event binding (events.js).

// --- View navigation --------------------------------------------------------

// Pure DOM view toggle — navigation state lives in the URL (route.js), so this
// no longer persists anything.
function showView(viewId) {
  PORTAL_VIEWS.forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.classList.toggle('is-hidden', id !== viewId);
  });
  const shell = document.querySelector('.portal-shell');
  if (shell) shell.scrollTop = 0;
}

// Highlight the active top-level nav item (section = data-nav value).
function setActiveNav(section) {
  document.querySelectorAll('.portal-nav .nav-item').forEach((el) => {
    el.classList.toggle('is-active', el.getAttribute('data-nav') === section);
  });
}

// Mirror the resolved identity into the persistent top bar (company / user /
// live status dot). Called by the welcome + unrecognised context renderers.
function renderTopbarIdentity(d, fallbackName) {
  setText('topbar-company', (d && d.clientname) || '—');
  setText('topbar-user', (d && d.halo_user_full_name) || fallbackName || '');
  const dot = document.getElementById('topbar-status');
  if (dot) {
    const online = d && d.status === 'Online';
    const variant = online ? 'status-success' : ((d && d.status) ? 'status-warning' : 'status-neutral');
    dot.className = 'topbar-status-dot ' + variant;
    dot.setAttribute('data-cnstooltip', (d && d.status) || 'Unknown');
  }
}

// --- Welcome / context rendering (filled from GET /api/context) -------------

// How the user was identified -> [icon, pill variant, label].
const IDENTIFIED_VIA = {
  url: ['fa-circle-check', 'status-success', 'Confirmed from your sign-in'],
  netwatch: ['fa-circle-check', 'status-success', 'Identified from your current sign-in'],
  screenconnect: ['fa-circle-check', 'status-success', 'Identified from your active session'],
  trmm_current: ['fa-circle-check', 'status-success', 'Identified from your current sign-in'],
  trmm_last: ['fa-clock-rotate-left', 'status-info', 'Identified from your last sign-in'],
  fuzzy: ['fa-circle-question', 'status-warning', 'Best guess for this computer — please check it’s you'],
  // Authenticated logins (idea #1078) — they signed in explicitly, so never show the
  // "couldn't identify you" device warning.
  password: ['fa-circle-check', 'status-success', 'Signed in'],
  microsoft: ['fa-circle-check', 'status-success', 'Signed in with Microsoft'],
  google: ['fa-circle-check', 'status-success', 'Signed in with Google'],
  otp_email: ['fa-circle-check', 'status-success', 'Verified by email']
};

function renderIdentified(via) {
  const el = document.getElementById('welcome-identified');
  if (!el) return;
  const v = IDENTIFIED_VIA[via];
  if (v) {
    el.className = 'welcome-identified ' + v[1];
    el.innerHTML = '<i class="fa-solid ' + v[0] + '"></i> ' + escapeHtml(v[2]);
  } else {
    el.className = 'welcome-identified status-warning';
    el.innerHTML = '<i class="fa-solid fa-circle-exclamation"></i> We couldn’t identify you automatically — please check your details below.';
  }
  el.classList.remove('is-hidden');
}

function renderWelcomeContext() {
  const d = agentDetails();
  // Authenticated (password/SSO) sessions have no device — hide the computer/site/hardware
  // fields so the landing shows a clean "signed in" identity, not empty device rows or a
  // "couldn't identify you" warning (idea #1078).
  const authed = (typeof portalAuthenticated === 'function') && portalAuthenticated();
  document.querySelectorAll('.welcome-device-field').forEach((el) => el.classList.toggle('is-hidden', authed));

  setText('welcome-name', d.halo_user_full_name || portalContext().username || 'there');
  setText('meta-company', d.clientname || '—');
  setText('meta-site', d.sitename || '—');
  setText('meta-computer', d.hostname || '—');
  setText('meta-email', d.email || '—');
  setText('meta-phone', d.halo_user_phone || '—');
  setText('meta-cpu', d.processor || '—');
  setText('meta-memory', d.memory || '—');
  setText('meta-age', d.estimated_age || '—');

  // Free space — append the used % and colour it: <80 green, <90 amber, else red.
  const freeEl = document.getElementById('meta-freespace');
  if (freeEl) {
    const pct = typeof d.disk_used_pct === 'number' ? d.disk_used_pct : null;
    if (d.free_space && pct != null) {
      const cls = pct < 80 ? 'text-success' : (pct < 90 ? 'text-warning' : 'text-danger');
      freeEl.innerHTML = escapeHtml(d.free_space) +
        ' <span class="' + cls + '">(' + pct + '% used)</span>';
    } else {
      freeEl.textContent = d.free_space || '—';
    }
  }

  const statusEl = document.getElementById('meta-status');
  if (statusEl) {
    const online = d.status === 'Online';
    statusEl.className = 'status-pill ' + (online ? 'status-success' : (d.status ? 'status-warning' : 'status-neutral'));
    statusEl.textContent = d.status || 'Unknown';
  }

  renderIdentified(d.resolved_via);
  renderTopbarIdentity(d, portalContext().username);
}

// Toggle privileged surfaces on the granular per-function capabilities (idea
// #970): billing / new_user / suspend_user / directory. Each is set explicitly on
// the PSA user Permissions tab — there is no blanket admin flag. (The server
// re-enforces every one of these; this only drives the UI.)
function applyCapabilities(caps) {
  const c = caps || {};
  const show = (id, on) => { const el = document.getElementById(id); if (el) el.classList.toggle('is-hidden', !on); };
  const anyUser = !!(c.directory || c.new_user || c.suspend_user);

  // Top nav: Billing on its own permission; Users when any user-function is held;
  // Remote access on its own permission (idea #1078).
  show('nav-billing', !!c.billing);
  show('nav-users', anyUser);
  // Native websocket chat (idea #1184) — fleet feature gate, offered to any recognised session.
  show('nav-chat', !!c.chat);
  // Remote access is offered ONLY to a real logged-in (password/SSO) session — never
  // the frictionless agent_id device flow, even if that contact holds the capability.
  // A tray session sees it too (idea #3076) but as "Sign in to use": the route opens the sign-in.
  show('nav-remote', !!c.remote_access);
  // Landing-page Remote Access shortcut (idea #1078) — same gate.
  show('remote-access-card', !!c.remote_access);
  // Lock markers on the signed-in-tier entries a tray session holds but can't open yet (idea #3076).
  show('nav-remote-lock', !!c.remote_access && !portalAuthenticated());
  // NetWatch (idea #3076) — needs password/SSO + 2FA like Remote; a tray session sees it locked.
  show('nav-netwatch', !!c.netwatch);
  show('nav-netwatch-lock', !!c.netwatch && !portalAuthenticated());
  show('nav-users-lock', anyUser && portalSigninRequired());
  show('nav-billing-lock', !!c.billing && portalSigninRequired());
  // Sign-out button — only for a real logged-in (password/SSO) session.
  show('topbar-logout', portalAuthenticated());

  // Inside the Users view: directory list, the two request cards, and the
  // Requests block (hidden when neither request permission is held).
  show('users-directory-section', !!c.directory);
  show('card-newuser', !!c.new_user);
  show('card-suspend', !!c.suspend_user);
  show('users-requests-section', !!(c.new_user || c.suspend_user));
}

// Device not yet known to docs — keep the shell usable but make it clear.
function renderUnrecognisedContext() {
  setText('welcome-name', portalContext().username || 'there');
  setText('meta-company', '—');
  setText('meta-site', '—');
  setText('meta-computer', '—');
  setText('meta-email', '—');
  setText('meta-phone', '—');
  setText('meta-cpu', '—');
  setText('meta-memory', '—');
  setText('meta-freespace', '—');
  setText('meta-age', '—');
  const statusEl = document.getElementById('meta-status');
  if (statusEl) {
    statusEl.className = 'status-pill status-warning';
    statusEl.textContent = 'Not recognised';
  }
  renderTopbarIdentity({}, portalContext().username);
  const idEl = document.getElementById('welcome-identified');
  if (idEl) {
    idEl.className = 'welcome-identified status-warning';
    idEl.innerHTML = '<i class="fa-solid fa-circle-exclamation"></i> This device isn’t recognised yet.';
    idEl.classList.remove('is-hidden');
  }
}

// --- Date helpers (Halo returns UTC strings, often without a Z) -------------

function parseUtcDate(value) {
  if (!value) return new Date();
  const s = value.endsWith && value.endsWith('Z') ? value : value + 'Z';
  return new Date(s);
}

function formatLocalDate(value, includeTime) {
  const d = parseUtcDate(value);
  if (includeTime === false) return d.toLocaleDateString();
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// --- Status pill ------------------------------------------------------------

function statusPill(statusId) {
  const id = Number(statusId);
  const label = STATUSID_MAP[id] || 'Unknown';
  const variant = STATUS_BADGE[id] || 'neutral';
  return '<span class="status-pill status-' + variant + '">' + escapeHtml(label) + '</span>';
}

// --- Ticket list ------------------------------------------------------------

function setTicketsState(state) {
  ['loading', 'list', 'empty', 'error'].forEach((s) => {
    const el = document.getElementById('tickets-' + s);
    if (el) el.classList.toggle('is-hidden', s !== state);
  });
  // The filter/search toolbar only makes sense alongside a populated list.
  const toolbar = document.getElementById('tickets-toolbar');
  if (toolbar) toolbar.classList.toggle('is-hidden', state !== 'list');
}

// Last fetched list + the active filter, so filtering/searching re-renders
// client-side without another round-trip. `var` (hoisted) — see state.js note.
var _ticketCache = [];
var _ticketFilter = 'open';

function renderTicketList(tickets) {
  _ticketCache = (tickets || []).slice();
  if (_ticketCache.length === 0) {
    setTicketsState('empty');
    return;
  }
  setTicketsState('list');
  applyTicketFilter();
}

// Switch the active filter tab (open / closed / all) and re-render.
function setTicketFilter(filter) {
  _ticketFilter = filter;
  document.querySelectorAll('#tickets-toolbar .filter-tab').forEach((b) => {
    b.classList.toggle('is-active', b.getAttribute('data-filter') === filter);
  });
  applyTicketFilter();
}

// Render the cached list through the current filter + search term. Closed =
// status id 9 (the projected "Closed"); everything else is open.
function applyTicketFilter() {
  const container = document.getElementById('tickets-container');
  if (!container) return;
  const searchEl = document.getElementById('tickets-search');
  const q = (searchEl && searchEl.value ? searchEl.value : '').trim().toLowerCase();

  let list = _ticketCache.slice();
  if (_ticketFilter === 'open') list = list.filter((t) => Number(t.status_id) !== 9);
  else if (_ticketFilter === 'closed') list = list.filter((t) => Number(t.status_id) === 9);
  if (q) {
    list = list.filter((t) => {
      const num = t.number != null ? t.number : t.id;
      return ((t.summary || '') + ' #' + num).toLowerCase().indexOf(q) !== -1;
    });
  }
  list.sort((a, b) => parseUtcDate(b.dateoccurred) - parseUtcDate(a.dateoccurred));

  container.innerHTML = '';
  if (list.length === 0) {
    container.innerHTML =
      '<div class="empty-state is-inline"><i class="fa-solid fa-filter-circle-xmark"></i>' +
      '<p>No tickets match this filter.</p></div>';
    return;
  }
  list.forEach((ticket) => container.appendChild(buildTicketRow(ticket)));
}

function buildTicketRow(ticket) {
  const summary = ticket.summary || '(no summary)';
  const truncated = summary.length > 60 ? summary.slice(0, 60) + '…' : summary;
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'ticket-row';
  row.setAttribute('data-action', 'open-ticket');
  // data-ticket-id stays the internal PK — the detail fetch (/tickets/:id) is by
  // PK. Display the human-facing NUMBER (native id != number).
  row.setAttribute('data-ticket-id', ticket.id);
  const ticketNumber = ticket.number != null ? ticket.number : ticket.id;
  row.innerHTML =
    '<span class="ticket-row-main">' +
      '<span class="ticket-row-title">' + escapeHtml(truncated) + '</span>' +
      '<span class="ticket-row-meta"><i class="fa-solid fa-calendar"></i> ' +
        escapeHtml(formatLocalDate(ticket.dateoccurred)) +
        ' &nbsp;<i class="fa-solid fa-hashtag"></i>' + escapeHtml(ticketNumber) + '</span>' +
    '</span>' + statusPill(ticket.status_id);
  return row;
}

function showTicketsError(message) {
  const el = document.getElementById('tickets-error-message');
  if (el) el.textContent = message;
  setTicketsState('error');
}

// --- Ticket details ---------------------------------------------------------

function setTicketDetailsState(state) {
  ['loading', 'content', 'error'].forEach((s) => {
    const el = document.getElementById('ticket-details-' + s);
    if (el) el.classList.toggle('is-hidden', s !== state);
  });
}

function setText(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function renderTicketDetails(data) {
  const ticket = data.ticket || {};
  setText('td-id', '#' + (ticket.number != null ? ticket.number : ticket.id));
  setText('td-title', ticket.summary || 'No title');
  setText('td-summary', ticket.summary || 'No summary provided');
  setText('td-created', formatLocalDate(ticket.dateoccurred));
  setText('td-updated', formatLocalDate(ticket.lastactiondate || ticket.last_update));
  setText('td-contact', ticket.user_name || 'Unknown');
  setText('td-phone', ticket.phonenumber || 'No phone provided');
  setText('td-email', ticket.user_email || 'No email provided');

  const statusEl = document.getElementById('td-status');
  if (statusEl) statusEl.innerHTML = statusPill(ticket.status_id);

  renderTicketRequest(ticket);
  renderTicketActions(data.actions || []);

  // Fresh composer each time a ticket opens.
  const replyEl = document.getElementById('reply-message');
  if (replyEl) replyEl.value = '';

  setTicketDetailsState('content');
}

// The structured request behind a portal-form ticket (new user / suspend user), rendered as a
// read-only key/value grid above the history (idea #2007). Before this the client could only
// see their own submission as the HTML blob in the opening note — and had no way at all to get
// back to it. Secrets are already stripped server-side (toDetailTicket), not here.
function renderTicketRequest(ticket) {
  const section = document.getElementById('td-request-section');
  const grid = document.getElementById('td-request-grid');
  const btn = document.getElementById('td-request-update');
  if (!section || !grid) return;

  const form = ticket && ticket.portal_form;
  const fields = form && Array.isArray(form.fields) ? form.fields : [];
  if (!fields.length) {
    section.classList.add('is-hidden');
    grid.innerHTML = '';
    return;
  }

  setText('td-request-title', form.title || 'Request Details');
  grid.innerHTML = '';
  fields.forEach((f) => {
    const item = document.createElement('div');
    item.className = 'detail-item';
    const label = document.createElement('span');
    label.className = 'detail-label';
    label.textContent = f.label;
    const value = document.createElement('span');
    value.className = 'detail-value';
    value.textContent = f.value;
    item.appendChild(label);
    item.appendChild(value);
    grid.appendChild(item);
  });
  section.classList.remove('is-hidden');

  // Only an OPEN new-user request can still be corrected. The ticket number is what the resume
  // form keys on, and the session is what authorises it — no token needed for a logged-in user.
  if (btn) {
    btn.classList.toggle('is-hidden', !ticket.can_update);
    btn.dataset.ticketNumber = ticket.number != null ? String(ticket.number) : '';
  }
}

// Ids of the actions currently rendered in the thread — so the live poll can
// detect and surgically append only the genuinely new ones. `var` hoists.
var _shownActionIds = new Set();

function buildActionItem(action, isNew) {
  const title = action.outcome || action.new_status_name || 'Update';
  // Full message — this is the user's own ticket thread, not a preview list.
  const content = action.note_html ? rewriteActionImageHtml(action.note_html) : escapeHtml(action.note || 'No details provided');

  const item = document.createElement('div');
  item.className = 'activity-item' + (action.mine ? ' is-mine' : '') + (isNew ? ' is-new' : '');
  if (action.id != null) item.setAttribute('data-action-id', String(action.id));
  item.innerHTML =
    '<div class="activity-head">' +
      '<span class="activity-title">' + escapeHtml(title) +
        (isNew ? ' <span class="activity-new-badge">New</span>' : '') + '</span>' +
      '<span class="activity-date">' + escapeHtml(formatLocalDate(action.datetime)) + '</span>' +
    '</div>' +
    '<div class="activity-body">' + content +
      (action.who ? '<div class="activity-who">By ' + escapeHtml(action.who) + '</div>' : '') +
    '</div>';
  return item;
}

function renderTicketActions(actions) {
  const container = document.getElementById('td-actions');
  const empty = document.getElementById('td-actions-empty');
  if (!container) return;

  const visible = (actions || []).filter((a) => a.outcome !== 'Claim' && a.outcome !== 'Opened');
  _shownActionIds = new Set();
  if (visible.length === 0) {
    container.innerHTML = '';
    if (empty) empty.classList.remove('is-hidden');
    return;
  }
  if (empty) empty.classList.add('is-hidden');

  const sorted = visible.slice().sort((a, b) => parseUtcDate(b.datetime) - parseUtcDate(a.datetime));
  container.innerHTML = '';
  sorted.forEach((action) => {
    container.appendChild(buildActionItem(action, false));
    if (action.id != null) _shownActionIds.add(String(action.id));
  });
}

// Surgically add any not-yet-shown actions (from the live poll), newest on top
// with a highlight + "New" badge. Returns how many were added.
function mergeNewActions(actions) {
  const container = document.getElementById('td-actions');
  if (!container) return 0;
  const empty = document.getElementById('td-actions-empty');
  const visible = (actions || []).filter((a) => a.outcome !== 'Claim' && a.outcome !== 'Opened');
  const fresh = visible.filter((a) => a.id != null && !_shownActionIds.has(String(a.id)));
  if (!fresh.length) return 0;
  if (empty) empty.classList.add('is-hidden');
  // Oldest-first so each insert-at-top leaves the newest action on top.
  fresh.sort((a, b) => parseUtcDate(a.datetime) - parseUtcDate(b.datetime));
  fresh.forEach((action) => {
    container.insertBefore(buildActionItem(action, true), container.firstChild);
    _shownActionIds.add(String(action.id));
  });
  return fresh.length;
}

// Refresh the detail header status pill on poll (the status may have changed).
function updateTicketStatusPill(ticket) {
  if (!ticket) return;
  const statusEl = document.getElementById('td-status');
  if (statusEl) statusEl.innerHTML = statusPill(ticket.status_id);
}

function showTicketDetailsError(message) {
  const el = document.getElementById('ticket-details-error-message');
  if (el) el.textContent = message;
  setTicketDetailsState('error');
}

// --- Users directory --------------------------------------------------------

// Last fetched directory + the active status filter, so search/tab re-filter
// client-side. `var` hoists (see state.js note) since the bundle concatenates.
var _userCache = [];
var _userFilter = 'all';

// system token -> short directory label. 'ads' reads as "Computer" (#1987):
// end users know it as their computer login, not as Active Directory.
var ACCOUNT_SHORT = { ads: 'Computer', m365: 'M365', google: 'Google' };

function setUsersMessage(html) {
  const c = document.getElementById('users-directory');
  if (c) c.innerHTML = html;
}

function renderUsersDirectory(users) {
  _userCache = (users || []).slice();
  applyUsersFilter();
}

// Switch the directory status filter (all / enabled / disabled / pending).
function setUserFilter(filter) {
  _userFilter = filter;
  document.querySelectorAll('#view-users .filter-tab').forEach((b) => {
    b.classList.toggle('is-active', b.getAttribute('data-ufilter') === filter);
  });
  applyUsersFilter();
}

function applyUsersFilter() {
  const c = document.getElementById('users-directory');
  if (!c) return;

  if (_userCache.length === 0) {
    setUsersMessage('<div class="empty-state"><i class="fa-solid fa-user-slash"></i>' +
      '<p>No user accounts found for your organisation.</p></div>');
    return;
  }

  const searchEl = document.getElementById('users-search');
  const q = (searchEl && searchEl.value ? searchEl.value : '').trim().toLowerCase();
  let list = _userCache.slice();
  if (_userFilter === 'enabled') list = list.filter((u) => u.status !== 'Disabled');
  else if (_userFilter === 'disabled') list = list.filter((u) => u.status === 'Disabled');
  else if (_userFilter === 'pending') list = list.filter((u) => u.pending_suspension);
  if (q) {
    list = list.filter((u) =>
      [u.name, u.email, u.title, u.department].filter(Boolean).join(' ').toLowerCase().indexOf(q) !== -1);
  }

  if (list.length === 0) {
    setUsersMessage('<div class="empty-state is-inline"><i class="fa-solid fa-filter-circle-xmark"></i>' +
      '<p>No people match this filter.</p></div>');
    return;
  }

  c.innerHTML = '';
  list.forEach((u) => c.appendChild(buildUserCard(u)));
}

function userInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function buildUserCard(u) {
  const card = document.createElement('div');
  card.className = 'user-card' + (u.status === 'Disabled' ? ' is-disabled' : '') +
    (u.pending_suspension ? ' is-pending' : '');

  const badges = [];
  if (u.pending_suspension) badges.push('<span class="status-pill status-warning">Pending</span>');
  if (u.is_vip) badges.push('<span class="status-pill status-warning">VIP</span>');
  if (u.role) badges.push('<span class="status-pill status-info">' + escapeHtml(u.role) + '</span>');
  badges.push('<span class="status-pill ' + (u.status === 'Disabled' ? 'status-danger' : 'status-success') + '">' +
    escapeHtml(u.status) + '</span>');

  const sub = [u.title, u.department].filter(Boolean).map(escapeHtml).join(' · ');

  // Live linked-account chips (dimmed when that account is itself disabled).
  const accChips = (u.accounts || []).map((a) => {
    const lbl = ACCOUNT_SHORT[a.system] || a.system;
    return '<span class="acct-chip' + (a.enabled === false ? ' is-off' : '') + '">' + escapeHtml(lbl) + '</span>';
  }).join('');

  const meta =
    '<span><i class="fa-solid fa-right-to-bracket"></i> Last login: ' +
      (u.last_login ? escapeHtml(formatLocalDate(u.last_login, false)) : 'never') + '</span>' +
    '<span><i class="fa-solid fa-pen"></i> Modified: ' +
      (u.last_modified ? escapeHtml(formatLocalDate(u.last_modified, false)) : '—') + '</span>';

  const contact = [];
  if (u.email) contact.push('<a class="user-contact" href="mailto:' + escapeHtml(u.email) + '">' +
    '<i class="fa-solid fa-envelope"></i> ' + escapeHtml(u.email) + '</a>');
  if (u.phone) contact.push('<span class="user-contact"><i class="fa-solid fa-phone"></i> ' + escapeHtml(u.phone) + '</span>');

  // "Disable" → prefill the Suspend form. Only for users who can request it and
  // who aren't already disabled / already pending.
  let actions = '';
  if (portalCapabilities().suspend_user && u.status !== 'Disabled' && !u.pending_suspension) {
    actions = '<div class="user-actions">' +
      '<button type="button" class="cp-btn cp-btn-outline-secondary cp-btn-sm user-disable-btn" data-action="disable-user" ' +
        'data-name="' + escapeHtml(u.name) + '" data-email="' + escapeHtml(u.email || '') + '" ' +
        'data-accounts="' + escapeHtml((u.accounts || []).map((a) => a.system).join(',')) + '">' +
        '<i class="fa-solid fa-user-xmark"></i> Disable</button></div>';
  } else if (u.pending_suspension) {
    actions = '<div class="user-actions"><span class="status-pill status-warning">Pending</span></div>';
  }

  card.innerHTML =
    '<div class="user-avatar">' + escapeHtml(userInitials(u.name)) + '</div>' +
    '<div class="user-main">' +
      '<div class="user-name-row"><span class="user-name">' + escapeHtml(u.name) + '</span>' +
        '<span class="user-badges">' + badges.join('') + '</span></div>' +
      (sub ? '<div class="user-sub">' + sub + '</div>' : '') +
      (accChips ? '<div class="user-accounts">' + accChips + '</div>' : '') +
      '<div class="user-meta-line">' + meta + '</div>' +
      (contact.length ? '<div class="user-contacts">' + contact.join('') + '</div>' : '') +
    '</div>' + actions;
  return card;
}

// --- Suspend user picker (searchable dropdown over the company directory) ----

// Cache of suspendable users + the current selection. `var` hoists.
var _suspendUsers = [];
var _suspendSelected = null;
// Company accounts closed by hand (idea #3411): [{ key, label, named }].
var _suspendManaged = [];

function setSuspendUsers(users, managed) {
  _suspendUsers = (users || []).slice();
  _suspendManaged = Array.isArray(managed) ? managed.slice() : [];
}

// Bluehost mail, the phone system and the EHR hold no per-person record we can read, so these
// are offered for whoever is picked, ticked like the live accounts, "if they have one".
function suspendManagedLabel(a) {
  if (a.key === 'bluehost') return 'Disable their ' + a.label;
  if (a.key === 'phone') return a.named ? 'Remove their ' + a.label + ' account / extension' : 'Remove their phone extension';
  if (a.key === 'chat') return 'Disable their ' + a.label + ' account';
  if (a.key === 'emr') return 'Disable their ' + a.label + ' login';
  return 'Disable their ' + a.label + ' account';
}

// Render the dropdown results for the current query (max 12).
function renderSuspendResults(query) {
  const box = document.getElementById('su-user-results');
  if (!box) return;
  const q = (query || '').trim().toLowerCase();
  let list = _suspendUsers.slice();
  if (q) {
    list = list.filter((u) => [u.name, u.email, u.title].filter(Boolean).join(' ').toLowerCase().indexOf(q) !== -1);
  }
  list = list.slice(0, 12);
  if (!list.length) {
    box.innerHTML = '<div class="su-result is-empty">No matching people</div>';
    box.classList.remove('is-hidden');
    return;
  }
  box.innerHTML = list.map((u) => {
    const i = _suspendUsers.indexOf(u);
    const sub = [u.title, u.email].filter(Boolean).map(escapeHtml).join(' · ');
    return '<button type="button" class="su-result" data-action="suspend-pick" data-su-index="' + i + '">' +
      '<span class="su-result-name">' + escapeHtml(u.name) + '</span>' +
      (sub ? '<span class="su-result-sub">' + sub + '</span>' : '') + '</button>';
  }).join('');
  box.classList.remove('is-hidden');
}

function hideSuspendResults() {
  const box = document.getElementById('su-user-results');
  if (box) box.classList.add('is-hidden');
}

// Apply a chosen user to the form: name (hidden), email, and per-account
// suspend checkboxes from their live accounts.
function selectSuspendUser(user) {
  _suspendSelected = user || null;
  const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val == null ? '' : val; };
  set('su-user', user ? user.name : '');
  set('su-email', user ? (user.email || '') : '');
  const searchEl = document.getElementById('su-user-search');
  if (searchEl) searchEl.value = user ? user.name : '';
  renderSuspendAccounts(user ? (user.accounts || []) : null);
  hideSuspendResults();
}

// Render the "Suspend which accounts?" checkboxes for the selected user (null = nobody picked
// yet, so the company-level hand-closed accounts stay hidden too).
function renderSuspendAccounts(accounts) {
  const wrap = document.getElementById('su-accounts');
  const list = document.getElementById('su-accounts-list');
  if (!wrap || !list) return;
  const live = (accounts || []).filter((a) => a && a.system);
  const managed = accounts ? _suspendManaged : [];
  renderSuspendTerminate(managed);
  if (!live.length && !managed.length) {
    list.innerHTML = '';
    wrap.classList.add('is-hidden');
    return;
  }
  // 'ads' reads as "Computer Account" here too (#1987) — same end-user wording
  // as the directory chip. The posted `system` token is still 'ads'.
  const labels = { ads: 'Computer Account', m365: 'Microsoft 365', google: 'Google Workspace' };
  list.innerHTML = live.map((a) => {
    const lbl = labels[a.system] || a.system;
    const off = a.enabled === false ? ' <span class="su-acct-off">(already disabled)</span>' : '';
    return '<label class="cp-check su-acct-row">' +
      '<input class="cp-check-input su-account-cb" type="checkbox" value="' + escapeHtml(a.system) + '"' +
      (a.enabled === false ? '' : ' checked') + '>' +
      '<span class="cp-check-label">Suspend ' + escapeHtml(lbl) + off + '</span></label>';
  }).join('') + managed.map((a) => '<label class="cp-check su-acct-row">' +
      '<input class="cp-check-input su-account-cb" type="checkbox" value="' + escapeHtml(a.key) + '" checked>' +
      '<span class="cp-check-label">' + escapeHtml(suspendManagedLabel(a)) +
      ' <span class="su-acct-off">(if they have one)</span></span></label>').join('');
  wrap.classList.remove('is-hidden');
}

// "They have left the company" (idea #3491): offered only when the company has an account we can
// delete later (Bluehost email, RingCentral chat), and it names exactly those.
function renderSuspendTerminate(managed) {
  const row = document.getElementById('su-terminate-row');
  const label = document.getElementById('su-terminate-label');
  const box = document.getElementById('su-terminate');
  if (!row || !label || !box) return;
  const names = (managed || []).filter((a) => a.key === 'bluehost' || a.key === 'chat')
    .map((a) => (a.key === 'bluehost' ? 'their ' + a.label : 'their ' + a.label + ' account'));
  if (!names.length) {
    box.checked = false;
    row.classList.add('is-hidden');
    return;
  }
  label.textContent = 'They have left the company: delete ' + names.join(' and ') + ' after 30 days';
  row.classList.remove('is-hidden');
}

// Reset the suspend picker (called when the form is shown fresh).
function resetSuspendPicker() {
  selectSuspendUser(null);
  const searchEl = document.getElementById('su-user-search');
  if (searchEl) searchEl.value = '';
}

// --- Inline field validation ------------------------------------------------

function setFieldError(fieldId, message) {
  const input = document.getElementById(fieldId);
  if (!input) return;
  const field = input.closest('.portal-field') || input.parentElement;
  input.classList.add('has-error');
  input.setAttribute('aria-invalid', 'true');
  let err = field.querySelector('.field-error');
  if (!err) {
    err = document.createElement('span');
    err.className = 'field-error';
    field.appendChild(err);
  }
  err.textContent = message;
}

function clearFieldErrors(form) {
  if (!form) return;
  form.querySelectorAll('.field-error').forEach((e) => e.remove());
  form.querySelectorAll('.has-error').forEach((e) => {
    e.classList.remove('has-error');
    e.removeAttribute('aria-invalid');
  });
}

// Validate the ticket form. Returns [] when valid, else [[fieldId, message], …].
function validateTicketForm(form) {
  const errors = [];
  const val = (name) => (form[name] && form[name].value ? form[name].value.trim() : '');
  if (!val('summary')) errors.push(['tf-summary', 'Please enter a brief summary.']);
  if (!val('details')) errors.push(['tf-details', 'Please describe the issue.']);
  const email = val('email');
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.push(['tf-email', 'Please enter a valid email address.']);
  }
  return errors;
}

// --- Form prefill -----------------------------------------------------------

function prefillTicketForm() {
  const d = agentDetails();
  const draft = loadTicketDraft();
  const set = (id, value) => {
    const el = document.getElementById(id);
    if (el && value != null) el.value = value;
  };
  set('tf-company', draft.company || d.clientname || '');
  set('tf-site', draft.site || d.sitename || '');
  set('tf-user', draft.user || d.halo_user_full_name || portalContext().username || '');
  set('tf-email', draft.email || d.email || '');
  set('tf-phone', draft.phone || d.halo_user_phone || '');
  set('tf-computer', draft.computerName || d.hostname || '');
  set('tf-summary', draft.summary || '');
  set('tf-details', draft.details || '');
}
